import { useState, useEffect, useMemo, useRef, lazy, Suspense, Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { db, sendActiveRoundBeacon } from '../lib/db'
import { supabase } from '../lib/supabase'
import { golfapi } from '../lib/golfapi'
import { useAuth } from '../contexts/AuthContext'
import { enqueueWrite, installDrainListeners } from '../lib/pendingWrites'
import { calculateTotal, effectivePar, effectiveHandicapRank, strokesOnHole, resolveCourseHandicap } from '../lib/golf'
import useHistoryModal from '../lib/useHistoryModal'
import { isSupported as wakeLockSupported, requestWakeLock, releaseWakeLock, readWakeLockPref, shouldHoldWakeLock } from '../lib/wakeLock'
import { useDisplayMode } from '../lib/displayMode'
import DisplayModeToggle from '../components/DisplayModeToggle'
import { teeKey, teeGenderSuffix, teeNameOf, resolveSeatTee, resolveSeatYardage, teesInPlay } from '../lib/tee'
import CourseLinkPicker from '../components/CourseLinkPicker'
import StandaloneGamesPanel from '../components/StandaloneGamesPanel'
import ScorecardChipStrip from '../components/ScorecardChipStrip'
import ShuffleCard from '../components/ShuffleCard'
import ErrorBoundary from '../components/ErrorBoundary'
import { nextPutts, deriveGir, girBackfillPutts, effectiveScore, courseHoleCount, editHoleOptions, clampHoleCount, promoteSavedScores, saveResultCommitted, playerPlayedHoleNumbers, parFillHoles, incompleteScoringPlayers, buildSeatGuestMap, canReplaceSeat, isRoundOwner, newcomerStartHole, backEntryHoleNumbers, buildBackEntryHoleMap, buildNewSeatPayload, buildNewSeatMember, splitBalanced, initialScoringState } from '../lib/scorecardStats'
import PlayerRowMenu from '../components/PlayerRowMenu'
import { isResumedRoundDead, createActiveRoundPoll, readResumableStandaloneRound } from '../lib/activeRound'
import { shouldAutoReopenMap, resolveResumeAutoOpen, mapFailedStorageKey, isRoundMapFailed, MAP_FAILED_KEY_PREFIX, pickMapSurface } from '../lib/swUpdate'
// Import factories kept at MODULE scope so the specifier stays static (Vite/
// esbuild need a literal `import('...')` to code-split Mapbox into its own chunk,
// loaded only when GPS is opened). The React.lazy wrapper is created INSIDE the
// component via useMemo keyed on a retry counter (F-074 FIX 2): React.lazy
// memoizes the module promise INCLUDING a rejection, so a lazy created once would
// re-throw a purged-chunk rejection forever WITHOUT ever re-attempting import().
// Recreating the lazy on a manual retry hands React a fresh promise → a genuine
// new network fetch that recovers if the chunk has become reachable again.
const importHoleMap = () => import('../components/HoleMap')
const importManualHoleMap = () => import('../components/ManualHoleMap')

// F-074 defense-in-depth: if the GPS-map lazy chunk fails to load (its
// content-hashed URL 404s after a new build is published mid-round), the local
// ErrorBoundary below renders THIS instead of letting the rejected import bubble
// to the root ErrorBoundary and replace the whole scorecard. It only paints when
// the map is actually meant to be open (`visible`), so a background chunk failure
// while the map is closed stays silent and scoring continues untouched.
//
// `onFail` fires once when this overlay MOUNTS — i.e. the moment the boundary
// caught the failed import. The scorecard uses it to LATCH a per-round
// "map load failed" flag that suppresses the per-hole-save auto-reopen for the
// rest of the round (see shouldAutoReopenMap), so the overlay does NOT re-cover
// the scorecard on every subsequent save. It runs regardless of `visible` (the
// failure happened even if the map was closed at the time).
//
// "Back to scoring" is the PRIMARY (prominent) action — dismissing the map and
// continuing to score is the safe mid-round move. "Refresh now" is DE-EMPHASIZED
// to a secondary style: a mid-round reload is exactly what F-074 avoids, so it
// stays available but is not the primary green CTA.
function MapUnavailableOverlay({ visible, onClose, onFail }) {
  useEffect(() => {
    onFail?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!visible) return null
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: '#0f1f0f', color: '#e7e2d6',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, padding: 24, textAlign: 'center',
    }}>
      <p style={{ fontSize: '2rem', margin: 0 }}>🛰️</p>
      <p style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>Map temporarily unavailable</p>
      <p style={{ margin: 0, maxWidth: 320, fontSize: '0.9rem', opacity: 0.8 }}>
        A new version is ready. Your scores are safe — the map refreshes automatically after the round.
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <button
          onClick={onClose}
          style={{ padding: '10px 16px', border: 'none', borderRadius: 10, background: '#1f6b3a', color: 'white', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}
        >
          Back to scoring
        </button>
        <button
          onClick={() => { try { window.location.reload() } catch {} }}
          style={{ padding: '10px 16px', border: '1px solid #34503a', borderRadius: 10, background: 'transparent', color: '#e7e2d6', fontSize: '0.95rem', cursor: 'pointer' }}
        >
          Refresh now
        </button>
      </div>
    </div>
  )
}

// Walk match-play holes in order, comparing each team's best per hole.
// Shared by the live status block and the group summary (previously the
// same ~60-line walk was duplicated inline in both and executed twice per
// render). scoreFor(pid, holeKey, holePar, hole) returns the displayed
// score for a player on a hole — gross for the live block, net-aware for
// the summary. trackHole (optional) captures that hole's per-hole result
// even after closeout ("Hole N wins" on a bonus hole).
// A hole counts as "played" as soon as ANY player on EITHER team has
// entered a score; untouched players take par (the stepper's visual
// default). Closeout FREEZES the running W/L — bonus holes don't change
// the official result.
function walkMatchHoles(sortedHoles, team1Pids, team2Pids, scores, scoreFor, trackHole) {
  const totalHoles = sortedHoles.length || 18
  let t1Wins = 0, t2Wins = 0, halved = 0, holesPlayed = 0
  let closeoutLeader = null, closeoutLead = 0, closeoutRemaining = 0
  let thisHoleResult = null
  for (const h of sortedHoles) {
    const hn = String(h.hole_number)
    const holePar = h.par || 4
    const anyEntered =
      team1Pids.some(pid => scores[pid]?.[hn] != null) ||
      team2Pids.some(pid => scores[pid]?.[hn] != null)
    if (!anyEntered) {
      if (trackHole != null && h.hole_number === trackHole) thisHoleResult = null
      continue
    }
    const dispScore = (pid) => scoreFor(pid, hn, holePar, h)
    const t1Best = Math.min(...team1Pids.map(dispScore))
    const t2Best = Math.min(...team2Pids.map(dispScore))
    let result
    if (t1Best < t2Best) result = 1
    else if (t2Best < t1Best) result = 2
    else result = 0
    if (trackHole != null && h.hole_number === trackHole) thisHoleResult = result
    if (closeoutLeader != null) continue
    holesPlayed++
    if (result === 1) t1Wins++
    else if (result === 2) t2Wins++
    else halved++
    const diff = t1Wins - t2Wins
    const absDiff = Math.abs(diff)
    const remaining = totalHoles - holesPlayed
    if (absDiff > remaining) {
      closeoutLeader = diff > 0 ? 1 : 2
      closeoutLead = absDiff
      closeoutRemaining = remaining
    }
  }
  return { totalHoles, t1Wins, t2Wins, halved, holesPlayed, closeoutLeader, closeoutLead, closeoutRemaining, thisHoleResult }
}

// Non-destructive merge: for every player + every hole the caller has
// EITHER a local or a server score, take whichever exists (prefer the
// higher-hole-count set when both exist, so a stale server response
// can't wipe a local hole that hasn't synced yet). Used on hydrate so
// the offline-scored back nine survives a re-open. Idempotent — merge(a,
// a) === a.
function mergeHoleScores(local, server) {
  const out = { ...(server || {}) }
  const localMap = local || {}
  for (const pid of Object.keys(localMap)) {
    const l = localMap[pid] || {}
    const s = out[pid] || {}
    const combined = { ...s }
    for (const k of Object.keys(l)) {
      const lv = l[k]
      const sv = combined[k]
      if (lv == null) continue
      if (sv == null) { combined[k] = lv; continue }
      // Both sides have a value. Prefer local — the current device's
      // in-memory state is authoritative for the user actively scoring;
      // a stale poll shouldn't overwrite the number they just typed.
      combined[k] = lv
    }
    out[pid] = combined
  }
  return out
}

// Best-effort JSON parse of a localStorage payload with a default. Used
// throughout ScorecardPage's initializers so a corrupt value doesn't
// crash the mount.
function safeParse(raw, fallback) {
  try { const p = JSON.parse(raw); return p ?? fallback } catch { return fallback }
}

// Chip glyph/label/tone lookup for the scorecard chip strip. Kept as
// plain functions so ScorecardChipStrip stays presentational — the
// scorecard drives which formats it wants to surface.
function chipGlyphFor(format) {
  switch (format) {
    case 'shuffle_card': return '♦'
    case 'wolf':         return 'W'
    case 'skins':        return '$'
    case 'nassau':       return 'N'
    case 'stableford':   return 'S'
    case 'simple':       return '1'
    case 'match_1v1':    return 'M'
    case 'vegas':        return 'V'
    default:             return '•'
  }
}
function chipLabelFor(format) {
  switch (format) {
    case 'shuffle_card': return 'Shuffle'
    case 'wolf':         return 'Wolf'
    case 'skins':        return 'Skins'
    case 'nassau':       return 'Nassau'
    case 'stableford':   return 'Stableford'
    case 'simple':       return 'Simple'
    case 'match_1v1':    return 'Match'
    case 'vegas':        return 'Vegas'
    default:             return format
  }
}
function chipToneFor(format) {
  return format === 'shuffle_card' ? 'shuffle-diamond' : format === 'wolf' ? 'wolf' : 'default'
}

// Reshape the client's per-hole map into the array shape upsert_hole_stats
// expects. The client keeps holeStats[playerId] as { "1": {fir, gir, ...},
// "2": {...} } so per-toggle updates stay O(1) React state churn; the
// save path collapses that to [{ hole_number: 1, fir, gir, ... }, ...]
// for the DB writer. Rows with EVERY field null/undefined are dropped so
// a first-touch on a hole that ended up with no captured stats doesn't
// create a spurious empty row.
function statsMapToRows(perHoleMap) {
  if (!perHoleMap || typeof perHoleMap !== 'object') return []
  const rows = []
  for (const [holeKey, s] of Object.entries(perHoleMap)) {
    const hn = Number(holeKey)
    if (!Number.isInteger(hn) || hn < 1 || hn > 18 || !s) continue
    const hasAny = s.fir != null || s.gir != null || s.pen === true
      || s.pen_count > 0 || Number.isFinite(s.putts) || s.fir_direction || s.pen_type
    if (!hasAny) continue
    rows.push({
      hole_number: hn,
      fir: s.fir === true ? true : s.fir === false ? false : null,
      fir_direction: s.fir_direction || null,
      gir: s.gir === true ? true : s.gir === false ? false : null,
      putts: Number.isFinite(s.putts) ? s.putts : null,
      pen_count: Number.isFinite(s.pen_count) ? s.pen_count : (s.pen === true ? 1 : 0),
      pen_type: s.pen_type || null,
    })
  }
  return rows
}

// Shared "Keep screen awake" switch — rendered on both the main scoring
// surface and the scramble scoring surface (ScrambleScoreEntry). Pure
// presentation: the preference state and the wake-lock effect live in
// ScorecardPage, which owns the singleton request/release.
function WakeLockToggle({ on, onToggle }) {
  const supported = wakeLockSupported()
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      disabled={!supported}
      title={supported ? undefined : 'Your browser does not support keeping the screen awake.'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', marginTop: 20, padding: '14px',
        background: 'none', border: '1px solid var(--green-mid)',
        borderRadius: 'var(--radius)', color: 'var(--cream)',
        fontSize: '0.9rem', fontWeight: 600,
        cursor: supported ? 'pointer' : 'not-allowed',
        opacity: supported ? 1 : 0.5,
      }}
    >
      <span style={{ textAlign: 'left' }}>
        Keep screen awake
        {!supported && (
          <span className="text-xs text-muted" style={{ display: 'block', fontWeight: 400 }}>
            Not supported on this device
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto', marginLeft: 12, padding: '4px 10px',
          borderRadius: 'var(--radius)', fontSize: '0.75rem', fontWeight: 700,
          background: on ? 'var(--gold)' : 'var(--green-deep)',
          color: on ? 'var(--green-deep)' : 'var(--cream)',
          border: `1px solid ${on ? 'var(--gold)' : 'var(--green-mid)'}`,
        }}
      >
        {on ? 'ON' : 'OFF'}
      </span>
    </button>
  )
}

export default function ScorecardPage() {
  const { player, isCommissioner } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [displayMode] = useDisplayMode()

  const standaloneState = location.state?.standalone ? location.state : null
  const standaloneKey = standaloneState ? `standalone_round_${player?.id || 'anon'}` : null
  // Reload-resume (D-300). An Android Wi-Fi↔cellular switch mid-round triggers a
  // full PWA reload that drops React Router's location.state, so standaloneState
  // goes null on a page the user was actively scoring — dropping them to "No
  // active round." The round itself is NOT gone: PlayPage parked its identity in
  // localStorage['active_round'] (on round start / holes-tee edit; saveProgress
  // mirrors SCORES to standaloneKey + the DB, not this identity blob). Re-read
  // that blob so the page can transparently re-enter the round (effect below) instead
  // of stranding the owner on a screen whose only button looks like it will
  // overwrite the in-progress round. Recomputed when standaloneState flips (the
  // resume nav sets it), at which point this returns null and the guards clear.
  const persistedResume = useMemo(() => {
    if (!player?.id) return null
    let raw = null
    try { raw = localStorage.getItem('active_round') } catch { /* private mode */ }
    // OWNED SOLO ROUND WINS. A parked active_round that passes the helper's owner
    // check (id + course present, player_id === this player) is a strong, specific
    // signal that THIS device has an in-progress solo round — stronger than any
    // cached "current event" — so we resume it unconditionally, even when a live
    // event is also cached. This is what keeps the D-300 fix working for the
    // morning-event → afternoon-solo golfer: PlayPage does NOT refresh the event
    // bootstrap cache, so a current-event flag stamped that morning stays
    // "non-expired" for up to ~2.5h and must NOT suppress the afternoon solo resume.
    //
    // The original event-hijack concern is already handled WITHOUT any event
    // check: an event-only participant has no OWNED resumable round (they never
    // started a solo round → active_round is absent or not theirs), so the helper
    // returns null and the stateless /scorecard open falls through to fetchSetup's
    // event score-entry branch. FIX 2's owner check is the guard. A genuinely
    // finished solo round whose active_round outlived a failed clear is retired
    // online by the server liveness gate below (self-heals). player?.id is in the
    // deps because the helper's owner check reads it.
    return readResumableStandaloneRound(raw, !!standaloneState, player?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standaloneState, player?.id])
  const membersRef = useRef([])
  // Resurrection gate (ops #42 goal c). Flips true only when the server has
  // POSITIVELY confirmed the resumed round no longer exists — then the
  // active_rounds write vectors below (save mirror + visibility beacon) become
  // no-ops so a stale/cached open can't recreate a deleted round for a
  // co-player to pick up. See the liveness effect further down.
  const roundDeadRef = useRef(false)
  // Imperative handle to the by-id liveness check (assigned by the liveness
  // effect below). Lets a save that comes back { error: 'round_deleted' } drive
  // the 3-state liveness classification NOW — surfacing the grab prompt (or the
  // bounce-to-Home) immediately instead of waiting up to 30s for the poll tick.
  const checkLivenessRef = useRef(null)

  const [event, setEvent] = useState(null)
  const [holes, setHoles] = useState([])
  const [groupPlayers, setGroupPlayers] = useState([])
  const [scoringFor, setScoringFor] = useState({})   // { playerId: bool } — who this user is keeping score for
  // Multi-device scoring: { playerId: scored_by_uuid|null }. Server-side state
  // hydrated from round_scores.scored_by on round load. When scoredByMap[X]
  // is set to someone other than the logged-in user, the picker for X is
  // grayed out (someone else owns that row).
  const [scoredByMap, setScoredByMap] = useState({})
  // D-346: { player_id → isGuest(bool) } for the in-round ⋮ menu. A seat is
  // Replace/Swap-eligible ONLY when its underlying players row is a guest/stub
  // (players.auth_user_id IS NULL). Hydrated from round_scores' joined
  // auth_user_id; a locally-added guest seat is stamped true at creation.
  const [seatGuestMap, setSeatGuestMap] = useState({})
  // Seed scores from localStorage on the very first render so a
  // backgrounded/killed PWA can re-open onto its last-known state
  // without waiting for the server hydrate. Server data merges in
  // non-destructively via mergeHoleScores below.
  const [scores, setScores] = useState(() => {
    if (!standaloneKey) return {}
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    return saved?.scores || {}
  })
  // COMMITTED score snapshot (Bug #36 — honest save UI). `scores` above is the
  // live edit buffer the score inputs read/write; `savedScores` reflects ONLY
  // what has actually been persisted through a save path. The completion dots +
  // the score summary derive from THIS snapshot so an unsaved edit never lights
  // a dot or moves the summary. Seeded the same way `scores` is (last-saved
  // localStorage) and updated only inside saveProgress on a successful write.
  const [savedScores, setSavedScores] = useState(() => {
    if (!standaloneKey) return {}
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    return saved?.scores || {}
  })
  // Advanced stats sidecar (Scorecard v2). Parallel to scores; keyed the same
  // way: { playerId: { holeNum: { fir, gir, pen, putts } } }. Persists to
  // round_scores.hole_stats via the same upsert paths. Seeded from
  // localStorage on cold-open (write-through) then hydrated from the server.
  const [holeStats, setHoleStats] = useState(() => {
    if (!standaloneKey) return {}
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    return saved?.holeStats || {}
  })
  const [currentHole, setCurrentHole] = useState(() => {
    if (!standaloneKey) return 1
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    return Number(saved?.currentHole) || 1
  })
  // F-077: players the scorekeeper has marked "finished / stopped" in Edit
  // Round. { playerId: true }. A finished player is excluded from par-autosave
  // (their row freezes at their last real hole) — the discoverable replacement
  // for the deselect-to-avoid-fabrication workaround. Seeded from localStorage
  // and persisted alongside scores so a mid-round resume keeps the flags.
  const [playersDone, setPlayersDone] = useState(() => {
    if (!standaloneKey) return {}
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    return saved?.playersDone || {}
  })
  // If AuthContext hadn't cached a player when this component first
  // rendered, standaloneKey resolved to `standalone_round_anon` and the
  // useState initializers for scores/currentHole read the wrong bucket
  // (empty). Once player.id lands, replay the read against the correct
  // key so the "paint from cache" promise still holds — with a guard so
  // we don't overwrite anything the user has already typed.
  //
  // Two independent updates below — scores AND currentHole are treated
  // separately, whereas the prior version bailed after scores loaded
  // and never got to currentHole. Symptom Jacob hit 2026-07-14: after
  // navigating to a side page (/game, /shuffle) and back to /scorecard,
  // the hole dropped to 1 even though localStorage had 2/3/etc.
  useEffect(() => {
    if (!standaloneState || !standaloneKey) return
    const saved = safeParse(localStorage.getItem(standaloneKey), null)
    if (Object.keys(scores).length === 0 && saved?.scores && Object.keys(saved.scores).length > 0) {
      setScores(saved.scores)
      // Keep the committed snapshot in step — localStorage only holds
      // already-saved holes, so seeding savedScores from it is correct.
      setSavedScores(prev => (Object.keys(prev).length === 0 ? saved.scores : prev))
    }
    const savedHole = Number(saved?.currentHole)
    if (Number.isFinite(savedHole) && savedHole > 0 && currentHole === 1) {
      setCurrentHole(savedHole)
    }
    // D-300 reload-resume: the useState initializers above only seed when
    // standaloneKey was already resolved at MOUNT. On the reload-resume path the
    // page first mounts with standaloneState=null (dropped router state), so the
    // key resolves LATER — this key-change effect is what re-seeds the round.
    // Replay holeStats + playersDone here too (scores/currentHole already handled
    // above) so a rehydrated round keeps its advanced stats and finished-player
    // flags without waiting on the server hydrate. Guarded (only seed when the
    // current value is empty) so a user edit made before the key resolved is
    // never clobbered — same posture as the scores seed.
    if (Object.keys(holeStats).length === 0 && saved?.holeStats && Object.keys(saved.holeStats).length > 0) {
      setHoleStats(saved.holeStats)
    }
    if (Object.keys(playersDone).length === 0 && saved?.playersDone && Object.keys(saved.playersDone).length > 0) {
      setPlayersDone(saved.playersDone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standaloneKey])

  // Reload-resume navigation (D-300). When the reload dropped router state but a
  // live standalone round is parked in localStorage, re-enter it with the exact
  // { ...payload, standalone: true } shape Home / PlayPage / App.ResumeToActiveRound
  // use — so standaloneState comes back, the seed effect above rehydrates scores,
  // and the fetchSetup effect (deps [player?.id, standaloneState?.id]) re-fires to
  // build holes/players and merge server ↔ local on reconnect. replace:true keeps
  // history clean; the server liveness gate further
  // down still validates the round, so a stale blob can't render a dead round
  // forever, and a deliberately-finished round (active_round already removed) is
  // never resurrected. No-op once standaloneState is present (persistedResume →
  // null), so there's no navigation loop.
  useEffect(() => {
    if (standaloneState || !persistedResume) return
    navigate('/scorecard', { replace: true, state: { ...persistedResume, standalone: true } })
  }, [standaloneState, persistedResume, navigate])

  // Active side games on this round, projected from StandaloneGamesPanel's
  // list_games fetch. Used by the inline chip strip + auto-render of
  // stateful game surfaces (Shuffle Card picker on hole-turn). Panel is
  // the single source of truth; this state is a read-only mirror.
  const [activeSideGames, setActiveSideGames] = useState([])
  // Per-round advanced-stats expansion for non-me players. Own row is
  // always visible; other players show a "Track Advanced Stats" tap
  // affordance that expands the row when the scorekeeper wants to log
  // FIR/GIR/Pen/Putts for them.
  const [advExpanded, setAdvExpanded] = useState(new Set())
  // "+ Add Game" chip opens a modal that lets the user start a new side
  // game inline. StandaloneGamesPanel handles the actual form; we just
  // toggle its openForm via a controlled prop.
  const [addGameOpen, setAddGameOpen] = useState(false)

  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [showSubmitted, setShowSubmitted] = useState(false)
  // Exit-Round modal state. null = closed. 'root' = Cancel / Save Round /
  // Delete Round. 'save' = Save My / Save All / Cancel. 'delete' =
  // Delete My / Delete All / Cancel. Solo rounds skip step 2 (there's no
  // "others" to distinguish from — the single player IS all players).
  const [exitStep, setExitStep] = useState(null)
  const [courseCoords, setCourseCoords] = useState([])  // course_coordinates rows (GPS)
  const [showMap, setShowMap] = useState(false)         // GPS overlay visible
  const [gpsEnabled, setGpsEnabled] = useState(false)   // opened GPS at least once this round
  // F-074 FIX 2: bumped on a MANUAL retry after a GPS-map chunk failure. Feeding
  // it to the useMemo below recreates the React.lazy wrapper, forcing a fresh
  // import() (React.lazy otherwise caches the rejected promise and re-throws it
  // without a new network request). The happy path never bumps this, so the map's
  // lazy instance stays stable → the chunk loads exactly once per round.
  const [mapRetryKey, setMapRetryKey] = useState(0)
  const HoleMap = useMemo(() => lazy(importHoleMap), [mapRetryKey])
  const ManualHoleMap = useMemo(() => lazy(importManualHoleMap), [mapRetryKey])
  // Persisted preference: once a player uses GPS, auto-open it on the next hole
  // after scoring — remembered across rounds/sessions. (Device-level UI pref;
  // can move to account settings when that exists.)
  const [gpsPref, setGpsPref] = useState(() => {
    try { return localStorage.getItem('tf_gps_pref') === '1' } catch { return false }
  })
  // Keep-screen-awake preference. Per-device UI convenience (NOT shared/source-
  // of-truth data), so localStorage is the correct home (owner chose this over
  // a players-table column). Default OFF (owner-specified): no stored value
  // means OFF; only an explicit '1' means ON. Once the user flips it, their
  // last choice is remembered on this device.
  const [wakeLockOn, setWakeLockOn] = useState(() => readWakeLockPref())
  const toggleWakeLock = () => {
    setWakeLockOn(prev => {
      const next = !prev
      try { localStorage.setItem('tf_wakelock_pref', next ? '1' : '0') } catch { /* private mode */ }
      return next
    })
  }
  // True only when this page is actually scoring a live round: a signed-in
  // scorer plus either a standalone (casual) round or an event with an active
  // round per its status — which includes the event-scramble surface
  // (ScrambleScoreEntry renders when roundInfo.round is an afternoon/sunday
  // round, a subset of "round is truthy"). False on the Sign-In / "No active
  // event" / "No active round" static screens, so the wake lock is never held
  // there. Derived PRIMITIVE boolean, computed above the effect because hooks
  // can't live below the early returns; kept out of object-reference deps per
  // the repo's stale-deps lesson.
  const hasActiveRound = !!(
    (player || isCommissioner) && standaloneState
  )
  // Hold the screen-wake lock only while the toggle is ON AND a round is
  // actively being scored; release on toggle OFF, when the round goes inactive,
  // and on unmount (leaving the page / round-complete navigation). The
  // singleton in wakeLock.js owns the visibilitychange re-acquire, so this
  // effect stays on primitive dependencies — no volatile per-tap state in the
  // deps array, and no third visibilitychange listener added to this
  // already-busy page. requestWakeLock/releaseWakeLock never throw when the
  // API is absent.
  useEffect(() => {
    if (shouldHoldWakeLock(wakeLockOn, hasActiveRound)) {
      requestWakeLock()
    } else {
      releaseWakeLock()
    }
    return () => { releaseWakeLock() }
  }, [wakeLockOn, hasActiveRound])
  // Per-round flag: did the user open GPS at any point in THIS round? Drives
  // the auto-open-on-next-hole behavior — only fires if they've already
  // engaged GPS once. Reset per mount. Stops the scorer who never wants GPS
  // from getting yanked into the map on every hole save (live evidence
  // 2026-07-01: scorer saved hole 1, app jumped straight into GPS even
  // though they hadn't opened it manually).
  const gpsOpenedThisRoundRef = useRef(false)
  // F-074: latched true when the GPS-map lazy chunk fails to load in THIS round
  // (a new build published mid-round purged the old content-hashed chunk). Once
  // set, EVERY auto-reopen path is suppressed for the rest of the round (see
  // shouldAutoReopenMap) — both the per-hole-save reopen AND the resume-after-nav
  // reopen — so the "map unavailable" overlay does NOT re-cover the scorecard.
  // A ref (not state) because it is read inside the async save callback, where a
  // freshly-set state value would be stale. FIX 1: the ref alone resets on every
  // remount, so the latch is ALSO mirrored to sessionStorage under roundLatchKey
  // and re-hydrated on mount (below) — the ref is the fast in-memory read, the
  // sessionStorage value is the cross-remount source of truth for the round.
  const mapLoadFailedThisRoundRef = useRef(false)
  // Per-round sessionStorage key for the map-failure latch. FIX 1: keyed to the
  // PER-ROUND-INSTANCE, never the shared event.id — casual rounds use
  // standalone_round_id; event rounds use event id + the active-round token
  // (friday_morning, friday_afternoon, …) so a transient GPS-map chunk failure in
  // one round of a multi-round event (AM/PM, multi-day league) latches ONLY that
  // round, not every later round of the same event. null until a round id is
  // known (event loads async) — the hydrate/persist calls no-op while null.
  // Legacy event-round token retired (D-341): only standalone rounds have a
  // per-round latch key now.
  const roundLatchKey = standaloneState?.id
    ? mapFailedStorageKey(standaloneState.id)
    : null
  // FIX 1: re-hydrate the in-memory latch from sessionStorage whenever the round
  // key resolves (mount, or when the async event load supplies event.id). This
  // is what makes the latch survive a navigate-away-and-back remount, so no
  // auto-reopen path can re-throw the purged import mid-round. Belt-and-suspenders:
  // sweep every OTHER round's stale latch first, so a prior round's (or prior
  // event-round's) latch can never bleed into this round instance even within the
  // same browser session — only THIS round's key survives (and survives remounts,
  // which is the whole point of persisting it).
  useEffect(() => {
    if (!roundLatchKey) return
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i)
        if (k && k.startsWith(MAP_FAILED_KEY_PREFIX) && k !== roundLatchKey) {
          sessionStorage.removeItem(k)
        }
      }
      if (isRoundMapFailed(sessionStorage.getItem(roundLatchKey))) {
        mapLoadFailedThisRoundRef.current = true
      }
    } catch { /* private mode */ }
  }, [roundLatchKey])
  const [loadingGps, setLoadingGps] = useState(false)
  const [linkCandidates, setLinkCandidates] = useState(null) // GolfAPI matches for an unlinked course
  const [linking, setLinking] = useState(false)
  // X2 missing-hole warning: holds { missing:[numbers], proceed:fn } when a save
  // is attempted with un-scored holes.
  const [missingPrompt, setMissingPrompt] = useState(null)
  // F-077: pre-save confirm for an individual stroke round's "Save All Players"
  // when >=1 still-selected player is partial. Holds { players:[{name,holeCount}],
  // proceed:fn }. Peer of missingPrompt; uses the useHistoryModal overlay pattern.
  const [partialPrompt, setPartialPrompt] = useState(null)
  // D-346: in-round player ⋮ menu flows. `replaceFlow` drives the Replace/Swap
  // chooser + starting-point + manual back-entry overlays for a guest seat.
  //   { step: 'choose',    seatPid, seatName, incomingName }
  //   { step: 'startpoint',seatPid, seatName }              // newcomer already in seat
  //   { step: 'backentry', seatPid, seatName, entries:{} }  // holes 1..X-1 strokes
  const [replaceFlow, setReplaceFlow] = useState(null)
  const [seatBusy, setSeatBusy] = useState(false)
  // Rework v2: per-player Change-tee flow ({ seatPid, seatName, current }) opened
  // from the Edit Round ⋮ menu, plus a version counter bumped on a tee change so
  // the seatTeeByPid / teesInPlaySet memos (keyed off the location-derived
  // standaloneState reference, which never changes identity) recompute after an
  // in-place payload tee mutation.
  const [changeTeeFlow, setChangeTeeFlow] = useState(null)
  const [teeVersion, setTeeVersion] = useState(0)
  // Rework v2 · balanced-wrap MEASURE for the per-hole tee-yardage list. The
  // probe renders the full list on a single nowrap line; if its natural width
  // exceeds the visible container the list is split into two balanced rows.
  const teeProbeRef = useRef(null)
  const teeWrapRef = useRef(null)
  const [teeSplit, setTeeSplit] = useState(false)

  // Hole numbers with NO entered score from any scoring player, evaluated
  // against an optional effective-scores object (defaults to current state).
  // The optional arg lets a save button pre-commit par defaults for the
  // current hole locally, then check the REST of the round against the
  // committed snapshot — so the current hole isn't flagged as missing when
  // the user is the one explicitly trying to save it.
  //
  // Team-aware: in a team round the scorekeeper often taps a teammate's
  // stepper without tapping their own row. Check the union across every
  // scoring player so the warning fires only when the hole is genuinely
  // blank for the whole group.
  const computeMissingHoles = (effectiveScores) => {
    const eff = effectiveScores ?? scores
    const pidsScoring = (standaloneState ? membersRef.current : groupPlayers)
      .filter(m => scoringFor[m.player_id])
      .map(m => m.player_id)
    if (pidsScoring.length === 0) {
      const mine = eff[player?.id] || {}
      return holes.filter(h => !mine[String(h.hole_number)]).map(h => h.hole_number)
    }
    return holes
      .filter(h => !pidsScoring.some(pid => eff[pid]?.[String(h.hole_number)] != null))
      .map(h => h.hole_number)
  }
  // X2 gate: missing holes prompt before a save. The save flow is:
  //   1. Commit par defaults for the current hole locally (the visual
  //      default the user saw on the stepper). Build that snapshot in the
  //      caller and pass it in as `effectiveScores`.
  //   2. Check the REST of the round (using that snapshot) for missing
  //      holes — if any, prompt; if not, proceed.
  // The pre-commit step means a Save click on hole 9 doesn't flag hole 9
  // itself as missing (par-default is the user's deliberate intent), but
  // earlier holes the user skipped still get caught.
  const guardedSave = (proceed, effectiveScores) => {
    const missing = computeMissingHoles(effectiveScores)
    if (missing.length > 0) { setMissingPrompt({ missing, proceed }); return }
    proceed()
  }
  // Build a {pid → {hole → score}} snapshot with par defaults filled in for
  // the current hole, for every scoring player. Used by save buttons before
  // calling guardedSave so the current hole counts as filled.
  const commitCurrentHolePar = () => {
    const playerList = standaloneState ? membersRef.current : groupPlayers
    const out = {}
    playerList.forEach(member => {
      const existing = scores[member.player_id] || {}
      // Skip players not being scored AND players marked finished (F-077) — a
      // finished player is frozen at their last real hole, so the current hole
      // must not be par-committed for them.
      if (!scoringFor[member.player_id] || playersDone[member.player_id]) { out[member.player_id] = existing; return }
      const holeVal = existing[String(currentHole)] ?? holePar
      out[member.player_id] = { ...existing, [String(currentHole)]: holeVal }
    })
    return out
  }

  // Integrate the on-scorecard modals/overlays with browser back — without
  // this, Android's system back button pops the SPA history instead of
  // dismissing them, so users hit the "back-button trap" Jacob reported
  // (had to press back multiple times to escape a frozen state).
  useHistoryModal(showMap, () => setShowMap(false), 'gps')
  useHistoryModal(!!exitStep, () => setExitStep(null), 'exit')
  useHistoryModal(!!missingPrompt, () => setMissingPrompt(null), 'missing')
  useHistoryModal(!!partialPrompt, () => setPartialPrompt(null), 'partial')
  useHistoryModal(!!replaceFlow, () => setReplaceFlow(null), 'replace')
  useHistoryModal(!!changeTeeFlow, () => setChangeTeeFlow(null), 'changetee')
  useHistoryModal(!!linkCandidates, () => setLinkCandidates(null), 'link')

  // Creator-delete handoff (D-279). When the round CREATOR deletes a
  // multiplayer round we're still scoring, the by-id liveness poll (below)
  // flips this on and we prompt to keep playing. Custom overlay + useHistoryModal
  // (a 6th) so Android's system back dismisses it — consistent with this page's
  // other five scoring overlays. useConfirm can't compose with useHistoryModal
  // (its promise lives in the provider), so we follow the page's overlay pattern.
  const [grabPrompt, setGrabPrompt] = useState(false)
  const grabPromptShownRef = useRef(false)
  const leaveDeletedRound = () => {
    roundDeadRef.current = true
    try { localStorage.removeItem('active_round') } catch {}
    try { sessionStorage.removeItem('tf_resume_consumed') } catch {}
    setGrabPrompt(false)
    navigate('/', { replace: true })
  }
  const keepPlayingGrab = async () => {
    try {
      const res = await db('grab_round', { standalone_round_id: standaloneState?.id })
      if (res?.error === 'already_continued') {
        // Another player already grabbed it — the round is alive under them.
        // Let this player keep entering their own scores (deleted_at is cleared).
        grabPromptShownRef.current = true
        setGrabPrompt(false)
        showToast(`Round continued by ${res.by}. You can keep entering your own scores.`, 'success', 8000)
        return
      }
      if (res?.error) {
        setGrabPrompt(false)
        showToast('This round is no longer available.', 'error')
        navigate('/', { replace: true })
        return
      }
      // Success — the caller now owns the round; deleted_at is cleared server-
      // side so saves resume. player.id is already the owner_player_id we save
      // with, so no further state change is needed to continue scoring.
      roundDeadRef.current = false
      grabPromptShownRef.current = false
      setGrabPrompt(false)
      showToast('You\u2019re now scoring this round \u2713', 'success')
    } catch {
      setGrabPrompt(false)
      showToast('Couldn\u2019t continue the round \u2014 check your connection.', 'error')
    }
  }
  useHistoryModal(grabPrompt, leaveDeletedRound, 'grab')

  // S1 · Edit round (standalone) — change holes / tee in place without leaving.
  const [showEditRound, setShowEditRound] = useState(false)
  useHistoryModal(showEditRound, () => setShowEditRound(false), 'edit')
  const [editHoles, setEditHoles] = useState('18')
  const [editTee, setEditTee] = useState(null)
  // F-104.1-T2: front/back selector for 9-hole edits on an 18-hole course.
  // Owner's Test 6 (2026-10-30): switching 18 → 9 via Edit Round offered no
  // way to pick WHICH 9. Mirrors PlayPage's `nineSet` state. Only matters
  // when editHoles === '9' AND the course is 18-hole; the modal hides this
  // row otherwise. Default is seeded in openEditRound from the current
  // standaloneState.starting_hole so a mid-round Edit opens on the current
  // half (back-9 round opens with "Back 9" selected). Applied value is
  // threaded into applyEditRound via `opts.nextStartingHole` (T1 contract).
  const [editNineSet, setEditNineSet] = useState('front')
  const openEditRound = () => {
    // Clamp the pre-selected count to what the course actually supports so the
    // selector never opens on an option the course can't offer (Bug #33).
    const realCount = courseHoleCount(standaloneState?.course, holes.length)
    setEditHoles(String(clampHoleCount(holes.length || 18, realCount)))
    setEditTee(standaloneState?.tee || teeKey(standaloneState?.course?.tees?.[0]) || null)
    // Seed the front/back radio from the server-authoritative starting_hole
    // (F-104.1-T1). starting_hole=10 → Back 9; else Front 9. Falls back to
    // nine_set for in-flight pre-F-104.1 payloads.
    const currentStartingHole = Number(standaloneState?.starting_hole) > 1
      ? Number(standaloneState.starting_hole)
      : (standaloneState?.nine_set === 'back' ? 10 : 1)
    setEditNineSet(currentStartingHole === 10 ? 'back' : 'front')
    setShowEditRound(true)
  }
  const applyEditRound = async (opts = {}) => {
    // Never let the requested hole count exceed the course's real hole count —
    // otherwise applyEditRound fabricates par-4 holes 10–18 for a 9-hole course
    // (holes_data.find returns undefined for those). Bug #33.
    const realCount = courseHoleCount(standaloneState?.course, holes.length)
    const count = clampHoleCount(parseInt(editHoles), realCount)
    const src = standaloneState?.course?.holes_data || []
    // Same gender-aware par/handicap override as fetchSetup — keep edit-round
    // path consistent so a women's player who edits mid-round still sees women's
    // par on remaining holes.
    const gender = player?.gender || null
    // F-104.1-T1: PRESERVE starting_hole across Edit Round. The pre-F-104.1
    // loop iterated i=1..count unconditionally, silently reflowing a back-9
    // round into a front-9 after any mid-round Edit (owner's Test 6, 2026-10-30).
    // Read the current starting_hole from standaloneState (server-authoritative
    // via PlayPage's activeRound literal + the resume payload), falling back to
    // nine_set for in-flight pre-F-104.1 payloads. T2 will pass a `nextStartingHole`
    // option from a modal front/back selector to override it; today the caller
    // passes nothing and the current value is preserved verbatim.
    const currentStartingHole = Number(standaloneState?.starting_hole) > 1
      ? Number(standaloneState.starting_hole)
      : (standaloneState?.nine_set === 'back' ? 10 : 1)
    const requestedStartingHole = opts.nextStartingHole ?? currentStartingHole
    // F-104.1-T1 review-fix: an 18-hole span can only begin at hole 1 — never
    // at hole 10. If the user expands a back-9 round (currentStartingHole=10)
    // to 18 holes via Edit Round, keeping nextStartingHole=10 produced garbage
    // hole_numbers 10..27, lastHoleNum=27, "Save Hole 27 ✓", and holes 19..27
    // with no course data (par defaults to 4, yardage null). Clamp whenever
    // the requested span would overrun the course (startingHole + count - 1
    // > realCount) → fall back to 1. Covers both 9→18 on back-9 and the
    // theoretical back-9 selection on a 9-hole course (round-create guards
    // that separately; defense in depth here).
    const nextStartingHole = (requestedStartingHole + count - 1 > realCount)
      ? 1
      : requestedStartingHole
    const next = []
    for (let i = 0; i < count; i++) {
      const holeNum = nextStartingHole + i
      const m = src.find(h => h.hole_number === holeNum)
      next.push({
        hole_number: holeNum,
        par: effectivePar(m, gender) || 4,
        handicap_rank: effectiveHandicapRank(m, gender) || holeNum,
        par_men: m?.par ?? null,
        par_women: m?.par_women ?? null,
        handicap_rank_men: m?.handicap_rank ?? null,
        handicap_rank_women: m?.handicap_rank_women ?? null,
        yardage: editTee ? m?.yardages?.[teeNameOf(editTee)] : null,
        all_yardages: m?.yardages || {},
      })
    }
    setHoles(next)
    // Clamp against the full hole-number RANGE [nextStartingHole, lastHoleNum]
    // — not just the upper bound. The pre-F-104.1-T2 Math.min(c, lastHoleNum)
    // only clamped DOWN, so an owner starting an 18-hole round (currentHole=1)
    // who edits to Back 9 (nextStartingHole=10) was left with currentHole=1,
    // outside the new [10..18] range. The scorecard then rendered 'Hole 1' —
    // par default 4, no yardage, holesByNumber.get(1) undefined — while the
    // dot row showed 10..18. Save & Next would silently par-fill a phantom
    // hole 1 and advance to 2, cascading garbage entries up to hole 9 before
    // the user ever reached the real back-9 span. Match fetchSetup's
    // bidirectional clamp at ~line 1360 instead.
    const lastHoleNum = nextStartingHole + count - 1
    setCurrentHole(c => (c < nextStartingHole || c > lastHoleNum) ? nextStartingHole : c)
    // Persist the new count + starting_hole so a mid-round resume reflects
    // the edit. Stamp updated_at so HomePage's multi-device resume-card
    // compare (localStorage vs bootstrap cache) can pick the fresher source.
    //
    // F-104.1-T1: also mirror starting_hole + nine_set onto the active_round
    // blob so the LS-only resume path (reload-resume, D-300) rebuilds holes
    // at the correct range. nine_set is kept as a convenience alias for any
    // read-site still on the legacy field.
    try {
      const ar = JSON.parse(localStorage.getItem('active_round') || '{}')
      if (ar && ar.id) {
        ar.holes = String(count)
        ar.hole_count = count
        ar.starting_hole = nextStartingHole
        ar.nine_set = count === 9 && nextStartingHole === 10 ? 'back'
          : count === 9 && nextStartingHole === 1 ? 'front'
          : null
        ar.updated_at = Date.now()
        localStorage.setItem('active_round', JSON.stringify(ar))
      }
    } catch {}
    // F-104 review-fix (2026-10-27): mirror the new count onto the server's
    // standalone_rounds.hole_count so the save-time completion gate
    // (upsert_standalone_score reads this value) sees the owner's new intent.
    // Without this call, applyEditRound 18→9 stays client-side only, the
    // server keeps declaredHoleCount=18, and the save of hole 9 clamps
    // is_complete to false — the F-103 owner-finalize block never fires, the
    // round sticks as "resume me" forever.
    //
    // F-104.1-T1: send the PRESERVED starting_hole (vs. the hard-coded `1`
    // of the pre-F-104.1 fix). A back-9 Edit Round that reduces count from
    // 18 to 9 now keeps starting_hole=10 on standalone_rounds instead of
    // overwriting it to 1 — holes 10..18 stay the eligible span.
    // Best-effort: a server failure here never blocks the local Apply (the
    // user still gets the UI they asked for).
    if (standaloneState?.id) {
      try {
        await db('update_standalone_round_length', {
          standalone_round_id: standaloneState.id,
          hole_count: count,
          starting_hole: nextStartingHole,
        })
      } catch (e) {
        console.warn('update_standalone_round_length failed:', e?.message || e)
      }
    }
    setShowEditRound(false)
  }
  // F-077: mark/unmark a player as finished/stopped. Applied immediately (no
  // Apply tap) since it's independent of the holes/tee edit. A finished player
  // is skipped by par-autosave (handleNextHole) and frozen at their last real
  // hole in saveAndExit. Persisted alongside scores via the standaloneKey writes.
  const togglePlayerDone = (pid) => {
    setPlayersDone(prev => ({ ...prev, [pid]: !prev[pid] }))
  }
  // X1 save-partial: scores are already persisted by saveProgress; "save" simply
  // keeps the round (does NOT delete it) and leaves. Partial rounds are
  // is_complete=false and therefore not index-eligible.
  //
  // Par-default commit: the stepper's displayed par is purely visual until the
  // user taps +/-. An all-par round (everyone scored par, no one tapped) leaves
  // `scores` empty, which makes saveProgress silently skip every player
  // (holesCompleted === 0 → continue). Before saving, fill par for every
  // scoring player on every "played" hole — defined as any hole that has at
  // least one entered score from any scoring player, PLUS the current hole
  // (the user is here, presumably finished it). handleNextHole already does
  // this for the per-hole advance path; saveAndExit needs the same logic so
  // the explicit "Save round" path works for all-par rounds too.
  const saveAndExit = async () => {
    setSaving(true)
    const playerList = standaloneState ? membersRef.current : groupPlayers
    // F-077 hardening: par-fill each player ONLY on their OWN played holes,
    // never the group union. A player who stopped early (or was marked finished
    // in Edit Round) must not have later holes fabricated. A still-active player
    // counts the current hole they're standing on (par default = deliberate,
    // same as Save & Next); a finished player is frozen at their last real hole.
    const parFor = (h) => holes.find(x => x.hole_number === h)?.par || 4
    const filled = {}
    playerList.forEach(member => {
      const pid = member.player_id
      const existing = scores[pid] || {}
      if (!scoringFor[pid]) { filled[pid] = existing; return }
      const played = playerPlayedHoleNumbers(existing, {
        currentHole,
        includeCurrent: !playersDone[pid],
      })
      filled[pid] = parFillHoles(existing, played, parFor)
    })
    setScores(filled)
    try { await saveProgress(true, filled) } catch {}
    setSaving(false)
    setExitStep(null)
    try { localStorage.removeItem('active_round') } catch {}
    if (standaloneKey) { try { localStorage.removeItem(standaloneKey) } catch {} }
    clearMapFailedLatch()
    // Clear the cross-device resume cache too — round is filed (partial or
    // not, it's no longer "live"). round_scores rows are kept.
    if (standaloneState?.id) {
      try { await db('delete_active_round', { standalone_round_id: standaloneState.id }) } catch {}
    }
    // replace:true — after saving, "back" from the detail scorecard must NOT
    // re-enter the now-cleared scoring page (it would look like a fresh round).
    navigate('/round-leaderboard', { replace: true, state: { standalone_round_id: standaloneState.id, course: standaloneState.course, holes: holes.length } })
  }

  // "Save All Players" entry point (F-077 AC2). For an INDIVIDUAL stroke round,
  // warn — before anything saves — when >=1 still-selected player hasn't
  // finished all holes, listing them + their hole counts. "Go back" saves
  // nothing. Team/scramble rounds keep the existing union-based missing-hole
  // gate (guardedSave), where the scorekeeper legitimately taps teammates'
  // rows without their own and a hole is "missing" only for the whole group.
  const saveAllPlayers = () => {
    if (standaloneState && !isTeamRound) {
      const incomplete = incompleteScoringPlayers(
        membersRef.current, scores, scoringFor, holes.length || 18,
        { currentHole, playersDone },
      )
      if (incomplete.length > 0) {
        setPartialPrompt({ players: incomplete, proceed: saveAndExit })
        return
      }
      saveAndExit()
      return
    }
    guardedSave(saveAndExit, commitCurrentHolePar())
  }

  // "Save My Score" — the walking-off-mid-round path. Persists ONLY the
  // caller's round_scores row (par-filling played holes, same as
  // saveAndExit), then navigates the caller to the round leaderboard.
  // Leaves the round active for everyone else: does NOT clear
  // active_rounds (other devices need it to resume) and does NOT touch
  // other players' rows. Their scored_by lock keeps my row uneditable
  // on their devices even after I've left.
  const saveMyScoreAndExit = async () => {
    if (!player?.id) return
    setSaving(true)
    const playerList = standaloneState ? membersRef.current : groupPlayers
    // F-104 bug 3: iterate the LIVE holes array (hole_numbers 10..18 for a
    // back-9 round) instead of 1..holes.length — the latter would miss every
    // scored key on a back-9 round because hole_scores keys are "10".."18"
    // but the loop was asking for "1".."9".
    const playedHoles = new Set([currentHole])
    for (const hd of holes) {
      const h = hd.hole_number
      const k = String(h)
      if (scores[player.id]?.[k] != null) playedHoles.add(h)
    }
    // Fill par defaults on played holes for my row; leave every other
    // player's entry empty in the override so saveProgress skips them
    // (holesCompleted === 0 → continue).
    const filled = {}
    playerList.forEach(member => {
      if (member.player_id !== player.id) { filled[member.player_id] = {}; return }
      const existing = scores[member.player_id] || {}
      const next = { ...existing }
      for (const h of playedHoles) {
        const k = String(h)
        if (next[k] == null) {
          const hd = holes.find(x => x.hole_number === h)
          next[k] = hd?.par || 4
        }
      }
      filled[member.player_id] = next
    })
    setScores(prev => ({ ...prev, [player.id]: filled[player.id] }))
    try { await saveProgress(true, filled) } catch {}
    setSaving(false)
    setExitStep(null)
    navigate('/round-leaderboard', { replace: true, state: { standalone_round_id: standaloneState.id, course: standaloneState.course, holes: holes.length } })
  }

  // "Delete My Score" — walk off without a record. Nukes ONLY the caller's
  // round_scores row (server-side, gated on scored_by ownership) and
  // navigates home. Round stays active for other players. Silent-fail on
  // network errors is deliberate — this is a "just get me out" path.
  const deleteMyScoreAndExit = async () => {
    if (!player?.id || !standaloneState?.id) return
    try {
      await db('delete_my_standalone_score', {
        standalone_round_id: standaloneState.id,
        player_id: player.id,
      })
    } catch { /* best-effort */ }
    setExitStep(null)
    navigate('/')
  }

  // "Delete All Players" — nuke the entire round. Same behavior as before
  // the multi-step split; extracted from the inline onClick so both the
  // solo and multi-player paths can share it cleanly.
  const deleteAllAndExit = async () => {
    try { localStorage.removeItem('active_round') } catch {}
    if (standaloneKey) { try { localStorage.removeItem(standaloneKey) } catch {} }
    clearMapFailedLatch()
    if (standaloneState?.id) {
      // The server enforces the creator/organizer gate (F-014): only
      // standalone_rounds.owner_player_id may delete all players' scores. The
      // client hides this action for non-creators (UX only); the authoritative
      // check resolves the caller from the VERIFIED Supabase JWT that db()
      // attaches for this action — a self-asserted player id in the body would
      // be spoofable and is not trusted.
      try { await db('delete_standalone_round', { standalone_round_id: standaloneState.id }) } catch {}
    }
    setExitStep(null)
    navigate('/')
  }

  useEffect(() => {
    if (standaloneState && !player?.id) return
    // D-300 reload-resume: fetchSetup is the ONLY code that builds a standalone
    // round's holes[], membersRef/groupPlayers, and server score hydration. On a
    // network-switch reload the page mounts with standaloneState=null, so keying
    // this effect on player?.id alone left it a no-op (player was already cached)
    // and it never re-ran once the resume nav flipped standaloneState truthy —
    // yielding an empty scorecard (no players, no holes). Adding standaloneState?.id
    // to the deps makes it re-fire the moment the round re-enters. Skip the mount
    // pass while a resume is pending (standaloneState still null but a parked round
    // exists) so we don't waste a get_current_event call the round doesn't need —
    // the post-nav fire does the real setup. Fresh-start and in-round hole nav are
    // unaffected: both ids are set at mount (one fire), and standaloneState.id is
    // stable across hole changes (no extra fires).
    if (!standaloneState && persistedResume) return
    fetchSetup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, standaloneState?.id])

  // Drain any queued score writes on `online` + `visible`. Installs once
  // per mount and cleans up on unmount. See pendingWrites.js for how
  // entries are enqueued (from the catch block in saveProgress) and
  // deduped (same round + player + hole = same slot). The sender is the
  // regular db() call — keepalive keeps it alive across a mid-drain
  // background transition.
  useEffect(() => {
    return installDrainListeners(async (action, payload) => {
      await db(action, payload)
    })
  }, [])

  // Pre-round games handoff. PlayPage stashes an array of
  // { format, reason, code? } for every staged game that failed to
  // create at Start Round time. Surface a one-time toast so the user
  // sees the actual server rejection instead of a silent no-show.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('tf:preroundSkipped')
      if (!raw) return
      sessionStorage.removeItem('tf:preroundSkipped')
      const skipped = JSON.parse(raw)
      if (Array.isArray(skipped) && skipped.length > 0) {
        // Backward-compat: earlier build stashed bare format strings.
        const summary = skipped
          .map(s => typeof s === 'string'
            ? s
            : `${s.format}${s.reason ? ` (${s.reason})` : ''}`)
          .join('; ')
        showToast(
          `${skipped.length} side game${skipped.length === 1 ? '' : 's'} didn’t start — ${summary}. Retry from Side games below.`,
          'error',
          15000,
        )
      }
    } catch {}
    // showToast is defined later in the component; safe to reference at
    // event-loop tick time. Intentionally on-mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Last-ditch save on `visibilitychange → hidden`. The Beacon API's
  // job is to survive the OS killing the tab — normal fetch() can and
  // does die when the phone locks mid-save (this is exactly the "hole
  // erased on restart" symptom Adam hit). Only fires for standalone
  // rounds; event scoring has its own flow.
  // Refs mirror the latest scores + currentHole so the visibility-hide
  // beacon can read them WITHOUT the beacon effect needing to re-install
  // on every score tap. Prior version had [scores, currentHole] in its
  // deps, which meant every +/- swap tore down and re-added the
  // visibilitychange listener — dozens of add/remove pairs per hole,
  // plus a race window between remove and add where a background
  // transition slipping through would fire no beacon at all.
  const scoresRef = useRef(scores)
  const currentHoleRef = useRef(currentHole)
  useEffect(() => { scoresRef.current = scores }, [scores])
  useEffect(() => { currentHoleRef.current = currentHole }, [currentHole])

  // Resurrection gate — server liveness confirmation on a RESUMED open
  // (ops #42 goal c, client-side no-DDL mitigation). A round entered from
  // cached state (Home resume tile, cold-open auto-nav, or another device's
  // stale localStorage) is confirmed against the server ONCE on mount: if the
  // server positively reports this is no longer the caller's live round, we
  // mark it dead (write vectors below go quiet), clear the stale localStorage
  // cache, and bounce to Home — so a co-player can't be dropped into a dead
  // round (#2) and can't have it resurrected under them (#3). A transient
  // error NEVER bounces (isResumedRoundDead returns false on error) — a real
  // resume must survive a flaky network. A fresh start skips this entirely:
  // PlayPage stamps a one-shot sessionStorage marker because init_standalone_round
  // just created the row server-side (checking it could race the commit).
  useEffect(() => {
    if (!standaloneState?.id || !player?.id) return
    let isFresh = false
    try {
      isFresh = sessionStorage.getItem('tf_fresh_round') === standaloneState.id
      if (isFresh) sessionStorage.removeItem('tf_fresh_round')
    } catch {}
    let cancelled = false
    // Liveness of THIS SPECIFIC round — not the caller's single most-recent
    // active round. A player can legitimately hold two concurrent active_rounds
    // (a solo round + being a co-player in a friend's round); asking
    // get_active_round (ORDER BY updated_at DESC LIMIT 1) would return whichever
    // was touched last and falsely kill the other. get_active_round_by_id scopes
    // the check to standaloneState.id and returns a 3-state liveness (D-279).
    const checkLiveness = async () => {
      let result = null, error = null
      try {
        const res = await db('get_active_round_by_id', {
          player_id: player.id,
          standalone_round_id: standaloneState.id,
        })
        result = res?.data ?? null
      } catch (e) { error = e || new Error('get_active_round_by_id failed') }
      if (cancelled) return
      // DEAD — the server returns null for a hard-delete OR a soft-delete past
      // the handoff window → bounce to Home (unchanged #258 behavior).
      if (isResumedRoundDead({ result, error, roundId: standaloneState.id })) {
        roundDeadRef.current = true
        try { localStorage.removeItem('active_round') } catch {}
        // Let App's ResumeToActiveRound re-evaluate cleanly next time.
        try { sessionStorage.removeItem('tf_resume_consumed') } catch {}
        showToast('This round is no longer available.', 'error')
        navigate('/', { replace: true })
        return
      }
      // DELETED_GRABBABLE — the creator ended the round while we're scoring it.
      // Prompt once to keep playing (grab ownership); the ref guard stops the
      // ~30s poll from re-opening the modal every tick.
      if (result?.liveness === 'DELETED_GRABBABLE' && !grabPromptShownRef.current) {
        grabPromptShownRef.current = true
        setGrabPrompt(true)
      }
    }
    // Expose the check imperatively so saveProgress can trigger it the moment a
    // save is refused with { error: 'round_deleted' } — no need to wait for the
    // next poll tick to surface the grab prompt / bounce.
    checkLivenessRef.current = checkLiveness
    // ~30s visibility-gated poll (ops #43 scheduler) so a creator-delete that
    // lands while a co-player sits on the scorecard surfaces on its own — the
    // in-app fallback for anyone whose push is off. Paused while backgrounded;
    // an immediate reconcile fires on foreground.
    const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
    const poll = createActiveRoundPoll({ intervalMs: 30000, isHidden, onTick: checkLiveness })
    const onVis = () => poll.handleVisibilityChange()
    document.addEventListener('visibilitychange', onVis)
    // Immediate mount check preserves #258's on-open resurrection gate — EXCEPT
    // on a freshly-created round, where the by-id read could race the init
    // commit (PlayPage stamps the one-shot tf_fresh_round marker).
    if (!isFresh) checkLiveness()
    poll.start()
    return () => {
      cancelled = true
      checkLivenessRef.current = null
      document.removeEventListener('visibilitychange', onVis)
      poll.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standaloneState?.id, player?.id])

  useEffect(() => {
    if (!standaloneState?.id) return
    const onHide = () => {
      if (document.visibilityState !== 'hidden') return
      // Sync-only — no await, no Promise. sendBeacon returns immediately.
      // Resurrection gate: never re-seed active_rounds for a round the server
      // confirmed is dead (ops #42 goal c).
      if (player?.id && standaloneState?.id && !roundDeadRef.current) {
        sendActiveRoundBeacon({
          standalone_round_id: standaloneState.id,
          owner_player_id: player.id,
          payload: { ...standaloneState, scores: scoresRef.current, currentHole: currentHoleRef.current },
        })
      }
    }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [standaloneState, player?.id])

  // Multi-device scoring sync: every 6s, refresh scored_by ownership +
  // any other device's fresh scores. Paused while document.hidden — no
  // point polling a locked phone AND, more importantly, the poll fires
  // an immediate overwrite pass on `visible`, which used to nuke any
  // holes the user scored while the tab was in the background before
  // those saves reached the server. Firing once on visibility resume
  // takes over that job.
  useEffect(() => {
    if (!standaloneState?.id || !player?.id) return
    let cancelled = false
    const tick = async () => {
      if (document.visibilityState === 'hidden') return
      try {
        const { data } = await db('get_standalone_scores', { standalone_round_id: standaloneState.id })
        if (cancelled || !Array.isArray(data)) return
        const map = {}
        const liveScores = {}
        for (const r of data) {
          map[r.player_id] = r.scored_by || null
          const hs = typeof r.hole_scores === 'string' ? JSON.parse(r.hole_scores) : (r.hole_scores || {})
          liveScores[r.player_id] = hs
        }
        setScoredByMap(map)
        // Reconcile scoringFor with the latest ownership — picker greys
        // out for any player now owned by someone else.
        setScoringFor(prev => {
          const next = { ...prev }
          for (const pid of Object.keys(map)) {
            const owner = map[pid]
            if (owner != null && owner !== player.id) {
              next[pid] = false
            }
          }
          return next
        })
        // Two-tier merge — the fix for both the score-erase AND its
        // symmetric hazard.
        //
        // For players I OWN (scored_by === me OR unclaimed), merge
        // non-destructively: my typing must not get overwritten by a
        // server response that's mid-save-behind, but the server may
        // legitimately have holes I don't have yet (e.g. late-arriving
        // save from a prior session), and those should land.
        //
        // For players I DON'T own, the other device is authoritative.
        // Adopt the server row verbatim so a stale local value (e.g.
        // I mis-scored them before they claimed themselves; my
        // scores[X] hasn't been cleaned up) can't linger and confuse
        // downstream compute.
        setScores(prev => {
          const next = { ...prev }
          for (const pid of Object.keys(liveScores)) {
            const owner = map[pid]
            const iOwnThem = owner == null || owner === player.id
            if (iOwnThem) {
              next[pid] = mergeHoleScores({ [pid]: prev[pid] }, { [pid]: liveScores[pid] })[pid]
            } else {
              next[pid] = liveScores[pid]
            }
          }
          return next
        })
      } catch { /* network hiccup → keep last-known state */ }
    }
    // Fire once on `visible` in addition to the interval so a resume
    // still catches up other devices' progress quickly.
    const onVis = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVis)
    const interval = setInterval(tick, 6000)
    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [standaloneState?.id, player?.id])

  const fetchSetup = async () => {
    // Standalone round (from Play page) — use course from router state
    if (standaloneState) {
      const course = standaloneState.course
      const holeCount = parseInt(standaloneState.holes) || 18
      const pCount = standaloneState.playerCount || 1

      // Build hole data from course_holes data joined in search-course.
      // Par + handicap_rank are written through effectivePar / effectiveHand.-
      // Rank using the logged-in player's gender, so a female playing a
      // course with women's data populated (par_women / handicap_rank_women)
      // sees women's par on the stepper and scoring math uses women's par
      // for score_vs_par. Falls back to men's values when women's data is
      // null. Mixed-gender groups currently display the scorekeeper's
      // gender — per-player gendered par is post-launch.
      const holesFromCourse = course?.holes_data || []
      // standaloneState.tee may be a composite key (`Blue|women`) post-2026-07-01
      // OR a bare tee_name (legacy rounds). The yardages map is keyed by raw
      // tee_name (yardages are the same for men/women variants of the same
      // physical tee), so strip the gender suffix before lookup.
      const teeKeyVal = standaloneState.tee
      const teeNameLookup = teeNameOf(teeKeyVal)
      const gender = player?.gender || null
      // Back-9 support: when a 9-hole round is started on an 18-hole course
      // with nine_set='back', iterate hole numbers 10..18 instead of 1..9.
      // The hole_number key is preserved verbatim everywhere downstream
      // (round_scores.hole_scores, the stepper, GPS), so a back-9 round
      // saves keys "10".."18" and the scorecard displays "Hole 10" etc.
      //
      // F-104.1-T1: starting_hole is the SERVER-AUTHORITATIVE source
      // (standalone_rounds.starting_hole, threaded into standaloneState by
      // PlayPage's activeRound literal and preserved across resume via the
      // active_rounds.payload). Read it FIRST; fall back to nine_set only
      // for in-flight payloads created before F-104.1 (which carry nine_set
      // but no starting_hole). This closes the regression where a legitimate
      // back-9 round whose nine_set failed to thread (short holes_data,
      // resume payload shape drift) built holes 1..9 and rendered "Save Hole
      // 9 ✓" on the first played hole — because starting_hole=10 is stamped
      // by init_standalone_round regardless of nine_set thread status.
      const startHole = Number(standaloneState.starting_hole) > 1
        ? Number(standaloneState.starting_hole)
        : (standaloneState.nine_set === 'back' ? 10 : 1)
      const holeData = []
      for (let n = 0; n < holeCount; n++) {
        const i = startHole + n
        const match = holesFromCourse.find(h => h.hole_number === i)
        const par = effectivePar(match, gender) || 4
        const hcp = effectiveHandicapRank(match, gender) || i
        holeData.push({
          hole_number: i,
          par,
          handicap_rank: hcp,
          // Preserve men's values for any post-launch UI that needs to
          // show both ("Par 35 men / Par 36 women") without re-querying.
          par_men: match?.par ?? null,
          par_women: match?.par_women ?? null,
          handicap_rank_men: match?.handicap_rank ?? null,
          handicap_rank_women: match?.handicap_rank_women ?? null,
          yardage: teeNameLookup ? match?.yardages?.[teeNameLookup] : null,
          all_yardages: match?.yardages || {},
        })
      }
      setHoles(holeData)
      // For back-9 rounds, currentHole's default (1) is outside the playable
      // range. Initialize to startHole so the stepper opens on hole 10 not
      // hole 1. Restore from localStorage / standaloneState overrides this
      // a few lines down if a saved progress exists.
      //
      // CRITICAL: preserve any in-range currentHole the useState initializer
      // already restored from localStorage. Previously this unconditionally
      // set currentHole=startHole, stomping the LS-restored value on every
      // re-mount (Android screen-timeout → foreground causes a re-mount).
      // The useEffect at ~line 221 that watches [standaloneKey] only fires
      // on key change, not on this state stomp — so once currentHole was
      // reset to 1, LS restore couldn't come back and rescue it. Live
      // evidence 2026-07-11: after 4h of testing, every phone-lock cycle
      // dropped the scorecard to hole 1. Fix: only clamp when the current
      // value is genuinely out of range for the round's hole span.
      const lastHoleNum = startHole + holeCount - 1
      setCurrentHole(h => (h < startHole || h > lastHoleNum) ? startHole : h)

      // Build player list. Prefer the authoritative payload.players array
      // (slot/name/linked_player_id for EVERY participant including owner),
      // because the legacy `me + otherPlayers` reconstruction was perspective-
      // dependent — on a non-owner's device it stamped the viewer's name into
      // slot 0 and pushed the owner-perspective "others" after, producing
      // duplicate names (live evidence 2026-07-01: Kate's phone showed two
      // "Kate Drumm" rows because Jacob's otherPlayers[0] was Kate).
      const members = []
      const payloadPlayers = Array.isArray(standaloneState.players) ? standaloneState.players : null
      if (payloadPlayers && payloadPlayers.length > 0) {
        const sorted = [...payloadPlayers].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
        for (const pl of sorted) {
          const name = pl.name || `Player ${(pl.slot ?? 0) + 1}`
          let pid = pl.linked_player_id || null
          if (!pid) {
            for (let attempt = 0; attempt < 2 && !pid; attempt++) {
              try {
                const { data: stub } = await db('find_or_create_player_stub', {
                  name,
                  phone: pl.phone || null,
                  standalone_round_id: standaloneState.id,
                })
                if (stub?.id) pid = stub.id
              } catch (err) { console.error(`Stub creation error (attempt ${attempt + 1}):`, err) }
            }
          }
          members.push({ player_id: pid, name })
        }
      } else {
        // Legacy fallback: rounds started before payload.players existed.
        const others = standaloneState.otherPlayers || []
        members.push({ player_id: player?.id, name: player?.name || player?.first_name || 'You' })
        for (let i = 0; i < pCount - 1; i++) {
          const other = others[i]
          const name = other?.name || `Player ${i + 2}`
          let stubId = other?.linked_player_id || null
          if (!stubId) {
            for (let attempt = 0; attempt < 2 && !stubId; attempt++) {
              try {
                const { data: stub } = await db('find_or_create_player_stub', {
                  name,
                  phone: other?.phone || null,
                  standalone_round_id: standaloneState.id,
                })
                if (stub?.id) stubId = stub.id
              } catch (err) { console.error(`Stub creation error (attempt ${attempt + 1}):`, err) }
            }
          }
          members.push({ player_id: stubId, name })
        }
      }
      membersRef.current = members
      setGroupPlayers(members)

      // Hydrate from round_scores (the canonical per-hole table) BEFORE
      // localStorage/payload fallback. round_scores is updated by every
      // device on every save — reading it here means a multi-device round
      // shows fresh data from whoever scored last, not the device's stale
      // payload snapshot.
      //
      // Also seeds scored_by per player so the checkbox UI can gate
      // pickers correctly (grayed out for players another device claimed).
      let hydratedScores = null
      let hydratedStats = null
      let scoredByLookup = {}
      // rsRows is declared HERE (outer scope), NOT inside the try below, so the
      // guest-map + scoring-init AFTER the try can read it. When it was block-
      // scoped to the try, referencing it past the catch threw a ReferenceError
      // that aborted fetchSetup before setSeatGuestMap + setScoringFor ever ran
      // — every seat fell back to non-guest (Replace disabled) and not-scoring
      // (host default lost). Defects 2 & 6 (PR #333 fixes).
      let rsRows = null
      try {
        // Prefer /round-bootstrap — single call that returns round_scores
        // (same rows as get_standalone_scores) alongside the active_round
        // payload. Falls back to the old db() action if the endpoint
        // 401s / 404s (e.g., preview environment without the function
        // deployed yet). Same shape of data either way.
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.access_token) {
          try {
            const res = await fetch('/.netlify/functions/round-bootstrap', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ standalone_round_id: standaloneState.id }),
            })
            if (res.ok) {
              const json = await res.json()
              rsRows = json?.data?.round_scores || null
            }
          } catch { /* fall through to db() below */ }
        }
        if (!rsRows) {
          const legacy = await db('get_standalone_scores', { standalone_round_id: standaloneState.id })
          rsRows = legacy?.data
        }
        if (Array.isArray(rsRows) && rsRows.length > 0) {
          hydratedScores = {}
          hydratedStats = {}
          for (const r of rsRows) {
            const hs = typeof r.hole_scores === 'string' ? JSON.parse(r.hole_scores) : (r.hole_scores || {})
            hydratedScores[r.player_id] = hs
            // hole_stats (Scorecard v2) — server returns an ARRAY of
            // per-hole rows: [{ hole_number, fir, gir, putts, pen_count,
            // pen_type, fir_direction }, ...]. Reshape to the client's
            // {hole_number: {...}} map so per-hole reads stay O(1). Older
            // preview environments before the 20260714 migration return
            // an empty array — the map ends up {} and the UI treats
            // missing keys as "not tracked".
            const rawStats = Array.isArray(r.hole_stats) ? r.hole_stats : []
            const perHole = {}
            for (const s of rawStats) {
              if (!s || !Number.isInteger(s.hole_number)) continue
              perHole[String(s.hole_number)] = {
                fir: s.fir,
                fir_direction: s.fir_direction || null,
                gir: s.gir,
                putts: s.putts,
                // Legacy client fields we still render: keep `pen` as a
                // bool alias (pen_count > 0) so the existing toggle logic
                // doesn't need to know about pen_count.
                pen: (Number(s.pen_count) || 0) > 0,
                pen_count: s.pen_count || 0,
                pen_type: s.pen_type || null,
              }
            }
            hydratedStats[r.player_id] = perHole
            scoredByLookup[r.player_id] = r.scored_by || null
          }
        }
      } catch { /* server unavailable / migration not applied → fall through to local cache */ }
      setScoredByMap(scoredByLookup)
      // D-346: derive the guest-eligibility map for the ⋮ Replace/Swap gate from
      // the same hydrated rows (joined players.auth_user_id). Rows lacking the
      // column (older read path) stay unset → Replace hidden (fails closed). The
      // logged-in user is always authenticated, so force their seat non-guest.
      const guestMap = buildSeatGuestMap(Array.isArray(rsRows) ? rsRows : [])
      if (player?.id) guestMap[player.id] = false
      setSeatGuestMap(guestMap)

      // Initial scoringFor.
      //   * scored_by === me         → checked (I own this row)
      //   * scored_by === someone    → unchecked, "Scored by <Name>" text
      //   * scored_by === null       → checked — I'm the round creator, I'm
      //                                the default scorer until another
      //                                device claims. Also covers the
      //                                edge case where a stub seat missed
      //                                the pre-seed and shows up on the
      //                                scorecard without a round_scores
      //                                row (Jacob's 2026-07-14 test round
      //                                — 2 stubs weren't included in the
      //                                shuffle game because scored_by
      //                                came back undefined).
      const meId = player?.id
      const initialScoringFor = initialScoringState(members, scoredByLookup, meId)
      setScoringFor(initialScoringFor)

      // Restore saved progress. THREE sources (prefer freshest):
      //   1. round_scores hydration above (always live; from any device)
      //   2. localStorage[standaloneKey] — same-device fast path
      //   3. standaloneState.scores — cross-device payload fallback
      //   1. localStorage[standaloneKey] — same-device fast path
      //   2. standaloneState.scores — cross-device path (HomePage hydrated
      //      the payload from active_rounds DB and passed it via nav state).
      //      The per-device localStorage key isn't seeded on a new device.
      // Without (2), cross-device resume found the round but showed an empty
      // scorecard — surfaced live during the Brighton Dale tester (PR #97
      // era, hole 6).
      // Reconcile server ↔ local NON-DESTRUCTIVELY. Prior behaviour was
      // "server wins unconditionally" when it returned any data, which
      // silently wiped locally-buffered holes whenever a save was
      // interrupted (phone-lock mid-fetch, cellular drop between saves).
      // mergeHoleScores keeps every hole either side has and prefers the
      // local value when they conflict — the current device is the one
      // actively scoring, so its number is the user's real intent.
      //
      // CRITICAL: merge into the CURRENT React state (via the setScores
      // updater) rather than a fresh localStorage read. If the user taps
      // score buttons while fetchSetup is in flight (Netlify cold-start
      // can take a couple seconds), those adjustments live only in
      // React state until Next-tap writes them through. Merging into a
      // stale localStorage snapshot would clobber them — the exact bug
      // this PR is meant to fix.
      const lastHole = startHole + holeCount - 1
      const localHole = standaloneKey
        ? Number(safeParse(localStorage.getItem(standaloneKey), null)?.currentHole) || 0
        : 0
      const crossDevice = standaloneState.scores && Object.keys(standaloneState.scores).length > 0
        ? standaloneState.scores : null

      // Read the merged snapshot inside the updater so we always see the
      // most recent typing, not the scores value captured at closure
      // time. Then compute the resume hole + write-through from the same
      // merged value via a ref hop so we don't need to duplicate the
      // merge for the writeback.
      let mergedForResume = null
      setScores(prev => {
        const base = (hydratedScores && Object.keys(hydratedScores).length > 0)
          ? mergeHoleScores(prev, hydratedScores)
          : (Object.keys(prev).length > 0 ? prev
             : crossDevice ? crossDevice
             : prev)
        mergedForResume = base
        return base
      })
      // Advanced stats: same non-destructive merge, but only for standalone
      // rounds (the primary consumer of hole_stats today). Preferring local
      // over server matches the score-merge policy — the device actively
      // scoring is the source of truth.
      if (hydratedStats && Object.keys(hydratedStats).length > 0) {
        setHoleStats(prev => {
          const out = { ...(hydratedStats || {}) }
          for (const pid of Object.keys(prev || {})) {
            out[pid] = { ...(out[pid] || {}), ...(prev[pid] || {}) }
          }
          return out
        })
      }

      if (mergedForResume && Object.keys(mergedForResume).length > 0) {
        // Seed the committed snapshot (Bug #36) from the same merged source the
        // live buffer resumes from — every hole already persisted server-side /
        // locally counts as committed, so its dot + summary show on resume.
        setSavedScores(mergedForResume)
        let maxScoredHole = 0
        for (const pid of Object.keys(mergedForResume)) {
          const hs = mergedForResume[pid] || {}
          for (const k of Object.keys(hs)) {
            const n = Number(k)
            if (Number.isFinite(n) && hs[k] != null && n > maxScoredHole) {
              maxScoredHole = n
            }
          }
        }
        const resumedFromScores = maxScoredHole > 0
          ? Math.min(lastHole, maxScoredHole + 1) : startHole
        const resumedHole = localHole > 0
          ? Math.max(localHole, resumedFromScores)
          : resumedFromScores
        if (resumedHole !== currentHole) setCurrentHole(resumedHole)
        // Write-through: keep the merged (never the server-only)
        // snapshot in localStorage so a subsequent offline re-open sees
        // the same reality the user just left.
        if (standaloneKey) {
          try { localStorage.setItem(standaloneKey, JSON.stringify({ scores: mergedForResume, holeStats, currentHole: resumedHole, playersDone })) } catch {}
        }
      }
      return
    }

    try {
      // Legacy event-round scorecard RETIRED (D-341 Phase-3 cutover): there is
      // no server-side active-round pointer anymore (events.status dropped) and
      // no per-day course columns to resolve the round's course. Event/outing
      // scoring now runs entirely through the standaloneState path
      // (event_round_id), handled above before this fallback. Nothing to set up
      // here — with no standalone round the "No active round" screen renders.
      return
    } catch (err) { console.error('Scorecard setup error:', err) }
  }

  const loadGroupScores = async (ev, members, day, courseId) => {
    const newScores = {}
    await Promise.all(members.map(async m => {
      const { data: sc } = await db('get_player_round', { event_id: ev.id, player_id: m.player_id, day, round_time: 'morning' })
      newScores[m.player_id] = sc?.hole_scores
        ? (typeof sc.hole_scores === 'string' ? JSON.parse(sc.hole_scores) : sc.hole_scores)
        : {}
    }))
    setScores(newScores)
    // Bug #36: these come straight from round_scores (already committed), so
    // seed the saved snapshot too — otherwise a resumed event round would show
    // unlit dots / an empty summary until the next save.
    setSavedScores(newScores)
    // Jump to first unscored hole (based on logged-in player's scores)
    const myScores = newScores[player?.id] || {}
    const scoredHoles = Object.keys(myScores).map(Number).filter(n => myScores[String(n)] != null)
    if (scoredHoles.length > 0 && scoredHoles.length < 18) {
      setCurrentHole(Math.min(18, Math.max(...scoredHoles) + 1))
    }
  }

  // Status label rendered in place of the score picker when a player is
  // NOT being scored by the current device. Fires from three sibling
  // scorecard blocks (scramble / best-ball / match) so it lives here.
  //   scored_by = someone else → "Scored by <Name>"
  //   scored_by = null         → "Not being scored" (warning color at call site)
  //   scored_by = me           → null (isScoring=true, picker renders instead)
  const scoredByStatusText = (memberId) => {
    const owner = scoredByMap[memberId]
    if (!owner) return { text: 'Not being scored', warn: true }
    if (owner === player?.id) return null
    // A seat whose scored_by points at ITSELF is one of two things: (a) an
    // authenticated player scoring themselves from their own device, or (b) a
    // device-less GUEST the host explicitly dropped — persisted via the
    // self-owned sentinel in toggleScoring. A guest has no device to
    // self-score, so a self-owned guest seat reads as "not being scored"
    // (defect-6 follow-up: null-release re-defaulted to host-scoring on
    // re-entry, so a host could never persistently drop a guest).
    if (owner === memberId && seatGuestMap[memberId] === true) {
      return { text: 'Not being scored', warn: true }
    }
    const scorer = groupPlayers.find(m => m.player_id === owner)
    const label = scorer?.name || 'another device'
    return { text: `Scored by ${label}`, warn: false }
  }

  const toggleScoring = (playerId) => {
    // Self-toggle in EVENT mode is blocked (legacy: event scoring assumed
    // each player at least scored themselves). Standalone allows self-
    // release so two devices can hand off scoring of the same player.
    if (!standaloneState && playerId === player?.id) return
    const next = !scoringFor[playerId]
    setScoringFor(s => ({ ...s, [playerId]: next }))
    // Standalone: claim/release on the server so other devices see the
    // ownership change. claim_scoring is idempotent; failure here is
    // non-fatal (UI state still flips locally; next save will reconcile
    // via the scored_by_conflict gate).
    if (standaloneState?.id) {
      // Claim (next=true) → this device scores the seat (scored_by = me).
      // Drop (next=false) → for MY OWN seat, release to null so a second
      // device can hand-off and pick it up; for ANOTHER player's seat, stamp
      // scored_by with THAT player's own id (a self-owned sentinel). The
      // sentinel is neither null nor this host, so on the next scorecard open
      // initialScoringState no longer re-defaults the seat to host-scoring —
      // a host drop of a device-less guest now PERSISTS as "not being scored"
      // across leave/re-enter. (A null release re-defaulted to scoring, so the
      // ONLY previously-persistent not-scoring state was a claim by a real
      // second device — a host could never persistently drop a guest.)
      const claimedBy = next
        ? player?.id
        : (playerId === player?.id ? null : playerId)
      const courseId = standaloneState.course?.id
      ;(async () => {
        try {
          const { data } = await db('claim_scoring', {
            standalone_round_id: standaloneState.id,
            player_id: playerId,
            claimed_by: claimedBy,
            course_id: courseId,
          })
          if (data) {
            setScoredByMap(m => ({ ...m, [playerId]: data.scored_by }))
          }
        } catch {
          // server didn't accept the claim — back out the local state flip
          setScoringFor(s => ({ ...s, [playerId]: !next }))
        }
      })()
    }
  }

  const adjustScore = (playerId, holeNum, delta) => {
    if (!scoringFor[playerId]) return
    setScores(s => {
      const current = s[playerId]?.[String(holeNum)] ?? holePar
      const next = Math.max(1, Math.min(15, current + delta))
      // Auto-refresh GIR whenever the score changes AND putts are set for
      // this hole (Bug #35). GIR = strokes_to_green ≤ par - 2 where
      // strokes_to_green = score - putts. A score change re-derives from the
      // formula and CLEARS any manual GIR override (girManual) — the override
      // only "sticks until strokes or putts next change" per the spec. When
      // putts is unset there's no auto value to compute, so GIR is left as-is.
      const key = String(holeNum)
      const putts = holeStats[playerId]?.[key]?.putts
      const holeParForCalc = holesByNumber.get(Number(holeNum))?.par || holePar
      if (Number.isFinite(putts)) {
        const gir = deriveGir(next, putts, holeParForCalc)
        setHoleStats(hs => {
          const existing = hs[playerId]?.[key] || {}
          return { ...hs, [playerId]: { ...hs[playerId], [key]: { ...existing, gir, girManual: false } } }
        })
      }
      return { ...s, [playerId]: { ...s[playerId], [key]: next } }
    })
  }

  // Toggle a boolean advanced-stat (FIR / Pen). GIR has its own handler
  // (toggleGir) — it auto-fills from putts + score but can be tap-overridden.
  // Keeps three-state semantics: undefined = not tracked, true = yes, false = no.
  const toggleHoleStat = (playerId, holeNum, key) => {
    if (!scoringFor[playerId]) return
    const k = String(holeNum)
    setHoleStats(hs => {
      const existing = hs[playerId]?.[k] || {}
      const nextVal = existing[key] == null ? true
                    : existing[key] === true ? false
                    : null
      return { ...hs, [playerId]: { ...hs[playerId], [k]: { ...existing, [key]: nextVal } } }
    })
  }

  // GIR tile handler (Bug #35 — "auto-fill, tap to override").
  //   - Putts still UNSET: this is the shortcut (#35d) — back-fill putts to the
  //     regulation-implied value (score - (par - 2)) so GIR becomes true by
  //     definition. Requires a score; a no-op when the score is unset.
  //   - Putts set: a manual override — flip GIR and mark it girManual so the
  //     next strokes/putts change re-derives (adjustScore/adjustPutts clear the
  //     flag). girManual is CLIENT-STATE ONLY — statsMapToRows never emits it,
  //     so only the resulting gir boolean is persisted.
  const toggleGir = (playerId, holeNum) => {
    if (!scoringFor[playerId]) return
    const k = String(holeNum)
    const holeParForCalc = holesByNumber.get(Number(holeNum))?.par || holePar
    // Effective score (#37): explicit score if set, else the default par that
    // Save & Next commits — so tapping GIR on an untouched par hole back-fills
    // regulation putts instead of no-op'ing.
    const score = effectiveScore(scores[playerId]?.[k], holeParForCalc)
    setHoleStats(hs => {
      const existing = hs[playerId]?.[k] || {}
      if (!Number.isFinite(existing.putts)) {
        // Shortcut: no putts yet → back-fill. No score → nothing to derive from.
        const backfilled = girBackfillPutts(score, holeParForCalc)
        if (backfilled == null) return hs
        const gir = deriveGir(score, backfilled, holeParForCalc)
        return { ...hs, [playerId]: { ...hs[playerId], [k]: { ...existing, putts: backfilled, gir, girManual: false } } }
      }
      // Manual override toggle (putts already set).
      return { ...hs, [playerId]: { ...hs[playerId], [k]: { ...existing, gir: existing.gir !== true, girManual: true } } }
    })
  }

  // Putts stepper (Bug #34). Putts start UNSET (rendered '–'); the first '+'
  // yields 1 (base 0 via nextPutts), and a hole-out (0 putts) is reached with
  // '+' then '-'. Bounds [0, 6]. Also refreshes GIR (derived) and clears any
  // manual GIR override, since a putts change re-derives per the spec.
  const adjustPutts = (playerId, holeNum, delta) => {
    if (!scoringFor[playerId]) return
    const k = String(holeNum)
    // Bug #34 guard: from UNSET ('–'), '−' must NOT move putts to 0. A 0-putt
    // hole-out is only reachable via '+' (→1) then '−' (→0). Tapping '−' on an
    // untouched hole must leave putts unset and write NO hole_stats row —
    // otherwise nextPutts(undefined, -1) floors to 0 and we'd persist a phantom
    // 0-putt hole-out (+ GIR=false). The '−' button is also disabled while
    // unset; this handler guard is the belt-and-suspenders backstop.
    if (delta < 0 && !Number.isFinite(holeStats[playerId]?.[k]?.putts)) return
    setHoleStats(hs => {
      const existing = hs[playerId]?.[k] || {}
      const next = nextPutts(existing.putts, delta)
      const holeParForCalc = holesByNumber.get(Number(holeNum))?.par || holePar
      // Effective score (#37): the explicit score if the player set one, else
      // the default par (which is what Save & Next commits). Without this,
      // adjusting putts on an untouched par hole left the score undefined and
      // GIR never derived until the score stepper was tapped.
      const score = effectiveScore(scores[playerId]?.[k], holeParForCalc)
      const derived = deriveGir(score, next, holeParForCalc)
      const gir = derived !== null ? derived : existing.gir
      return { ...hs, [playerId]: { ...hs[playerId], [k]: { ...existing, putts: next, gir, girManual: false } } }
    })
  }
  // Scramble: a single team-level score per hole, fanned out to every
  // member's row so the existing per-player save path "just works."
  // teamMemberIds returns the player_ids of every member on the given team;
  // adjustTeamScore writes the same score to all of them.
  const teamMemberIds = (teamIdx) => {
    const list = standaloneState?.players || []
    const pids = []
    for (const sp of list) {
      if ((sp.team ?? 0) === teamIdx) {
        const m = membersRef.current[sp.slot]
        if (m?.player_id) pids.push(m.player_id)
      }
    }
    return pids
  }

  // Round-scoped Course Handicap for a given player_id. Reads the raw
  // Index that PlayPage stamped onto activeRound.players[] and converts
  // to a Course Handicap using the selected tee's slope/rating and the
  // course par (WHS formula: Idx × Slope/113 + (Rating − Par), capped 54).
  // When any of slope/rating/par is missing (older rounds, courses
  // without WHS data) we fall back to the raw Index — same behaviour
  // as pre-2026-07 so legacy rounds don't shift retroactively.
  const handicapForPlayer = (pid) => {
    const list = standaloneState?.players || []
    for (const sp of list) {
      const m = membersRef.current[sp.slot]
      if (m?.player_id === pid) {
        const rawIdx = sp.handicap_index
        if (rawIdx == null) return null
        // Per-player tee (T3): use THIS seat's tee for the Course Handicap
        // slope/rating, falling back to the round-level default.
        const teeObj = resolveSeatTee(standaloneState?.course?.tees, sp.tee, standaloneState?.tee)
        return resolveCourseHandicap(
          rawIdx,
          teeObj?.slope_rating,
          teeObj?.course_rating,
          standaloneState?.course?.par,
        )
      }
    }
    return null
  }
  const adjustTeamScore = (teamIdx, holeNum, delta) => {
    const pids = teamMemberIds(teamIdx)
    if (pids.length === 0) return
    // Read the first member's score as the canonical team value (all members
    // are kept in lock-step), then write the bumped value to ALL members.
    setScores(s => {
      const current = s[pids[0]]?.[String(holeNum)] ?? holePar
      const next = Math.max(1, Math.min(15, current + delta))
      const out = { ...s }
      for (const pid of pids) {
        out[pid] = { ...out[pid], [String(holeNum)]: next }
      }
      return out
    })
  }

  const saveProgress = async (silent = false, overrideScores = null) => {
    const src = overrideScores || scores

    // Standalone rounds: persist to localStorage + DB
    if (standaloneState) {
      if (standaloneKey) {
        localStorage.setItem(standaloneKey, JSON.stringify({ scores: src, currentHole, playersDone }))
      }
      const courseId = standaloneState.course?.id
      const roundId = standaloneState.id
      if (!courseId || !roundId) return

      // Use ref (synchronously available) — groupPlayers state may lag behind
      const players = membersRef.current.filter(m => m.player_id)
      // Build a slot→team lookup once so team rounds (Scramble, Best Ball,
      // and later Match Play) can stamp team_number on every row. Stroke /
      // event flows skip this entirely.
      //
      // Diagnostic: capture standaloneState.players verbatim when any sp.team
      // value is out of range (negative or >= numTeams). Live evidence of a
      // garble — a 2v2 match round saved team_number values of 2 and 4
      // (instead of 1 and 2), implying sp.team was 1,1,3,3 instead of
      // 0,0,1,1. Couldn't repro from the code path; persisting the offending
      // standaloneState lets us diagnose on the next occurrence.
      // Per-player tee (T3): map each seat's slot → its chosen tee key so the
      // loop can write every round_scores row with THAT player's tee_id /
      // tee_name / rating snapshots. Seats without an own key fall back to the
      // round-level standaloneState.tee (legacy rounds carry no per-seat tee).
      const teeBySlot = {}
      for (const sp of (standaloneState.players || [])) {
        if (sp?.tee) teeBySlot[sp.slot] = sp.tee
      }
      const teamBySlot = {}
      if (isTeamRound) {
        const numT = standaloneState?.numTeams || 1
        let anyBad = false
        for (const sp of (standaloneState.players || [])) {
          const t = sp.team ?? 0
          if (t < 0 || t >= numT) anyBad = true
          teamBySlot[sp.slot] = t
        }
        if (anyBad) {
          try {
            localStorage.setItem('tf:lastTeamGarble', JSON.stringify({
              at: new Date().toISOString(),
              numTeams: numT,
              players: standaloneState?.players || [],
              roundType: standaloneState?.roundType || null,
              roundId: standaloneState?.id || null,
            }))
          } catch {}
          console.warn('[scorecard] team value out of range', { numTeams: numT, players: standaloneState?.players })
        }
      }
      // Bug #36: track which players actually committed this pass so the
      // savedScores snapshot (which drives the completion dots + summary) is
      // updated only for genuinely-persisted rows. A scored_by conflict means
      // the row is owned by another device and did NOT commit — excluded.
      // A non-network save failure (validation / 4xx / real code-or-data bug —
      // the catch branch that surfaces the error instead of queuing it) also
      // did NOT commit and did NOT enqueue for retry, so it is excluded too;
      // otherwise the dot lights + the summary counts a hole that never
      // reached round_scores (the exact "looks saved but isn't" symptom).
      const conflictedPids = new Set()
      const failedPids = new Set()
      // D-279: set when any player's save comes back { error: 'round_deleted' }
      // — the round's creator soft/hard-deleted it and the server's
      // resurrection guard refused the write. Drives an immediate liveness
      // check after the loop (grab prompt / bounce) instead of waiting on the
      // ~30s poll, and suppresses the misleading "Saved ✓" toast.
      let roundDeletedDetected = false
      for (let pi = 0; pi < players.length; pi++) {
        const member = players[pi]
        const holeScores = {}
        Object.entries(src[member.player_id] || {}).forEach(([k, v]) => { if (v) holeScores[k] = parseInt(v) })
        const holesCompleted = Object.keys(holeScores).length
        if (holesCompleted === 0) continue
        const totalScore = Object.values(holeScores).reduce((a, v) => a + v, 0)
        const scoreVsPar = Object.entries(holeScores).reduce((sum, [holeNum, score]) => {
          const hd = holesByNumber.get(parseInt(holeNum))
          return sum + score - (hd?.par || 4)
        }, 0)
        // Per-player tee (T3), resolved BEFORE the try so BOTH the success
        // payload AND the offline-enqueue retry (catch branch) reference the
        // same per-seat tee. teeBySlot is keyed by slot; pi === slot here
        // (membersRef is a dense slot-ordered array, same assumption teamBySlot
        // relies on). Falls back to the round-level tee, then course tees[0].
        // standaloneState.tee = composite key (`Blue|women`) post-2026-07-01;
        // bare tee_name for legacy rounds — resolveSeatTee/findTeeByKey handle both.
        const teeSel = teeBySlot[pi] || standaloneState?.tee || null
        const teeObj = resolveSeatTee(standaloneState?.course?.tees, teeBySlot[pi], standaloneState?.tee)
        try {
          const dayName = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()]
          // round_time / tee fields / rating snapshots derived client-side
          // (server runs UTC and has no view of the user's tee choice).
          // round_time uses the LOCAL hour at save time: anything before
          // noon = morning, noon-onwards = afternoon. The COALESCE-on-
          // null pattern server-side means re-saving mid-round won't flip
          // the value past noon (the INSERT path stamps it once, the
          // UPDATE path only overwrites if a non-null value is sent — and
          // we send the current hour every time so it does follow real
          // time across the day; small-hours edge case is acceptable).
          const round_time = (new Date()).getHours() < 12 ? 'morning' : 'afternoon'
          // standaloneState.course (not the outer `course` const) — the
          // local `const course = getCourseForRound(...)` at the bottom of
          // saveProgress (event branch) shadows the outer one for the WHOLE
          // function body, so any reference to `course` inside this standalone
          // branch throws "Cannot access 'course' before initialization" (TDZ).
          // Optional chaining doesn't save us — TDZ throws on the variable
          // read itself before ?. evaluates. Live evidence: a 2v2 match save
          // returned exactly this error in prod (PR #84 stack trace decoded
          // to ScorecardPage.jsx:351).
          const saveRes = await db('upsert_standalone_score', {
            standalone_round_id: roundId, player_id: member.player_id, course_id: courseId,
            hole_scores: holeScores,
            // Advanced stats — array of per-hole rows for the hole_stats
            // table. Empty when the player isn't tracking advanced stats
            // (server no-ops on empty). Reshaped from the client's
            // {hole: {fir, gir, ...}} map at the save boundary so state
            // stays cheap to update per-toggle without array churn.
            hole_stats: statsMapToRows(holeStats[member.player_id]),
            holes_completed: holesCompleted,
            // Use the LIVE hole count (holes.length), not the round's original
            // standaloneState.holes — so an Edit-round (e.g. 18→9 after a rain
            // cutoff) lets a fully-scored 9 count as a completed round.
            is_complete: holesCompleted >= (holes.length || 18),
            total_score: totalScore, score_vs_par: scoreVsPar,
            day_of_week: dayName,
            is_scramble: isStandaloneScramble, // best ball is NOT a scramble
            // Stored 1-indexed (Team 1, Team 2, ...) so every read path can
            // display the value verbatim without a +1 conversion. Set for
            // any team round (scramble + best ball + match play).
            team_number: isTeamRound ? (teamBySlot[pi] ?? 0) + 1 : null,
            // Persist the format so read paths can distinguish best ball
            // from match play (both save with is_scramble=false). Mirrors
            // PlayPage's selectedType values: 'stroke' | 'scramble' | '2v2' | 'match'.
            round_type: standaloneState?.roundType || null,
            round_time,
            tee_id: teeObj?.id || null,
            tee_name: teeObj?.tee_name || teeNameOf(teeSel),
            // Multi-device gate: tell the server who's saving so the
            // scored_by check can reject if this row is owned by another
            // device. Server returns { error: 'scored_by_conflict', ... }
            // when blocked — we catch that below and surface a toast.
            scored_by_player_id: player?.id || null,
            course_rating_snapshot: teeObj?.course_rating ?? null,
            slope_rating_snapshot: teeObj?.slope_rating ?? null,
            // F-080: carry the league linkage (set by PlayPage when the round
            // was started from a league week) so the save recomputes league
            // standings. Null/absent for ad-hoc + scheduled rounds — the server
            // COALESCEs onto the existing row value, so a null never unlinks.
            league_round_id: standaloneState?.league_round_id || null,
          })
          // The server returned a non-throwing { error } at HTTP 200. Neither
          // known error committed the write, so neither may light a completion
          // dot (honest-save #36) — saveResultCommitted gates the exclusion,
          // and each branch below handles its own side effects.
          if (!saveResultCommitted(saveRes)) {
            if (saveRes.error === 'scored_by_conflict') {
              // Multi-device gate: someone else owns this player's scored_by.
              // Surface a toast + un-check the local box so the user knows they
              // can't score this player from this device until they reclaim.
              conflictedPids.add(member.player_id)
              const otherId = saveRes.current_scored_by
              setScoredByMap(m => ({ ...m, [member.player_id]: otherId }))
              setScoringFor(s => ({ ...s, [member.player_id]: false }))
              if (!silent) showToast(`Another device is scoring ${member.name || 'this player'} — tap their box to reclaim.`, 'error')
            } else if (saveRes.error === 'round_deleted') {
              // D-279: the creator soft/hard-deleted this round; the server's
              // resurrection guard refused the write. Treat as a non-commit
              // (exclude from the dot promotion) and mark the round dead so the
              // active_rounds resume-mirror below is skipped and the liveness
              // check (fired after the loop) can surface the grab prompt.
              failedPids.add(member.player_id)
              roundDeadRef.current = true
              roundDeletedDetected = true
            } else if (saveRes.error === 'round_completed') {
              // F-103 review-fix: the owner-completion finalize branch of
              // upsert_standalone_score has already stamped completed_at and
              // deleted the active_rounds row. Any further save/beacon from
              // this scorecard would re-resurrect the resume cache; flipping
              // roundDeadRef here is what short-circuits the save-mirror
              // (ScorecardPage.jsx ~L2049) and the visibility-hidden beacon
              // (~L1108) so the client never fires the resurrection write that
              // the server would in turn refuse. Not treated as a `round_deleted`
              // (no grab prompt / bounce) because the round is DONE, not
              // handed-off: the SubmittedModal (if still open) continues to
              // behave normally and the Home resume filter hides the tile on
              // next navigation.
              failedPids.add(member.player_id)
              roundDeadRef.current = true
            }
          }
        } catch (err) {
          // Network failure (offline, phone-lock kill, Netlify cold-start
          // timeout) — queue the write so it drains automatically on
          // next `visible` / `online` event. The localStorage write above
          // already reflects the intent; queuing here promotes it to a
          // durable "will retry" state instead of a lost save.
          //
          // Classifier: fetch aborts throw TypeError / AbortError; server
          // rejections throw with a message and a Response.status the
          // db() proxy prefixes as "DB error: 5xx". Anything else is
          // treated as a real bug and surfaced. The prior broad regex
          // (/network|fetch|failed|offline|load/i) mis-classified any
          // "…failed…" or "…load…" server error as retryable, then the
          // queue would retry the poison payload forever.
          const msg = String(err?.message || '')
          const isFetchAbort = err?.name === 'TypeError' || err?.name === 'AbortError'
          const is5xx = /DB error:\s*5\d\d/.test(msg)
          const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
          const isNetworkish = isFetchAbort || is5xx || isOffline
          // Only queue on network-like failures. Non-network errors are
          // real code / data bugs — queueing them would just retry the
          // poison payload forever (and pendingWrites now dead-letters
          // after MAX_ATTEMPTS, but there's no reason to make the user
          // wait for that ceiling on a bug we should surface today).
          if (isNetworkish) {
            enqueueWrite('upsert_standalone_score', {
              standalone_round_id: roundId, player_id: member.player_id, course_id: courseId,
              hole_scores: holeScores,
              hole_stats: statsMapToRows(holeStats[member.player_id]),
              holes_completed: holesCompleted,
              is_complete: holesCompleted >= (holes.length || 18),
              total_score: totalScore, score_vs_par: scoreVsPar,
              day_of_week: dayName,
              is_scramble: isStandaloneScramble,
              team_number: isTeamRound ? (teamBySlot[pi] ?? 0) + 1 : null,
              round_type: standaloneState?.roundType || null,
              round_time,
              tee_id: teeObj?.id || null,
              tee_name: teeObj?.tee_name || teeNameOf(teeSel),
              scored_by_player_id: player?.id || null,
              course_rating_snapshot: teeObj?.course_rating ?? null,
              slope_rating_snapshot: teeObj?.slope_rating ?? null,
              // F-080: league linkage survives the offline replay too.
              league_round_id: standaloneState?.league_round_id || null,
            })
            if (!silent) showToast('Offline — score saved locally. Will sync when back online.')
          } else {
            // Non-network failure: nothing persisted and nothing queued for
            // retry. Exclude this player from the savedScores promotion below
            // so the completion dot stays unlit + the summary doesn't count
            // the hole — honest-save must not light a dot on a failed write.
            failedPids.add(member.player_id)
            // Diagnostic capture retained for genuine (non-network) errors so
            // an unexpected code-path failure still surfaces on mobile.
            console.error('Standalone save error:', err)
            try {
              localStorage.setItem('tf:lastSaveError', JSON.stringify({
                at: new Date().toISOString(),
                name: err?.name || null,
                message: err?.message || String(err),
                stack: err?.stack ? String(err.stack).slice(0, 4000) : null,
                path: typeof location !== 'undefined' ? location.pathname : null,
                roundType: standaloneState?.roundType || null,
                playerId: member.player_id,
                holesCompleted,
              }))
            } catch {}
            const stackHead = err?.stack
              ? String(err.stack).split('\n').slice(0, 3).join('\n')
              : ''
            showToast(`DB save failed: ${err.message}\n${stackHead}`, 'error')
          }
        }
      }
      // Bug #36: promote this save's committed scores into savedScores so the
      // completion dots + summary now reflect it. Skips players whose row was
      // conflict-blocked (owned by another device) OR whose write threw a
      // non-network error (surfaced, not queued) — neither committed, so
      // neither may light a dot.
      const excludedPids = new Set([...conflictedPids, ...failedPids])
      setSavedScores(prev =>
        promoteSavedScores(prev, src, players.map(m => m.player_id), excludedPids)
      )
      // Cross-device resume cache: mirror the standaloneState payload to the
      // active_rounds DB table on every save. Lets the round resume on a
      // different device / Netlify-preview URL / browser profile (where
      // localStorage doesn't carry over). The localStorage write above stays
      // as a write-through cache for hot-path render speed + offline support.
      // Failure is non-fatal — the round still saved to round_scores; the
      // resume cache is opportunistic.
      // Resurrection gate (ops #42 goal c): if the server positively confirmed
      // this round is dead (deleted elsewhere), do NOT re-UPSERT active_rounds —
      // that's the write that would resurrect a deleted round's resume tile for
      // a co-player. The score itself already saved above; only the resume
      // cache mirror is skipped.
      if (player?.id && standaloneState?.id && !roundDeadRef.current) {
        try {
          await db('upsert_active_round', {
            standalone_round_id: standaloneState.id,
            owner_player_id: player.id,
            payload: { ...standaloneState, scores: src, currentHole },
          })
        } catch {}
      }
      // D-279: the creator deleted this round while we were saving into the
      // handoff gap. Drive the by-id liveness check now (grab prompt if still
      // grabbable, bounce-to-Home if dead) and skip the misleading "Saved ✓" —
      // nothing this pass reached round_scores.
      if (roundDeletedDetected) {
        try { checkLivenessRef.current?.() } catch {}
        return
      }
      if (!silent) showToast('Saved ✓', 'success')
      return
    }

    // Legacy event group-save RETIRED (D-341 Phase-3 cutover): event/outing
    // scoring runs through the standaloneState path above (event_round_id).
    // Without a server-side active-round pointer (events.status dropped) and
    // with `event` no longer set on this page, there is nothing to save here.
  }

  const handleNextHole = async () => {
    setSaving(true)
    // Commit current hole's displayed score for all players being scored
    const committed = {}
    const playerList = standaloneState ? membersRef.current : groupPlayers
    playerList.forEach(member => {
      const existing = scores[member.player_id] || {}
      // F-077: skip players marked finished/stopped — their row freezes at the
      // last real hole and must not accrue par on further hole advances (this
      // is the par-autosave the "Player finished" control turns off, replacing
      // the deselect workaround). Players not being scored are skipped as before.
      if (scoringFor[member.player_id] && !playersDone[member.player_id]) {
        const holeVal = existing[String(currentHole)] ?? holePar
        committed[member.player_id] = { ...existing, [String(currentHole)]: holeVal }
      } else {
        committed[member.player_id] = existing
      }
    })
    // Cap at the LAST hole_number in the array (not length) so a back-9
    // round caps at 18 instead of 9 — currentHole steps 10→11→…→18.
    const lastHoleNum = holes[holes.length - 1]?.hole_number || holes.length || 18
    const nextHole = Math.min(lastHoleNum, currentHole + 1)
    await saveProgress(true, committed)
    // For standalone, also persist the next hole position
    if (standaloneKey) {
      localStorage.setItem(standaloneKey, JSON.stringify({ scores: committed, holeStats, currentHole: nextHole, playersDone }))
    }
    setSaving(false)
    setScores(committed)
    setCurrentHole(nextHole)
    // Blended flow: jump straight to the next hole's map ONLY if GPS has
    // been opened at least once in THIS round. Scorers who never open GPS
    // never get yanked into it on save. Per-round (not device-level) so a
    // user who used GPS yesterday isn't forced into it today. F-074: also
    // suppressed once the map chunk has failed to load this round, so the
    // "map unavailable" overlay never re-covers the scorecard on every save.
    if (shouldAutoReopenMap({
      gpsOpenedThisRound: gpsOpenedThisRoundRef.current,
      mapLoadFailedThisRound: mapLoadFailedThisRoundRef.current,
    })) openMap()
  }

  const showToast = (msg, type = '', durationMs) => {
    setToast({ msg, type })
    // Errors hold 15s (multi-line stack traces need read time + screenshot
    // time on mobile); everything else clears in 3s.
    const ms = durationMs ?? (type === 'error' ? 15000 : 3000)
    setTimeout(() => setToast(null), ms)
  }

  // ── D-346: in-round player ⋮ menu handlers ───────────────────────────────

  // Update a seat's display name in the persisted resume payload (in-session
  // standaloneState.players + the localStorage active_round identity blob) so a
  // reload / other device shows the renamed seat. Scores never re-point — this
  // touches names only.
  const updatePayloadSeatName = (pid, name) => {
    try {
      for (const sp of (standaloneState?.players || [])) {
        if (sp?.linked_player_id === pid) sp.name = name
      }
    } catch {}
    try {
      const raw = localStorage.getItem('active_round')
      if (raw) {
        const ar = JSON.parse(raw)
        if (ar && ar.id === standaloneState?.id) {
          if (Array.isArray(ar.players)) for (const sp of ar.players) if (sp.linked_player_id === pid) sp.name = name
          if (Array.isArray(ar.otherPlayers)) for (const op of ar.otherPlayers) if (op.linked_player_id === pid) op.name = name
          localStorage.setItem('active_round', JSON.stringify(ar))
        }
      }
    } catch {}
  }

  // Rework v2 · open the Change-tee picker for a seat, seeded with the seat's
  // current resolved tee (its own key, else the round default). Matched by
  // linked_player_id so a Replace/Swap-shuffled slot never mis-seeds.
  const openChangeTee = (pid, name) => {
    let current = standaloneState?.tee || null
    for (const sp of (standaloneState?.players || [])) {
      if (sp?.linked_player_id === pid) { current = sp.tee || standaloneState?.tee || null; break }
    }
    setChangeTeeFlow({ seatPid: pid, seatName: name, current })
  }

  // Rework v2 · Change tee (this player) — apply a new tee to a seat for the
  // WHOLE ROUND. Updates the per-seat tee in THREE places so display + persisted
  // rows stay consistent: (1) the in-memory standaloneState.players[].tee (drives
  // seatTeeByPid / teesInPlaySet display — matched by linked_player_id, the same
  // key updatePayloadSeatName uses, so a Replace/Swap-shuffled slot never stamps
  // the wrong seat), (2) the localStorage active_round payload (survives
  // reload/resume), and (3) round_scores via a forced upsert_standalone_score
  // resave — the save loop reads teeBySlot fresh from standaloneState.players, so
  // the resave rewrites THIS seat's tee_id / tee_name / course+slope rating
  // snapshots across the round. Entered strokes (hole_scores) are untouched: the
  // resave sends the SAME scores buffer. teeVersion bumps to recompute the memos.
  const changeSeatTee = async (pid, teeKeyVal) => {
    if (!standaloneState || !pid || !teeKeyVal) return
    setSeatBusy(true)
    try {
      for (const sp of (standaloneState?.players || [])) {
        if (sp?.linked_player_id === pid) sp.tee = teeKeyVal
      }
    } catch {}
    try {
      const raw = localStorage.getItem('active_round')
      if (raw) {
        const ar = JSON.parse(raw)
        if (ar && ar.id === standaloneState?.id && Array.isArray(ar.players)) {
          for (const sp of ar.players) if (sp.linked_player_id === pid) sp.tee = teeKeyVal
          localStorage.setItem('active_round', JSON.stringify(ar))
        }
      }
    } catch {}
    setTeeVersion(v => v + 1)
    // Force a resave so round_scores tee_id/tee_name/rating snapshots recompute
    // for this seat whole-round. Silent (no toast from the save itself); strokes
    // ride through unchanged (same scores buffer). Best-effort — an offline save
    // enqueues via the normal pendingWrites retry, so the tee still lands later.
    try { await saveProgress(true) } catch {}
    setSeatBusy(false)
    setChangeTeeFlow(null)
    showToast('Tee updated ✓', 'success')
  }

  // Append a brand-new EMPTY guest seat to the current round mid-round. Mirrors
  // the round-start path: create a stub (auth_user_id NULL) then re-run
  // init_standalone_round with the extended player_ids (idempotent per seat via
  // its exists-check) to seed an empty round_scores row. Extends the dense slot
  // arrays (membersRef/groupPlayers) + the persisted payload so the seat
  // survives reload/cross-device. Touches NO existing seat. Returns the new
  // player_id (or null on failure).
  const appendGuestSeat = async (rawName) => {
    if (!standaloneState) return null
    const courseId = standaloneState.course?.id
    const roundId = standaloneState.id
    if (!courseId || !roundId || !player?.id) {
      showToast('Cannot add a player right now — round not fully loaded.', 'error')
      return null
    }
    const slot = membersRef.current.length
    const name = (rawName && rawName.trim()) || `Player ${slot + 1}`
    let newPid = null
    try {
      const { data: stub } = await db('find_or_create_player_stub', {
        name, phone: null, standalone_round_id: roundId,
      })
      newPid = stub?.id || null
    } catch (e) {
      console.warn('add-seat stub create failed:', e?.message || e)
    }
    if (!newPid) { showToast('Could not add player — please try again.', 'error'); return null }

    // Extend local render state (dense slot arrays + gates).
    const newMember = buildNewSeatMember(name, newPid)
    membersRef.current = [...membersRef.current, newMember]
    setGroupPlayers(prev => [...prev, newMember])
    setScoringFor(s => ({ ...s, [newPid]: true }))
    setSeatGuestMap(m => ({ ...m, [newPid]: true }))

    // Extend the persisted payload's players[] (fetchSetup rebuilds membersRef
    // from payload.players, so a new seat must land there to survive reload).
    const newPayload = buildNewSeatPayload(slot, name, newPid)
    try { if (Array.isArray(standaloneState.players)) standaloneState.players.push(newPayload) } catch {}
    const updatedPlayers = Array.isArray(standaloneState.players) ? standaloneState.players : [newPayload]
    try {
      const raw = localStorage.getItem('active_round')
      if (raw) {
        const ar = JSON.parse(raw)
        if (ar && ar.id === roundId) {
          ar.players = Array.isArray(ar.players) ? [...ar.players, newPayload] : [newPayload]
          ar.otherPlayers = Array.isArray(ar.otherPlayers)
            ? [...ar.otherPlayers, { name, phone: null, linked_player_id: newPid }]
            : [{ name, phone: null, linked_player_id: newPid }]
          ar.playerCount = membersRef.current.length
          localStorage.setItem('active_round', JSON.stringify(ar))
        }
      }
    } catch {}

    // Seed the round_scores row + refresh the resume cache with the extended
    // payload. Idempotent — init_standalone_round skips already-seeded seats.
    try {
      const seedIds = membersRef.current.map(m => m.player_id).filter(Boolean)
      await db('init_standalone_round', {
        standalone_round_id: roundId,
        owner_player_id: player.id,
        payload: { ...standaloneState, players: updatedPlayers },
        course_id: courseId,
        player_ids: seedIds,
        league_round_id: standaloneState?.league_round_id || null,
      })
    } catch (e) {
      console.warn('add-seat init_standalone_round failed:', e?.message || e)
    }
    return newPid
  }

  // ACTION 1 — Add player: a fresh empty guest seat, others untouched.
  const handleAddPlayer = async () => {
    if (seatBusy) return
    setSeatBusy(true)
    const pid = await appendGuestSeat(null)
    setSeatBusy(false)
    if (pid) showToast('Player added ✓', 'success')
  }

  // Open the Replace/Swap chooser for a guest seat.
  const openReplaceFlow = (seatPid, seatName) => {
    setReplaceFlow({ step: 'choose', seatPid, seatName, incomingName: '' })
  }

  // ACTION 2 option 1 — Transfer scores to new player: keep the seat's
  // round_scores/hole_scores, just RENAME the guest stub. Single step, no
  // starting-point prompt.
  const doTransferRename = async () => {
    const seatPid = replaceFlow?.seatPid
    const name = (replaceFlow?.incomingName || '').trim()
    if (!name) { showToast('Enter the new player’s name.', 'error'); return }
    setSeatBusy(true)
    try {
      const res = await db('rename_guest_seat', { player_id: seatPid, name })
      if (res?.error) { showToast('Could not rename — guest players only.', 'error'); setSeatBusy(false); return }
    } catch (e) { showToast('Rename failed — please try again.', 'error'); setSeatBusy(false); return }
    membersRef.current = membersRef.current.map(m => m.player_id === seatPid ? { ...m, name } : m)
    setGroupPlayers(prev => prev.map(m => m.player_id === seatPid ? { ...m, name } : m))
    updatePayloadSeatName(seatPid, name)
    setSeatBusy(false)
    setReplaceFlow(null)
    showToast('Player updated ✓', 'success')
  }

  // ACTION 2 option 2 — Keep scores with original & finish their round: freeze
  // the original guest (mark done + release their scoring box), spawn a fresh
  // newcomer seat, then show the starting-point prompt for the newcomer.
  const doKeepAndFinish = async () => {
    const seatPid = replaceFlow?.seatPid
    const name = (replaceFlow?.incomingName || '').trim()
    if (!name) { showToast('Enter the new player’s name.', 'error'); return }
    setSeatBusy(true)
    setPlayersDone(d => ({ ...d, [seatPid]: true }))
    setScoringFor(s => ({ ...s, [seatPid]: false }))
    const newPid = await appendGuestSeat(name)
    setSeatBusy(false)
    if (!newPid) { setReplaceFlow(null); return }
    setReplaceFlow({ step: 'startpoint', seatPid: newPid, seatName: name })
  }

  // ACTION 2 option 3 — Delete current player's round: remove ONLY this guest
  // seat's round_scores row, then the incoming player takes the SAME seat fresh
  // (reuse the stub/slot → no slot-array churn), then the starting-point prompt.
  const doDeleteAndReplace = async () => {
    const seatPid = replaceFlow?.seatPid
    const name = (replaceFlow?.incomingName || '').trim()
    if (!name) { showToast('Enter the new player’s name.', 'error'); return }
    // Defense-in-depth: remove_guest_seat is owner-gated server-side. The UI
    // hides this option for non-owners (isRoundOwner gate in the chooser); bail
    // early if it's somehow reached on a non-owner device so we never surface
    // the dead-end "could not remove" toast. Options 1 & 2 remain the co-scorer
    // path. Server stays authoritative.
    if (!isRoundOwner(standaloneState?.player_id ?? null, player?.id)) {
      showToast('Only the round’s owner can delete a player’s round.', 'error')
      return
    }
    setSeatBusy(true)
    try {
      const res = await db('remove_guest_seat', { standalone_round_id: standaloneState.id, player_id: seatPid })
      if (res?.error) { showToast('Could not remove this player’s round.', 'error'); setSeatBusy(false); return }
    } catch (e) { showToast('Remove failed — please try again.', 'error'); setSeatBusy(false); return }
    try {
      const rn = await db('rename_guest_seat', { player_id: seatPid, name })
      if (rn?.error) { /* non-fatal: seat still usable under the old name */ }
    } catch {}
    membersRef.current = membersRef.current.map(m => m.player_id === seatPid ? { ...m, name } : m)
    setGroupPlayers(prev => prev.map(m => m.player_id === seatPid ? { ...m, name } : m))
    updatePayloadSeatName(seatPid, name)
    setScores(s => ({ ...s, [seatPid]: {} }))
    setSavedScores(s => ({ ...s, [seatPid]: {} }))
    setHoleStats(s => ({ ...s, [seatPid]: {} }))
    setPlayersDone(d => { const n = { ...d }; delete n[seatPid]; return n })
    setScoringFor(s => ({ ...s, [seatPid]: true }))
    setSeatBusy(false)
    setReplaceFlow({ step: 'startpoint', seatPid, seatName: name })
  }

  // Starting-point prompt (options 2 & 3). Start-on-current: nothing to write —
  // the newcomer is scored from `currentHole` onward; earlier holes stay blank
  // (absent from hole_scores) so they're excluded from totals/leaderboard with
  // no penalty. Enter-previous: open the manual back-entry UI for holes < X.
  const doStartOnCurrentHole = () => {
    setReplaceFlow(null)
    showToast(`Scoring from hole ${newcomerStartHole(currentHole)} ✓`, 'success')
  }
  const doEnterPrevious = () => {
    const holeNums = backEntryHoleNumbers(currentHole, firstHoleNum)
    const entries = {}
    for (const h of holeNums) entries[String(h)] = ''
    setReplaceFlow(f => ({ ...f, step: 'backentry', entries, holeNums }))
  }
  // Bump one back-entry hole's strokes. First tap from blank starts at par.
  const bumpBackEntryHole = (holeNum, delta) => {
    setReplaceFlow(f => {
      if (!f || f.step !== 'backentry') return f
      const key = String(holeNum)
      const par = holesByNumber.get(Number(holeNum))?.par || 4
      const cur = f.entries[key]
      const base = Number.isFinite(Number(cur)) && cur !== '' ? Number(cur) : par
      const next = Math.max(1, Math.min(15, base + delta))
      return { ...f, entries: { ...f.entries, [key]: next } }
    })
  }
  // Persist the manual back-entry: merge the entered earlier holes into the
  // newcomer's scores, then save via the normal upsert path (blank holes are
  // dropped by buildBackEntryHoleMap + the save-path's `if (v)` filter, so an
  // untouched earlier hole stays absent — no phantom score).
  const doSaveBackEntry = async () => {
    const seatPid = replaceFlow?.seatPid
    const backMap = buildBackEntryHoleMap(replaceFlow?.entries)
    setSeatBusy(true)
    const merged = { ...(scores[seatPid] || {}), ...backMap }
    const nextScores = { ...scores, [seatPid]: merged }
    setScores(nextScores)
    await saveProgress(true, nextScores)
    setSeatBusy(false)
    setReplaceFlow(null)
    showToast('Previous holes saved ✓', 'success')
  }

  // F-074: when a lazy chunk (the GPS map) can't load because a new build was
  // published mid-round, swController defers the reload and fires this event.
  // Surface a SOFT, non-blocking notice — never a modal/interstitial — so
  // scoring is never interrupted. The update applies automatically the next
  // time the user is on a safe surface (leaves the round / backgrounds / cold
  // launches).
  useEffect(() => {
    const onUpdatePending = () => {
      showToast('Update pending — finish your hole, the map will refresh after the round.', '', 8000)
    }
    window.addEventListener('tf:update-pending', onUpdatePending)
    return () => window.removeEventListener('tf:update-pending', onUpdatePending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Legacy event-round derivation retired (D-341): the scorecard is driven
  // solely by standaloneState now (no events.status active-round pointer).
  const isStrokePlay = standaloneState
    ? (standaloneState.roundType === 'stroke')
    : false
  // Standalone Scramble (casual round from Play page). Renders per-team rows
  // with a shared score input instead of per-player. Distinct from the
  // event/tournament scramble flow (which redirects to TeamsPage).
  const isStandaloneScramble = !!standaloneState && standaloneState.roundType === 'scramble'
  // Best Ball (Phase 3): per-player score entry, but the TEAM's score on
  // any given hole = the lowest score among that team's members. Each
  // player's row still saves their own gross hole_scores; team aggregation
  // happens in the display layer (and the team_number column groups rows).
  const isStandaloneBestBall = !!standaloneState && standaloneState.roundType === '2v2'
  // Match Play (Phase 4): per-player score entry like best ball, but the
  // hole-by-hole comparison of each team's best produces a running match
  // status (UP / AS / Dormie / Closed out N & M). Fixed at exactly 2 teams
  // (enforced by PlayPage); team sizes can be uneven (1v1, 2v2, 1v2, etc.).
  const isStandaloneMatchPlay = !!standaloneState && standaloneState.roundType === 'match'
  const isTeamRound = isStandaloneScramble || isStandaloneBestBall || isStandaloneMatchPlay
  const numTeams = standaloneState?.numTeams || 1
  // For 2-player rounds the team wrapper is noise — the player name says
  // everything. teamHeadingFor returns just the member name(s) when the
  // round has 2 players total; otherwise "Team N" as before.
  const isTwoPlayerRound = (standaloneState?.playerCount || 0) === 2
  const teamHeadingFor = (teamIdx) => {
    if (!isTwoPlayerRound) return `Team ${teamIdx + 1}`
    const names = (standaloneState?.players || [])
      .filter(sp => (sp.team ?? 0) === teamIdx)
      .map(sp => sp.name)
      .filter(Boolean)
    return names.length ? names.join(' & ') : `Team ${teamIdx + 1}`
  }
  const course = standaloneState ? standaloneState.course : null
  // F-poi-map-routing: choose the in-round GPS surface by whether admin POI
  // coordinates are actually loaded for this course, NOT by course.source.
  // courseCoords is loaded on mount for ANY course.id (get_course_coordinates
  // effect above), so a manual-provenance course later hand-mapped by an admin
  // has real POIs here and must render the POI rangefinder (HoleMap), not the
  // coordinate-less pin-drop map (ManualHoleMap). pickMapSurface returns
  // 'poi' | 'manual'; both render gates derive from it so they stay mutually
  // exclusive by construction (a manual course renders 'manual' ONLY when it
  // has no POIs; once POIs exist — or it was never manual — it renders 'poi').
  const hasPoiCoords = courseCoords.length > 0
  const mapSurface = pickMapSurface({ source: course?.source, hasPoiCoords })
  // O(1) hole lookup — replaces holes.find() inside per-player/per-hole
  // loops (which made group summaries O(holes²) per render on-course).
  const holesByNumber = useMemo(
    () => new Map(holes.map(h => [h.hole_number, h])),
    [holes]
  )
  const currentHoleData = holesByNumber.get(currentHole)
  const holePar = currentHoleData?.par || 4
  // Per-player yardage (T3). round_scores already saves each player's own tee
  // (tee_id/tee_name); mirror that on the on-course card so a player on blue
  // tees sees blue yardages and one on reds sees red. Seat tee is keyed by
  // slot; membersRef/groupPlayers is a dense slot-ordered array so the row
  // index === slot (same assumption the save loop's teeBySlot relies on).
  // PAR stays shared (holePar above) — only yardage differs by tee.
  // Per-seat tee keyed by the seat's PLAYER id (linked_player_id) — NOT by slot.
  // changeSeatTee + openChangeTee both target a seat by linked_player_id, and the
  // player rows render by member.player_id, so keying the resolution the same way
  // keeps Change-tee correct even after a Replace/Swap shuffles slots (a
  // slot→array-index lookup mis-targeted a shuffled seat; defect 3). The row now
  // shows the NEW tee immediately and re-open reflects it because the same key
  // updated in place drives display, seed, and persist.
  const seatTeeByPid = useMemo(() => {
    const m = {}
    for (const sp of (standaloneState?.players || [])) {
      if (sp?.tee && sp?.linked_player_id != null) m[sp.linked_player_id] = sp.tee
    }
    return m
    // teeVersion: an in-round Change-tee mutates standaloneState.players in place
    // (no new reference), so recompute when it bumps. eslint-disable to keep the
    // standaloneState-identity dep honest alongside the manual version trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standaloneState, teeVersion])
  // Rework v2: the distinct set of tee NAMES the current group is actually
  // playing — every seat's own tee, falling back to the round default — used to
  // BOLD the in-play tees in the per-hole yardage list. Standalone only (event
  // rounds carry no per-seat payload → empty set → nothing bolded, no crash).
  const teesInPlaySet = useMemo(() => {
    if (!standaloneState) return new Set()
    const keys = (standaloneState.players || []).map(sp => sp?.tee || standaloneState?.tee)
    return teesInPlay(keys, standaloneState?.tee)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standaloneState, teeVersion])
  // Rework v2 · decide whether the per-hole tee list must wrap to two rows.
  // Compares the invisible single-line probe's natural width to the visible
  // container width; flips teeSplit only on a real change (no thrash). Re-runs on
  // hole change, tee-set change, and container resize. Guarded so a missing
  // ResizeObserver (older webview) degrades to a one-shot measure, and any DOM
  // read failure leaves the list on one row (owner-approved natural wrap).
  useEffect(() => {
    const probe = teeProbeRef.current
    const container = teeWrapRef.current
    if (!probe || !container) return
    const measure = () => {
      try {
        const fits = probe.scrollWidth <= container.clientWidth + 1
        setTeeSplit(prev => (prev === !fits ? prev : !fits))
      } catch {}
    }
    measure()
    let ro = null
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(measure)
        ro.observe(container)
      }
    } catch {}
    return () => { try { ro && ro.disconnect() } catch {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHoleData, teesInPlaySet, teeSplit])
  // Yardage for the logged-in user's own tee — drives the GPS rangefinder so
  // the distance shown matches the tee they're actually playing (falls back to
  // the round-level / longest yardage, preserving the pre-T3 single-tee case).
  const myYardage = useMemo(() => {
    if (!standaloneState) return null
    if (!player?.id) return null
    return resolveSeatYardage(currentHoleData?.all_yardages, seatTeeByPid[player.id], standaloneState?.tee)
  }, [standaloneState, player?.id, currentHoleData, seatTeeByPid])
  // First/last playable hole NUMBER in the current round (not the array index).
  // For a back-9 round on an 18-hole course these are 10 and 18; for a front-9
  // they're 1 and 9; for a full 18 they're 1 and 18. Used by every prev/next
  // clamp + the GPS HoleMap onHoleChange handler.
  const firstHoleNum = holes[0]?.hole_number || 1
  const lastHoleNum  = holes[holes.length - 1]?.hole_number || holes.length || 18

  // ── Memoized render-math (computed once per relevant change, not per
  //    render — these fed per-keystroke loops on the on-course hot path).
  // Match play always compares exactly teams 0 and 1 (PlayPage enforces 2).
  const matchTeamPids = useMemo(
    () => (isStandaloneMatchPlay ? [teamMemberIds(0), teamMemberIds(1)] : [[], []]),
    // membersRef is populated in fetchSetup, which also sets groupPlayers —
    // keying on groupPlayers re-runs this once the roster is known.
    [isStandaloneMatchPlay, groupPlayers, standaloneState]
  )
  const sortedHolesAsc = useMemo(
    () => [...holes].sort((a, b) => a.hole_number - b.hole_number),
    [holes]
  )
  // Live status block: GROSS scores (display mode never nets the live call).
  const matchLive = useMemo(() => {
    if (!isStandaloneMatchPlay) return null
    const [t1, t2] = matchTeamPids
    if (t1.length === 0 || t2.length === 0) return null
    return walkMatchHoles(sortedHolesAsc, t1, t2, scores,
      (pid, hn, hPar) => {
        const v = scores[pid]?.[hn]
        return v != null ? parseInt(v) : hPar
      },
      currentHole)
  }, [isStandaloneMatchPlay, matchTeamPids, sortedHolesAsc, scores, currentHole])
  // Group summary: net-aware when adjusted display mode is on. Reads COMMITTED
  // state only (Bug #36) — `savedScores`, not the live edit buffer — so the
  // summary status stays consistent with the completion dots (which also read
  // savedScores). An unsaved current-hole edit shows in the live status block
  // (matchLive, still reads `scores`) but must not move the committed summary.
  const matchSummary = useMemo(() => {
    if (!isStandaloneMatchPlay) return null
    const [t1, t2] = matchTeamPids
    if (t1.length === 0 || t2.length === 0) return null
    const totalHoles = sortedHolesAsc.length || 18
    return walkMatchHoles(sortedHolesAsc, t1, t2, savedScores,
      (pid, hn, hPar, h) => {
        const v = savedScores[pid]?.[hn]
        const gross = v != null ? parseInt(v) : hPar
        if (displayMode !== 'adjusted') return gross
        const hcp = handicapForPlayer(pid)
        const strokes = hcp ? strokesOnHole(hcp, h.handicap_rank, totalHoles) : 0
        return gross - strokes
      },
      null)
  }, [isStandaloneMatchPlay, matchTeamPids, sortedHolesAsc, savedScores, displayMode])
  // Best Ball: per-team { teamTot, teamDiff, teamThru } (null for empty teams).
  // Reads COMMITTED state only (Bug #36) — `savedScores`, not the live edit
  // buffer — so team totals stay consistent with the completion dots. An
  // unsaved hole never moves the total until it's actually saved.
  const bestBallTotals = useMemo(() => {
    if (!isStandaloneBestBall) return null
    return Array.from({ length: numTeams }).map((_, teamIdx) => {
      const pids = teamMemberIds(teamIdx)
      if (pids.length === 0) return null
      let teamTot = 0, teamDiff = 0, teamThru = 0
      for (const h of holes) {
        const holeNum = String(h.hole_number)
        // Adjusted mode: NET best-ball. Each member's gross minus their own
        // strokes on this hole, then MIN across the team, then summed.
        const teamScores = pids
          .map(pid => {
            const v = savedScores[pid]?.[holeNum]
            if (v == null) return null
            const gross = parseInt(v)
            if (!Number.isFinite(gross)) return null
            if (displayMode !== 'adjusted') return gross
            const hcp = handicapForPlayer(pid)
            const strokes = hcp ? strokesOnHole(hcp, h.handicap_rank, holes.length) : 0
            return gross - strokes
          })
          .filter(v => v != null)
        if (teamScores.length === 0) continue
        const best = Math.min(...teamScores)
        teamTot += best
        teamDiff += best - (h.par || 4)
        teamThru++
      }
      return { teamTot, teamDiff, teamThru }
    })
  }, [isStandaloneBestBall, numTeams, holes, savedScores, displayMode, groupPlayers, standaloneState])
  // Stable HoleMap props — previously built as inline IIFEs in JSX, handing
  // the lazy map component fresh object/array identities every render.
  const teeColors = useMemo(() => {
    const tees = (course?.tees || []).filter(t => t.total_yardage != null)
    if (!tees.length) return null
    const sorted = [...tees].sort((a, b) => b.total_yardage - a.total_yardage)
    return { back: sorted[0].tee_color || sorted[0].tee_name, front: sorted[sorted.length - 1].tee_color || sorted[sorted.length - 1].tee_name }
  }, [course?.tees])
  const teeOptions = useMemo(() => {
    const names = (course?.tees || [])
      .map(t => t.tee_color || t.tee_name)
      .filter(Boolean)
    return [...new Set(names)]
  }, [course?.tees])
  // Pre-selected chip value for the mark-tee chooser. Must be looked up through
  // the same `tee_color || tee_name` priority used in `teeOptions` above, so
  // the string compares equal to one of the chip entries. standaloneState.tee
  // is a composite like 'Blue|women' (bare name on legacy rounds); teeNameOf
  // alone would hand back the tee_name even when the course row has a distinct
  // tee_color populated — review finding was that on a GolfCourseAPI course
  // with tee_name='Championship'/tee_color='Black' the default never matched
  // any chip.
  const defaultTeeColor = useMemo(() => {
    const t = resolveSeatTee(course?.tees, null, standaloneState?.tee)
    return t ? (t.tee_color || t.tee_name) : null
  }, [course?.tees, standaloneState?.tee])

  // Load any cached GPS coordinates for this course (free DB read; no quota spend).
  useEffect(() => {
    const cid = course?.id
    if (!cid) { setCourseCoords([]); return }
    let cancelled = false
    db('get_course_coordinates', { course_id: cid })
      .then(({ data }) => { if (!cancelled) setCourseCoords(data || []) })
      .catch(() => { if (!cancelled) setCourseCoords([]) })
    return () => { cancelled = true }
  }, [course?.id])

  const openMap = () => {
    // FIX 2: if the map chunk previously FAILED this round, this open is a manual
    // RETRY (auto-reopen is gated off while latched, so only a user tap reaches
    // here) — clear the latch and bump mapRetryKey so the lazy subtree remounts
    // with a fresh import(). If the chunk is reachable again the map recovers and
    // normal per-hole auto-reopen resumes; if it fails again handleMapChunkFailed
    // simply re-latches. The happy path never enters this branch, so the map's
    // lazy instance stays stable (one chunk load per round).
    if (mapLoadFailedThisRoundRef.current) {
      mapLoadFailedThisRoundRef.current = false
      try { if (roundLatchKey) sessionStorage.removeItem(roundLatchKey) } catch { /* private mode */ }
      setMapRetryKey(k => k + 1)
    }
    setGpsEnabled(true); setShowMap(true); setGpsPref(true)
    // Mark GPS as used in THIS round so subsequent hole saves auto-jump
    // back into the map. Reset on every mount by the ref.
    gpsOpenedThisRoundRef.current = true
    try { localStorage.setItem('tf_gps_pref', '1') } catch { /* private mode */ }
  }

  // F-074: called once when the local map ErrorBoundary catches a failed GPS-map
  // chunk load (MapUnavailableOverlay mounts). Latch the per-round flag so EVERY
  // auto-reopen (per-hole-save AND resume-after-nav) is suppressed for the rest of
  // the round, and zero the "opened this round" ref as a belt-and-suspenders
  // second gate. FIX 1: also persist the latch to sessionStorage so it survives a
  // remount — otherwise the resume auto-open would re-throw the purged import on
  // the way back in. A later MANUAL open still works (openMap clears the latch and
  // re-imports) — only the automatic reopen stays suppressed while latched.
  const handleMapChunkFailed = () => {
    mapLoadFailedThisRoundRef.current = true
    gpsOpenedThisRoundRef.current = false
    try { if (roundLatchKey) sessionStorage.setItem(roundLatchKey, '1') } catch { /* private mode */ }
  }

  // FIX 1: drop the persisted map-failure latch when the round is filed/deleted/
  // submitted. Not strictly required for correctness (a new round mints a new
  // round-id key that reads unset), but it keeps sessionStorage from carrying a
  // stale per-round entry after the round it belonged to is gone.
  const clearMapFailedLatch = () => {
    try { if (roundLatchKey) sessionStorage.removeItem(roundLatchKey) } catch { /* private mode */ }
  }

  // G4 — remember whether GPS was the last-viewed screen so a resume can jump
  // straight back to it. Only the explicit open/close flips this (not coords).
  // Skip the initial mount write: showMap starts false on every mount, and
  // writing '0' here would clobber a '1' left over from the previous session
  // BEFORE the resume effect (below) has a chance to read it. With this guard
  // localStorage only changes when the user actually opens or closes the map
  // after the page is up.
  const showMapFirstMountRef = useRef(true)
  useEffect(() => {
    if (showMapFirstMountRef.current) {
      showMapFirstMountRef.current = false
      return
    }
    try { localStorage.setItem('tf_gps_open', showMap ? '1' : '0') } catch { /* private mode */ }
  }, [showMap])

  // On resume into a round where GPS was the last-viewed screen, re-open it as
  // soon as cached coordinates are available — no quota spend, no surprise
  // fetch. Fires once per mount; the always-present exit covers the way out.
  const gpsResumedRef = useRef(false)
  useEffect(() => {
    if (gpsResumedRef.current || showMap || courseCoords.length === 0) return
    let last = '0'
    try { last = localStorage.getItem('tf_gps_open') || '0' } catch { /* private mode */ }
    // FIX 1/2: gate the resume auto-open through resolveResumeAutoOpen — the SAME
    // shared decision (delegates to shouldAutoReopenMap) as the per-hole-save
    // reopen — consulting the persisted failure latch. `last === '1'` (map was
    // the last-viewed screen) is the resume's
    // "GPS opened this round" signal; if the chunk failed earlier this round the
    // latch (re-hydrated on mount, plus a direct read here in case the hydrate
    // effect hasn't run for an async event id yet) keeps the map from
    // auto-reopening and re-throwing the purged import. A manual GPS tap still
    // reopens (and retries the import).
    try {
      if (roundLatchKey && isRoundMapFailed(sessionStorage.getItem(roundLatchKey))) {
        mapLoadFailedThisRoundRef.current = true
      }
    } catch { /* private mode */ }
    if (resolveResumeAutoOpen({
      gpsWasOpen: last === '1',
      roundMapFailed: mapLoadFailedThisRoundRef.current,
    })) { gpsResumedRef.current = true; openMap() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCoords])

  // GPS button. Coords already loaded → open. Linked course (external id) →
  // fetch coordinates on demand. Unlinked course → search GolfAPI and let the
  // user confirm the match (persisted + flagged for review).
  const openGps = async () => {
    if (courseCoords.length > 0) { openMap(); return }
    setLoadingGps(true)
    const ext = course?.external_course_id
    try {
      // Manual-mode courses (source='manual') open ManualHoleMap — a
      // separate GPS surface that lets the user set a pin per hole rather
      // than reading POIs from a provider. Rendered alongside the regular
      // HoleMap in the JSX; same showMap flag opens it because only one
      // mode can be active at a time on a given round.
      if (course?.source === 'manual') {
        setLoadingGps(false)
        openMap()
        return
      }
      // Admin-loaded courses are populated by the AdminCourseMapPage. The
      // `courseCoords.length > 0` early return at the top of this fn handles
      // the normal case where coords are loaded. If for some reason coords
      // aren't loaded (admin started the course but didn't map yet), DO NOT
      // fall through to the GolfAPI link/search flow — that's what burned
      // 2.2 credits + created duplicate rows on 2026-06-30. Toast instead.
      if (course?.source === 'admin') {
        setLoadingGps(false)
        showToast('No GPS data for this course yet. Open the admin mapper to add POIs.', 'error')
        return
      }
      // GolfCourseAPI (gca_-prefixed) doesn't expose per-hole POI coordinates —
      // only a single property centroid. Short-circuit with a clear message
      // rather than calling golfapi('coordinates') against a GolfCourseAPI id.
      if (ext && typeof ext === 'string' && ext.startsWith('gca_')) {
        setLoadingGps(false)
        showToast('GPS map not available for this course (provided without per-hole coordinates).', 'error')
        return
      }
      if (ext) {
        const res = await golfapi('coordinates', { courseID: ext })
        if (res?.degraded) { showToast('Course details temporarily unavailable.', 'error'); setLoadingGps(false); return }
        const { data } = await db('get_course_coordinates', { course_id: course.id })
        setCourseCoords(data || [])
        setLoadingGps(false)
        if (!data || data.length === 0) { showToast('No GPS data for this course.', 'error'); return }
        openMap()
      } else {
        // Unlinked local course → find GolfAPI candidates to confirm.
        const res = await golfapi('search', { name: course?.name, city: course?.city || undefined, state: course?.state || undefined })
        setLoadingGps(false)
        if (res?.degraded) { showToast('Course search temporarily unavailable.', 'error'); return }
        const cands = res?.results || []
        if (cands.length === 0) { showToast('No GPS match found for this course.', 'error'); return }
        setLinkCandidates(cands)
      }
    } catch {
      showToast('Could not load GPS.', 'error'); setLoadingGps(false)
    }
  }

  // User confirmed which GolfAPI course this is → fetch detail + coordinates,
  // persist the link (flagged for staff review), and open the map.
  const linkCourse = async (cand) => {
    setLinking(true)
    try {
      const det = await golfapi('detail', { courseID: cand.external_course_id })
      const gId = det?.course_id
      if (det?.degraded || !gId) { showToast('Course details unavailable right now.', 'error'); setLinking(false); return }
      const co = await golfapi('coordinates', { courseID: cand.external_course_id })
      if (co?.degraded) { showToast('Course details temporarily unavailable.', 'error'); setLinking(false); return }
      await db('link_course_to_golfapi', { local_course_id: course.id, resolved_course_id: gId, player_id: player?.id || null })
      const { data } = await db('get_course_coordinates', { course_id: course.id })
      setCourseCoords(data || [])
      setLinking(false)
      setLinkCandidates(null)
      if (!data || data.length === 0) { showToast('No GPS data for this course.', 'error'); return }
      openMap()
    } catch {
      showToast('Could not link course.', 'error'); setLinking(false)
    }
  }

  if (!player && !isCommissioner) return (
    <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
      <p style={{ fontSize: '2rem', marginBottom: 16 }}>🔒</p>
      <p className="text-muted" style={{ marginBottom: 20 }}>Sign in with your PIN to enter scores</p>
      <button className="btn btn-primary" onClick={() => navigate('/player-login')}>Sign In</button>
    </div></div>
  )

  // Reload-resume fallback (D-300). The effect above is already navigating back
  // into the persisted round; this render covers the single frame before that
  // lands (and any edge case where the auto-nav can't run) with an explicit
  // "Resume round" primary — placed AHEAD of the "No active event" / "No active
  // round" fallbacks so a standalone reload never sees the bare "Start a Round"
  // the owner feared would overwrite the in-progress round. Demotes "Start a new
  // round" to secondary. NEVER auto-starts or clears the persisted round.
  if (!standaloneState && persistedResume) return (
    <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
      <p style={{ fontSize: '2rem', marginBottom: 16 }}>⛳</p>
      <p className="text-muted" style={{ marginBottom: 4 }}>Your round is still in progress.</p>
      {persistedResume.course?.name && (
        <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 20 }}>{persistedResume.course.name}</p>
      )}
      <button
        className="btn btn-primary"
        onClick={() => navigate('/scorecard', { replace: true, state: { ...persistedResume, standalone: true } })}
      >
        Resume round
      </button>
      <div style={{ marginTop: 12 }}>
        <button className="btn btn-ghost" onClick={() => navigate('/play')}>Start a new round</button>
      </div>
    </div></div>
  )

  if (!event && !standaloneState) return (
    <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
      <p style={{ fontSize: '2rem', marginBottom: 16 }}>⛳</p>
      <p className="text-muted">No active event.</p>
      <button className="btn btn-primary" onClick={() => navigate('/play')} style={{ marginTop: 16 }}>Start a Round</button>
    </div></div>
  )

  // Legacy event-based afternoon/scramble team entry RETIRED (D-341): the
  // scorecard is standaloneState-only now (no events.status active-round
  // pointer). Standalone scrambles render through the team-round path below.

  if (!isStrokePlay && !standaloneState) return (
    <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
      <p style={{ fontSize: '2rem', marginBottom: 16 }}>⛳</p>
      <p className="text-muted">No active round.</p>
      <button className="btn btn-primary" onClick={() => navigate('/play')} style={{ marginTop: 16 }}>Start a Round</button>
    </div></div>
  )

  const activeScoring = groupPlayers.filter(m => scoringFor[m.player_id])
  const scoresLocked = event?.scores_locked === true && !isCommissioner

  return (
    <div className="page">
      {/* Manual-mode GPS overlay — only mounted for source='manual' courses.
          Distinct surface from HoleMap (no POIs to load; user sets pins). */}
      {/* F-074 render-level crash-prevention: this local ErrorBoundary contains
          a failed GPS-map chunk import (a new build purged the old content-hashed
          chunk mid-round) to a small in-scorecard overlay instead of the root
          "Something went wrong" screen. `onFail` LATCHES the failure (in memory +
          sessionStorage) so no auto-reopen path re-shows the overlay for the rest
          of the round. "Back to scoring" only DISMISSES (setShowMap(false) + reset
          the boundary's error state); it does NOT re-import — React.lazy caches
          the rejected module promise, so genuine recovery needs a FRESH lazy. That
          fresh import happens on a MANUAL GPS tap: openMap bumps mapRetryKey, the
          key below remounts this boundary + Suspense, and the useMemo'd HoleMap/
          ManualHoleMap are recreated → import() is re-attempted. This render-level
          behavior is NOT covered by CI — the repo has no jsdom/testing-library, so
          a full render/throw test is out of scope; it is verified MANUALLY per the
          owner checklist. The extractable pure logic (shouldAutoReopenMap,
          resolveResumeAutoOpen, mapFailedStorageKey, isRoundMapFailed,
          handlePreloadError, shouldApplyPendingUpdate) IS unit-tested; that the
          resume/per-save wiring actually consults the failure latch is guarded
          structurally in tests/unit/scorecard-map-latch-wiring.test.js. */}
      {gpsEnabled && mapSurface === 'manual' && (
        <ErrorBoundary key={`map-retry-${mapRetryKey}`} fallback={({ reset }) => <MapUnavailableOverlay visible={showMap} onClose={() => { setShowMap(false); reset() }} onFail={handleMapChunkFailed} />}>
        <Suspense fallback={null}>
          <ManualHoleMap
            courseId={course?.id}
            playerId={player?.id}
            hole={currentHole}
            holePar={holePar}
            firstHole={firstHoleNum}
            holeCount={lastHoleNum}
            visible={showMap}
            onClose={() => setShowMap(false)}
            onHoleChange={(h) => setCurrentHole(Math.max(firstHoleNum, Math.min(lastHoleNum, h)))}
          />
        </Suspense>
        </ErrorBoundary>
      )}

      {/* GPS overlay — mounted once opened, kept alive so a round ≈ 1 map load */}
      {/* F-074: same local crash-prevention as the manual overlay above —
          render-level behavior verified manually (no jsdom in CI). onFail latches
          the failure (memory + sessionStorage) so no auto-reopen re-shows the
          overlay; the mapRetryKey key remounts the boundary on a manual retry so
          the recreated lazy re-imports the chunk. */}
      {gpsEnabled && mapSurface === 'poi' && (
        <ErrorBoundary key={`map-retry-${mapRetryKey}`} fallback={({ reset }) => <MapUnavailableOverlay visible={showMap} onClose={() => { setShowMap(false); reset() }} onFail={handleMapChunkFailed} />}>
        <Suspense fallback={null}>
          <HoleMap
            coords={courseCoords}
            hole={currentHole}
            holePar={holePar}
            holeYards={myYardage ?? currentHoleData?.yardage ?? (currentHoleData?.all_yardages && Object.values(currentHoleData.all_yardages).length ? Math.max(...Object.values(currentHoleData.all_yardages)) : null)}
            firstHole={firstHoleNum}
            holeCount={lastHoleNum}
            courseId={course?.id}
            playerId={player?.id}
            teeColors={teeColors}
            teeOptions={teeOptions}
            defaultTeeColor={defaultTeeColor}
            availableTeeColors={teeOptions}
            visible={showMap}
            onClose={() => setShowMap(false)}
            onLeftGreen={() => setShowMap(false)}
            onHoleChange={(h) => setCurrentHole(Math.max(firstHoleNum, Math.min(lastHoleNum, h)))}
          />
        </Suspense>
        </ErrorBoundary>
      )}
      {linkCandidates && (
        <CourseLinkPicker
          courseName={course?.name || 'this course'}
          candidates={linkCandidates}
          busy={linking}
          onPick={linkCourse}
          onClose={() => { if (!linking) setLinkCandidates(null) }}
        />
      )}
      <div className="container">
        {/* Header — two columns so the action icons (right) don't squeeze
            the title/course-name stack (left) onto a single row. */}
        <div style={{ paddingTop: 8, paddingBottom: 12, borderBottom: '1px solid var(--green-mid)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold)', marginBottom: 2 }}>{course?.name || ''}</p>
            <h1 style={{ fontSize: '1.6rem', margin: 0 }}>Scoring</h1>
            {course?.name && <p className="text-muted text-sm" style={{ marginTop: 2 }}>{course.name}{course.par ? ` · Par ${course.par}` : ''}</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {course?.id && (
              <button
                onClick={openGps}
                disabled={loadingGps}
                title="GPS / Map"
                style={{ width: 46, height: 46, borderRadius: '50%', border: '3px solid #1b3a1f', background: '#284b30', color: '#c8c0b4', fontFamily: 'var(--font-mono)', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.03em', cursor: loadingGps ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                {loadingGps ? '…' : 'GPS'}
              </button>
            )}
            <button
              onClick={() => standaloneState ? navigate('/round-leaderboard', { state: { standalone_round_id: standaloneState.id, course: standaloneState.course, holes: holes.length } }) : navigate('/leaderboard')}
              title="Leaderboard"
              style={{ width: 46, height: 46, borderRadius: 6, border: '2px solid #5a6b5d', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#5a6b5d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <line x1="10" y1="6" x2="21" y2="6" />
                <line x1="10" y1="12" x2="21" y2="12" />
                <line x1="10" y1="18" x2="21" y2="18" />
                <text x="3" y="8" fontFamily="sans-serif" fontSize="6" stroke="none" fill="#5a6b5d">1</text>
                <text x="3" y="14" fontFamily="sans-serif" fontSize="6" stroke="none" fill="#5a6b5d">2</text>
                <text x="3" y="20" fontFamily="sans-serif" fontSize="6" stroke="none" fill="#5a6b5d">3</text>
              </svg>
            </button>
          </div>
        </div>

        {/* Chip strip (Scorecard v2) — ABOVE the scorecard, NOT inside it
            (locked with Jacob 2026-07-14). One pill per active game plus a
            "+ Add Game" pill. Pills are the ONLY visible surface for game
            actions on the scorecard — View/Edit/End moves to each game's
            dedicated page. For now:
              Shuffle → /shuffle?game=<id>
              others → scrolls to the (deliberately-hidden) Side games
                       panel below whose Edit/End affordances still work.
            Wolf/Skins get their own /wolf and /skins pages in a follow-up
            chunk; the pill routing swaps then. */}
        {standaloneState && holes.length > 0 && groupPlayers.length > 0 && (
          <ScorecardChipStrip
            chips={[
              ...activeSideGames.map(g => ({
                key: g.id,
                glyph: chipGlyphFor(g.format),
                label: chipLabelFor(g.format),
                tone: chipToneFor(g.format),
                format: g.format,
              })),
              { key: '__add__', glyph: '+', label: 'Add Game', tone: 'default' },
            ]}
            onChipTap={(key) => {
              if (key === '__add__') {
                setAddGameOpen(true)
                return
              }
              // Every game format now has a dedicated /game/:gameId
              // page (locked with Jacob 2026-07-14). Wolf/Skins get
              // their existing card components; Nassau/Simple/
              // Stableford/Match render a participant list + End
              // affordance until per-format cards land.
              navigate(`/game/${key}`)
            }}
          />
        )}

        {/* Hole-by-hole entry */}
        {holes.length > 0 && groupPlayers.length > 0 && (
          <div className="card">
            {/* Hole selector */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button onClick={() => setCurrentHole(h => Math.max(firstHoleNum, h - 1))} disabled={currentHole === firstHoleNum}
                style={{ width: 44, height: 44, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: currentHole === 1 ? 'var(--gray-700)' : 'var(--cream)', fontSize: '1.4rem', cursor: currentHole === 1 ? 'default' : 'pointer' }}>‹</button>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1 }}>Hole {currentHole}</p>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  Par {holePar} · Hdcp #{currentHoleData?.handicap_rank || '–'}
                </p>
                {currentHoleData?.all_yardages && Object.keys(currentHoleData.all_yardages).length > 0 && (() => {
                  // Rework v2 · per-tee yardage list. Tees the current group is
                  // actually playing (teesInPlaySet) render BOLD; the rest normal.
                  // BALANCED WRAP: an invisible single-line probe measures whether
                  // the full list overflows the card at the current width; if it
                  // does, split into two centered rows via splitBalanced (larger
                  // half on top). No thrash — teeSplit only flips on a real overflow
                  // change, and the probe always renders the full line so the memo
                  // never feeds back on itself. If a measure is unavailable the
                  // list simply renders on one wrapping row (owner-approved).
                  const teeEntries = Object.entries(currentHoleData.all_yardages)
                    .sort((a, b) => b[1] - a[1])
                  const renderEntry = ([name, yds], i) => (
                    <Fragment key={name}>
                      {i > 0 ? ' · ' : ''}
                      <span style={{ fontWeight: teesInPlaySet.has(name) ? 700 : 400, color: teesInPlaySet.has(name) ? 'var(--cream)' : 'var(--gray-600)' }}>{name} {yds}</span>
                    </Fragment>
                  )
                  const rowStyle = { fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--gray-600)', marginTop: 1, whiteSpace: 'normal' }
                  if (!teeSplit) {
                    return (
                      <>
                        {/* Invisible single-line probe — natural (nowrap) width vs
                            the container decides whether to split. */}
                        <p ref={teeProbeRef} aria-hidden="true" style={{ ...rowStyle, position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none', left: 0, top: 0 }}>
                          {teeEntries.map(renderEntry)}
                        </p>
                        <p ref={teeWrapRef} style={rowStyle}>{teeEntries.map(renderEntry)}</p>
                      </>
                    )
                  }
                  const [topCount] = splitBalanced(teeEntries.length)
                  const topRow = teeEntries.slice(0, topCount)
                  const bottomRow = teeEntries.slice(topCount)
                  return (
                    <>
                      <p ref={teeProbeRef} aria-hidden="true" style={{ ...rowStyle, position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', pointerEvents: 'none', left: 0, top: 0 }}>
                        {teeEntries.map(renderEntry)}
                      </p>
                      <p ref={teeWrapRef} style={rowStyle}>{topRow.map(renderEntry)}</p>
                      <p style={rowStyle}>{bottomRow.map((e, i) => renderEntry(e, i))}</p>
                    </>
                  )
                })()}
              </div>
              <button onClick={() => setCurrentHole(h => Math.min(lastHoleNum, h + 1))} disabled={currentHole === lastHoleNum}
                style={{ width: 44, height: 44, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: currentHole === 18 ? 'var(--gray-700)' : 'var(--cream)', fontSize: '1.4rem', cursor: currentHole === 18 ? 'default' : 'pointer' }}>›</button>
            </div>

            {/* Progress dots — 2 rows */}
            <div style={{ marginBottom: 12 }}>
              {[holes.slice(0,9), holes.slice(9,18)].map((row, rowIdx) => (
                <div key={rowIdx} style={{ display: 'flex', gap: 3, justifyContent: 'center', marginBottom: rowIdx === 0 ? 3 : 0 }}>
                  {row.map(h => {
                    // Dot reflects COMMITTED state only (Bug #36) — an unsaved
                    // edit in the live `scores` buffer must not light the dot.
                    const anyScore = groupPlayers.some(m => scoringFor[m.player_id] && savedScores[m.player_id]?.[String(h.hole_number)])
                    return (
                      <button key={h.hole_number} onClick={() => setCurrentHole(h.hole_number)}
                        style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer', background: h.hole_number === currentHole ? 'var(--gold)' : anyScore ? 'var(--green-light)' : 'var(--green-mid)', fontSize: '0.62rem', fontFamily: 'var(--font-mono)', color: h.hole_number === currentHole ? 'var(--green-deep)' : 'var(--cream)', fontWeight: h.hole_number === currentHole ? 700 : 400 }}>
                        {h.hole_number}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: 'var(--green-mid)', marginBottom: 10 }} />

            {/* Per-team rows (Standalone Scramble) — one score input per team,
                fanned out to every member of that team. */}
            {isStandaloneScramble && Array.from({ length: numTeams }).map((teamIdx_, teamIdx) => {
              const pids = teamMemberIds(teamIdx)
              if (pids.length === 0) return null
              const memberNames = (standaloneState?.players || [])
                .filter(sp => (sp.team ?? 0) === teamIdx)
                .map(sp => sp.name)
              const teamScore = scores[pids[0]]?.[String(currentHole)] ?? holePar
              const diff = teamScore - holePar
              const diffTxt = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
              const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-500)'
              return (
                <div key={`team-${teamIdx}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 4px',
                  borderBottom: teamIdx < numTeams - 1 ? '1px solid var(--green-mid)' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.85rem' }}>
                      {teamHeadingFor(teamIdx)}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-300)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {memberNames.join(', ')}
                    </div>
                  </div>
                  <button onClick={() => adjustTeamScore(teamIdx, currentHole, -1)}
                    style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{teamScore}</span>
                  <button onClick={() => adjustTeamScore(teamIdx, currentHole, 1)}
                    style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 500, minWidth: 28, textAlign: 'right', color: diffColor, flexShrink: 0 }}>{diffTxt}</span>
                </div>
              )
            })}

            {/* Best Ball — per-player rows visually grouped by team with a
                per-team "Best this hole" indicator. Each player still records
                their own gross; the team's per-hole score is the min(). */}
            {isStandaloneBestBall && Array.from({ length: numTeams }).map((_, teamIdx) => {
              const teamPlayers = (standaloneState?.players || [])
                .filter(sp => (sp.team ?? 0) === teamIdx)
              if (teamPlayers.length === 0) return null
              // Use DISPLAYED scores (entered value OR the default par the
              // user sees in the stepper) for team-best. If we only counted
              // entered values, a player who hadn't touched their stepper
              // (still showing par) would be invisible — the only player
              // who DID enter a score would falsely get ★ BEST even with a
              // worse number. With displayed values, untouched-par defaults
              // remain in the running so the badge stays correct.
              const teamHoleScoresDisplayed = teamPlayers
                .map(sp => scores[membersRef.current[sp.slot]?.player_id]?.[String(currentHole)] ?? holePar)
              const teamBest = teamHoleScoresDisplayed.length ? Math.min(...teamHoleScoresDisplayed) : null
              // Separately track whether ANY score has actually been entered
              // for this team this hole — the "best this hole" caption only
              // shows once someone has committed a value (avoids the
              // confusing default-par caption pre-entry).
              const teamHasEntered = teamPlayers.some(sp =>
                scores[membersRef.current[sp.slot]?.player_id]?.[String(currentHole)] != null
              )
              return (
                <Fragment key={`bb-team-${teamIdx}`}>
                  <div style={{
                    padding: '6px 4px 4px',
                    fontWeight: 700, color: 'var(--gold)', fontSize: '0.78rem',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    borderBottom: '1px solid var(--green-mid)',
                  }}>
                    {teamHeadingFor(teamIdx)}
                  </div>
                  {teamPlayers.map((sp, pIdxInTeam) => {
                    const member = membersRef.current[sp.slot]
                    if (!member) return null
                    const isMe = player?.id === member.player_id
                    const isScoring = scoringFor[member.player_id]
                    const holeScore = scores[member.player_id]?.[String(currentHole)] ?? holePar
                    const diff = holeScore - holePar
                    const diffTxt = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
                    const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-500)'
                    // ★ BEST = displayed score ties the team's best on this hole.
                    // We compare against `holeScore` (entered ?? holePar) so that
                    // a teammate who hasn't touched their stepper (still at par)
                    // remains a valid contender — otherwise the only player who
                    // entered a value would falsely win BEST even with a worse
                    // number. Pre-entry, all teammates show at par → all show
                    // ★ BEST, which collapses to the real winner as scores come in.
                    const isBest = teamBest != null && holeScore === teamBest
                    return (
                      <div key={member.player_id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 4px',
                        borderBottom: pIdxInTeam < teamPlayers.length - 1 ? '1px solid var(--green-mid)' : 'none',
                        opacity: isScoring ? 1 : 0.4,
                      }}>
                        <button onClick={() => toggleScoring(member.player_id)}
                          disabled={!standaloneState && isMe}
                          style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0, background: isScoring ? 'var(--gold)' : 'transparent', border: `2px solid ${isScoring ? 'var(--gold)' : 'var(--green-mid)'}`, cursor: (!standaloneState && isMe) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--green-deep)' }}>
                          {isScoring ? '✓' : ''}
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: isMe ? 600 : 400, color: isMe ? 'var(--gold)' : 'var(--cream)', fontSize: '0.9rem' }}>{member.name}</span>
                          {isBest && <span style={{ marginLeft: 6, fontSize: '0.62rem', fontWeight: 700, color: 'var(--green-bright)' }}>★ BEST</span>}
                        </div>
                        {isScoring ? (
                          <>
                            <button onClick={() => adjustScore(member.player_id, currentHole, -1)} style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{holeScore}</span>
                            <button onClick={() => adjustScore(member.player_id, currentHole, 1)} style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 500, minWidth: 28, textAlign: 'right', color: diffColor, flexShrink: 0 }}>{diffTxt}</span>
                          </>
                        ) : (
                          (() => {
                            const s = scoredByStatusText(member.player_id)
                            if (!s) return null
                            return (
                              <span
                                className="text-xs text-mono"
                                style={{ color: s.warn ? 'var(--red)' : 'var(--gray-300)', fontStyle: 'italic' }}
                              >
                                {s.text}
                              </span>
                            )
                          })()
                        )}
                      </div>
                    )
                  })}
                  {teamHasEntered && teamBest != null && (
                    <div style={{ padding: '5px 4px 10px', fontSize: '0.72rem', color: 'var(--gray-300)' }}>
                      {teamHeadingFor(teamIdx)} best this hole: <span style={{ color: 'var(--green-bright)', fontWeight: 700 }}>{teamBest}</span>
                    </div>
                  )}
                </Fragment>
              )
            })}

            {/* Match Play — per-player score entry grouped by team (visually
                identical to best ball), with a running hole-by-hole match
                status computed below. Always exactly 2 teams. */}
            {isStandaloneMatchPlay && Array.from({ length: 2 }).map((_, teamIdx) => {
              const teamPlayers = (standaloneState?.players || [])
                .filter(sp => (sp.team ?? 0) === teamIdx)
              if (teamPlayers.length === 0) return null
              // Team-best on this hole, computed against DISPLAYED scores so
              // that an un-touched-par teammate stays in the running (same
              // rationale as best ball — see comment in that block above).
              const teamHoleScoresDisplayed = teamPlayers
                .map(sp => scores[membersRef.current[sp.slot]?.player_id]?.[String(currentHole)] ?? holePar)
              const teamBest = teamHoleScoresDisplayed.length ? Math.min(...teamHoleScoresDisplayed) : null
              const teamHasEntered = teamPlayers.some(sp =>
                scores[membersRef.current[sp.slot]?.player_id]?.[String(currentHole)] != null
              )
              return (
                <Fragment key={`mp-team-${teamIdx}`}>
                  <div style={{
                    padding: '6px 4px 4px',
                    fontWeight: 700, color: 'var(--gold)', fontSize: '0.78rem',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    borderBottom: '1px solid var(--green-mid)',
                  }}>
                    {teamHeadingFor(teamIdx)}
                  </div>
                  {teamPlayers.map((sp, pIdxInTeam) => {
                    const member = membersRef.current[sp.slot]
                    if (!member) return null
                    const isMe = player?.id === member.player_id
                    const isScoring = scoringFor[member.player_id]
                    const holeScore = scores[member.player_id]?.[String(currentHole)] ?? holePar
                    const diff = holeScore - holePar
                    const diffTxt = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
                    const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-500)'
                    const isBest = teamBest != null && holeScore === teamBest
                    return (
                      <div key={member.player_id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 4px',
                        borderBottom: pIdxInTeam < teamPlayers.length - 1 ? '1px solid var(--green-mid)' : 'none',
                        opacity: isScoring ? 1 : 0.4,
                      }}>
                        <button onClick={() => toggleScoring(member.player_id)}
                          disabled={!standaloneState && isMe}
                          style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0, background: isScoring ? 'var(--gold)' : 'transparent', border: `2px solid ${isScoring ? 'var(--gold)' : 'var(--green-mid)'}`, cursor: (!standaloneState && isMe) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--green-deep)' }}>
                          {isScoring ? '✓' : ''}
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: isMe ? 600 : 400, color: isMe ? 'var(--gold)' : 'var(--cream)', fontSize: '0.9rem' }}>{member.name}</span>
                          {isBest && teamPlayers.length > 1 && <span style={{ marginLeft: 6, fontSize: '0.62rem', fontWeight: 700, color: 'var(--green-bright)' }}>★ BEST</span>}
                        </div>
                        {isScoring ? (
                          <>
                            <button onClick={() => adjustScore(member.player_id, currentHole, -1)} style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{holeScore}</span>
                            <button onClick={() => adjustScore(member.player_id, currentHole, 1)} style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 500, minWidth: 28, textAlign: 'right', color: diffColor, flexShrink: 0 }}>{diffTxt}</span>
                          </>
                        ) : (
                          (() => {
                            const s = scoredByStatusText(member.player_id)
                            if (!s) return null
                            return (
                              <span
                                className="text-xs text-mono"
                                style={{ color: s.warn ? 'var(--red)' : 'var(--gray-300)', fontStyle: 'italic' }}
                              >
                                {s.text}
                              </span>
                            )
                          })()
                        )}
                      </div>
                    )
                  })}
                  {teamHasEntered && teamBest != null && teamPlayers.length > 1 && (
                    <div style={{ padding: '5px 4px 10px', fontSize: '0.72rem', color: 'var(--gray-300)' }}>
                      {teamHeadingFor(teamIdx)} best this hole: <span style={{ color: 'var(--green-bright)', fontWeight: 700 }}>{teamBest}</span>
                    </div>
                  )}
                </Fragment>
              )
            })}

            {/* Match Play — running match status. Computed by walking holes
                in order, comparing each team's best per hole, and tracking
                wins/losses. Surfaces "All Square", "Team N X UP", "Dormie",
                or "Closed out X & Y" when the trailing team can no longer
                catch up. */}
            {isStandaloneMatchPlay && matchLive && (() => {
              // Walk logic lives in walkMatchHoles (shared with the group
              // summary) and is memoized in matchLive — this block only
              // formats the status strings.
              const { totalHoles, t1Wins, t2Wins, holesPlayed,
                      closeoutLeader, closeoutLead, closeoutRemaining,
                      thisHoleResult } = matchLive
              const diff = t1Wins - t2Wins
              const absDiff = Math.abs(diff)
              const holesRemaining = totalHoles - holesPlayed
              let status, statusColor
              const closed = closeoutLeader != null
              if (closed) {
                status = `Team ${closeoutLeader} wins ${closeoutLead} & ${closeoutRemaining}`
                statusColor = 'var(--green-bright)'
              } else if (holesPlayed === 0) {
                status = 'Match has not started'
                statusColor = 'var(--gray-300)'
              } else if (absDiff > 0 && absDiff === holesRemaining) {
                const leader = diff > 0 ? 1 : 2
                status = `Team ${leader} Dormie (${absDiff} UP, ${holesRemaining} to play)`
                statusColor = 'var(--gold)'
              } else if (diff === 0) {
                status = `All Square (thru ${holesPlayed})`
                statusColor = 'var(--cream)'
              } else {
                const leader = diff > 0 ? 1 : 2
                status = `Team ${leader} ${absDiff} UP (thru ${holesPlayed})`
                statusColor = 'var(--gold)'
              }
              const hr = thisHoleResult
              const hrText = hr === 1 ? 'Team 1 wins hole' : hr === 2 ? 'Team 2 wins hole' : hr === 0 ? 'Halved' : null
              const hrColor = hr === 0 ? 'var(--gray-300)' : 'var(--green-bright)'
              return (
                <div style={{
                  marginTop: 10, padding: '10px 12px',
                  background: 'var(--green-deep)',
                  border: '1px solid var(--gold)',
                  borderRadius: 'var(--radius)',
                  display: 'flex', flexDirection: 'column', gap: 4,
                }}>
                  {hrText && (
                    <div style={{ fontSize: '0.72rem', color: hrColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Hole {currentHole}: {hrText}
                    </div>
                  )}
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: statusColor }}>
                    {status}
                  </div>
                  {closed && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-300)' }}>
                      Match closed out — remaining holes can be played as exhibition.
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Per-player rows — used for stroke and event-based scoring. Hidden
                for Standalone Scramble + Best Ball + Match Play (team-aware
                blocks above replace them). */}
            {!isTeamRound && groupPlayers.map((member, idx) => {
              const isMe = player?.id === member.player_id
              const isScoring = scoringFor[member.player_id]
              const holeScore = scores[member.player_id]?.[String(currentHole)] ?? holePar
              const diff = holeScore - holePar
              const diffTxt = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
              const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-500)'
              // Advanced stats row (Scorecard v2). Standalone rounds only —
              // event scoring has its own commissioner-driven flow. Shown
              // beneath the score stepper for the scorekeeper's own row so
              // they can capture FIR / GIR / Pen / Putts inline. GIR is
              // auto-derived from putts + score in adjustPutts/adjustScore;
              // the FIR / Pen buttons are user-toggled.
              // Advanced-stats capture (revised 2026-07-14 per Jacob):
              //   - For the logged-in user's own row → always shown when
              //     scoring + player.advanced_stats_enabled is on.
              //   - For other players being scored → hidden by default;
              //     a "Track Advanced Stats" tap affordance expands the
              //     row. Reduces clutter for scorekeepers who only want
              //     their own stats logged.
              const advOn = player?.advanced_stats_enabled !== false
              const isEligible = !!standaloneState && isScoring && advOn
              const showAdvStats = isEligible && (isMe || advExpanded.has(member.player_id))
              const showAdvOptIn = isEligible && !isMe && !advExpanded.has(member.player_id)
              const statsForHole = holeStats[member.player_id]?.[String(currentHole)] || {}
              const fir = statsForHole.fir
              const gir = statsForHole.gir
              const pen = statsForHole.pen
              const putts = Number.isFinite(statsForHole.putts) ? statsForHole.putts : null
              // GIR tile lit-state (#37). Derive live from the EFFECTIVE score
              // (explicit score, else the default par that Save & Next commits)
              // + putts, so the tile lights the moment putts are set on an
              // untouched par hole — matching what adjustPutts persists. A
              // manual override (girManual) always wins (#35); when putts is
              // unset GIR isn't derivable → fall back to the stored value.
              const girDerived = deriveGir(
                effectiveScore(scores[member.player_id]?.[String(currentHole)], holePar),
                putts,
                holePar,
              )
              const girOn = statsForHole.girManual === true
                ? gir === true
                : (girDerived !== null ? girDerived : gir === true)

              // Per-player tee (T3 / rework v2): this seat's tee NAME from ITS
              // OWN tee, resolved by the seat's PLAYER id (same key Change-tee
              // writes) so a Replace/Swap-shuffled slot never shows the wrong
              // seat's tee. Falls back to the round-level tee for legacy/
              // un-overridden seats. Yardage is no longer shown per-row (moved to
              // the hole-level list); par is shared.
              const seatTee = seatTeeByPid[member.player_id]
              const seatTeeName = teeNameOf(seatTee || standaloneState?.tee)

              return (
                <div key={member.player_id} style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  padding: '9px 4px',
                  borderBottom: idx < groupPlayers.length - 1 ? '1px solid var(--green-mid)' : 'none',
                  opacity: isScoring ? 1 : 0.4,
                  transition: 'opacity 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Checkbox — event mode can't uncheck yourself; standalone
                        can (to release scored_by so another device claims). */}
                    <button onClick={() => toggleScoring(member.player_id)}
                      disabled={!standaloneState && isMe}
                      style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0, background: isScoring ? 'var(--gold)' : 'transparent', border: `2px solid ${isScoring ? 'var(--gold)' : 'var(--green-mid)'}`, cursor: (!standaloneState && isMe) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--green-deep)' }}>
                      {isScoring ? '✓' : ''}
                    </button>

                    {/* Name + this player's tee (rework v2: tee NAME/COLOR only —
                        the per-tee yardage moved to the hole-level list above). */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: isMe ? 600 : 400, color: isMe ? 'var(--gold)' : 'var(--cream)', fontSize: '0.9rem' }}>{member.name}</span>
                      {standaloneState && seatTeeName && (
                        <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--gray-500)', marginTop: 1 }}>
                          {seatTeeName}
                        </span>
                      )}
                    </div>

                    {/* Steppers — hidden when not scoring */}
                    {isScoring ? (
                      <>
                        <button onClick={() => adjustScore(member.player_id, currentHole, -1)}
                          style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{holeScore}</span>
                        <button onClick={() => adjustScore(member.player_id, currentHole, 1)}
                          style={{ width: 36, height: 36, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 500, minWidth: 28, textAlign: 'right', color: diffColor, flexShrink: 0 }}>{diffTxt}</span>
                      </>
                    ) : (
                      <span className="text-xs text-muted" style={{ fontFamily: 'var(--font-mono)' }}>not scoring</span>
                    )}

                    {/* Rework v2 · the per-player ⋮ was REMOVED from the scorecard
                        row and relocated into the Edit Round modal's Players
                        section (Finish/Resume, Change tee, Replace/Swap), with a
                        standalone Add-player button beside it. */}
                  </div>

                  {showAdvStats && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 6, paddingLeft: 30, // align under name (past the checkbox)
                    }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[
                          { key: 'fir', label: 'FIR', on: fir === true, warn: false },
                          { key: 'gir', label: 'GIR', on: girOn, warn: false },
                          { key: 'pen', label: 'Pen', on: pen === true, warn: pen === true },
                        ].map(t => (
                          <button
                            key={t.key}
                            // GIR auto-fills from putts + score but is tappable:
                            // a tap overrides the auto value (or back-fills putts
                            // when putts is unset). FIR / Pen are plain toggles.
                            onClick={() => t.key === 'gir'
                              ? toggleGir(member.player_id, currentHole)
                              : toggleHoleStat(member.player_id, currentHole, t.key)}
                            title={t.key === 'gir' ? 'GIR — auto from putts + score; tap to override' : `Toggle ${t.label}`}
                            style={{
                              padding: '3px 8px', borderRadius: 4,
                              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.03em',
                              cursor: 'pointer',
                              background: t.on
                                ? (t.warn ? 'var(--red)' : 'var(--gold)')
                                : 'transparent',
                              color: t.on
                                ? (t.warn ? 'var(--cream)' : 'var(--green-deep)')
                                : 'var(--gray-300)',
                              border: `1px solid ${t.on ? (t.warn ? 'var(--red)' : 'var(--gold)') : 'var(--green-mid)'}`,
                            }}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={() => adjustPutts(member.player_id, currentHole, -1)}
                          disabled={putts == null}
                          aria-label="Fewer putts"
                          // Bug #34: '−' is a no-op while putts is unset ('–') —
                          // 0 putts (a hole-out) is only reachable via '+' then
                          // '−'. Greyed/disabled so a stray tap can't persist a
                          // phantom 0-putt hole-out.
                          style={{ width: 24, height: 24, border: '1px solid var(--green-mid)', borderRadius: 4, background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '0.9rem', cursor: putts == null ? 'default' : 'pointer', opacity: putts == null ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >−</button>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{putts != null ? putts : '–'}</span>
                        <button
                          onClick={() => adjustPutts(member.player_id, currentHole, 1)}
                          aria-label="More putts"
                          style={{ width: 24, height: 24, border: '1px solid var(--green-mid)', borderRadius: 4, background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >+</button>
                        <span style={{ fontSize: '0.65rem', color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Putts</span>
                      </div>
                    </div>
                  )}
                  {showAdvOptIn && (
                    <button
                      onClick={() => {
                        setAdvExpanded(prev => {
                          const next = new Set(prev)
                          next.add(member.player_id)
                          return next
                        })
                      }}
                      style={{
                        marginLeft: 30, alignSelf: 'flex-start',
                        padding: '2px 0',
                        background: 'none', border: 'none',
                        color: 'var(--gold)', fontSize: '0.7rem',
                        cursor: 'pointer', textDecoration: 'underline',
                        letterSpacing: '0.02em',
                      }}
                    >
                      Track Advanced Stats
                    </button>
                  )}
                </div>
              )
            })}

            <div style={{ height: 1, background: 'var(--green-mid)', margin: '8px 0 12px' }} />

            {/* Locked banner */}
            {scoresLocked && (
              <div style={{ textAlign: 'center', padding: '10px 12px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--gold)', fontWeight: 600 }}>🔒 Scores Locked</p>
                <p style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 2 }}>Viewing only — the commissioner has locked score entry.</p>
              </div>
            )}

            {/* Action row */}
            <div style={{ display: 'flex', gap: 8 }}>
              {/* F-104 bug 3: compare against lastHoleNum (max hole NUMBER in
                  the round — 18 for front/full, 18 for back-9, 9 for front-9)
                  not holes.length (a COUNT). On a back-9 round the old code
                  rendered the final-save button on hole 10 because 10 >= 9. */}
              {currentHole < lastHoleNum ? (
                <button className="btn btn-primary btn-full" onClick={handleNextHole} disabled={saving || scoresLocked}>
                  {saving ? 'Saving...' : 'Save & Next →'}
                </button>
              ) : (() => {
                // Save-Hole-N (final hole). Build committed snapshot first
                // (par defaults for current hole), then guardedSave checks
                // the REST of the round against that snapshot — so the
                // current hole isn't flagged as missing, but earlier holes
                // the user skipped still are.
                const committed = commitCurrentHolePar()
                return (
                  <button className="btn btn-primary btn-full"
                    onClick={() => guardedSave(async () => {
                      setSaving(true)
                      setScores(committed)
                      await saveProgress(true, committed)
                      setSaving(false)
                      setShowSubmitted(true)
                    }, committed)}
                    disabled={saving || scoresLocked}>
                    {saving ? 'Saving...' : `Save Hole ${lastHoleNum} ✓`}
                  </button>
                )
              })()}
            </div>
          </div>
        )}

        {/* Shuffle Card — between scoring and group summary (locked with
            Jacob 2026-07-14). Renders one card + effect banner + active
            effects per active shuffle_card game. Card picker fires as a
            centered modal from within the component when picks_remaining
            > 0 — no additional wrapper needed here. */}
        {standaloneState && activeSideGames.filter(g => g.format === 'shuffle_card').map(g => (
          <div key={`sh-inline-${g.id}`} style={{ marginTop: 8 }}>
            <ShuffleCard
              game_id={g.id}
              viewerPlayerId={player?.id}
              viewerCurrentHole={currentHole}
              currentHolePar={holePar}
              variant="inline"
            />
          </div>
        ))}

        {/* S2 · current-score snapshot — SOLO only (2+ players use Group Summary below) */}
        {standaloneState && groupPlayers.length <= 1 && holes.length > 0 && (() => {
          // Summary reflects COMMITTED state only (Bug #36) — reads the saved
          // snapshot, not the live edit buffer, so an unsaved hole isn't counted.
          const mine = savedScores[player?.id] || {}
          const scored = Object.entries(mine).filter(([, v]) => v)
          const tot = calculateTotal(Object.fromEntries(scored))
          const vsPar = scored.reduce((s, [hn, v]) => s + (parseInt(v) - (holesByNumber.get(parseInt(hn))?.par || 4)), 0)
          const thru = scored.length
          const vsTxt = thru === 0 ? '–' : vsPar === 0 ? 'E' : vsPar > 0 ? `+${vsPar}` : `${vsPar}`
          return (
            <div className="card card-sm" style={{ marginTop: 8, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              <div><div className="text-xs text-muted text-mono" style={{ textTransform: 'uppercase' }}>Total</div><div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.05rem' }}>{tot || '–'}</div></div>
              <div><div className="text-xs text-muted text-mono" style={{ textTransform: 'uppercase' }}>+/- Par</div><div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.05rem' }}>{vsTxt}</div></div>
              <div><div className="text-xs text-muted text-mono" style={{ textTransform: 'uppercase' }}>Thru</div><div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.05rem' }}>{thru}</div></div>
            </div>
          )
        })()}

        {/* Group summary — total score and +/– only. Scramble collapses
            per-player rows into one row per team since all members of a
            team share identical hole_scores by design. */}
        {groupPlayers.length > 1 && (() => {
          // Adjusted mode is only shown when EVERY group player has a
          // round-start handicap — otherwise the toggle disappears and
          // the mode locks to Gross (see DisplayModeToggle).
          const groupHandicapsComplete = groupPlayers.every(m => handicapForPlayer(m.player_id) != null)
          return (
          <div className="card card-sm" style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <p className="text-xs text-muted text-mono" style={{ margin: 0, textTransform: 'uppercase' }}>Group Summary</p>
              <DisplayModeToggle available={groupHandicapsComplete} />
            </div>
            {isStandaloneMatchPlay ? (
              // Match Play: not stroke totals — show hole-by-hole wins per
              // team plus the running match status. Walks holes in order,
              // takes each team's best per hole, tallies. Same calc as the
              // per-hole status block above (kept local to each block so
              // there's no shared-state coupling between them).
              (() => {
                // Match-play summary: same memoized walk as the live status
                // block (walkMatchHoles via matchSummary), but net-aware —
                // adjusted display mode compares team NET bests. This block
                // only formats. Empty-team guard: matchSummary is null until
                // both teams have members.
                if (!matchSummary) return null
                const { totalHoles, t1Wins, t2Wins, halved, holesPlayed,
                        closeoutLeader, closeoutLead, closeoutRemaining } = matchSummary
                const diff = t1Wins - t2Wins
                const absDiff = Math.abs(diff)
                const holesRemaining = totalHoles - holesPlayed
                let status
                if (closeoutLeader != null) {
                  status = `T${closeoutLeader} ${closeoutLead} & ${closeoutRemaining}`
                } else if (holesPlayed === 0) status = '–'
                else if (absDiff > 0 && absDiff === holesRemaining) {
                  const leader = diff > 0 ? 1 : 2
                  status = `T${leader} Dormie`
                } else if (diff === 0) status = 'AS'
                else {
                  const leader = diff > 0 ? 1 : 2
                  status = `T${leader} ${absDiff}↑`
                }
                return (
                  <>
                    {[0, 1].map(teamIdx => {
                      const memberNames = (standaloneState?.players || [])
                        .filter(sp => (sp.team ?? 0) === teamIdx)
                        .map(sp => sp.name)
                      const wins = teamIdx === 0 ? t1Wins : t2Wins
                      return (
                        <div key={`mp-sum-${teamIdx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: teamIdx === 0 ? '1px solid var(--green-mid)' : 'none' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.875rem' }}>{teamHeadingFor(teamIdx)}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-300)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memberNames.join(', ')}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.95rem' }}>{wins}</span>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--gray-300)' }}>wins</span>
                          </div>
                        </div>
                      )
                    })}
                    <div style={{ marginTop: 8, padding: '6px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--gray-300)', textTransform: 'uppercase' }}>Status</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--gold)' }}>{status}</span>
                    </div>
                    {halved > 0 && (
                      <div style={{ fontSize: '0.65rem', color: 'var(--gray-300)', textAlign: 'right' }}>
                        {halved} hole{halved === 1 ? '' : 's'} halved · thru {holesPlayed}
                      </div>
                    )}
                  </>
                )
              })()
            ) : isStandaloneBestBall ? (
              // Best Ball: each team's total = sum of per-hole bests across
              // its members. Below each team line, show the contributing
              // players' totals so you can see who contributed what.
              Array.from({ length: numTeams }).map((_, teamIdx) => {
                // Totals computed once in the bestBallTotals memo (net-aware);
                // this block only formats.
                const totals = bestBallTotals?.[teamIdx]
                if (!totals) return null
                const { teamTot, teamDiff, teamThru } = totals
                const teamDiffTxt = teamThru === 0 ? '–' : teamDiff === 0 ? 'E' : teamDiff > 0 ? `+${teamDiff}` : `${teamDiff}`
                const teamDiffColor = teamDiff < 0 ? 'var(--blue-birdie)' : teamDiff > 0 ? 'var(--red)' : 'var(--gray-300)'
                const memberNames = (standaloneState?.players || [])
                  .filter(sp => (sp.team ?? 0) === teamIdx)
                  .map(sp => sp.name)
                return (
                  <div key={`bb-sum-${teamIdx}`} style={{ paddingBottom: teamIdx < numTeams - 1 ? 8 : 0, marginBottom: teamIdx < numTeams - 1 ? 8 : 0, borderBottom: teamIdx < numTeams - 1 ? '1px solid var(--green-mid)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.875rem' }}>{teamHeadingFor(teamIdx)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray-300)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memberNames.join(', ')}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.95rem' }}>{teamTot || '–'}</span>
                        {teamTot > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: teamDiffColor }}>{teamDiffTxt}</span>}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : isStandaloneScramble ? (
              Array.from({ length: numTeams }).map((_, teamIdx) => {
                const pids = teamMemberIds(teamIdx)
                if (pids.length === 0) return null
                // Scramble team total reflects COMMITTED state only (Bug #36) —
                // the saved snapshot, not the live edit buffer — so it stays
                // consistent with the completion dots. All team members share
                // identical hole_scores by design, so pids[0] is representative.
                const pts = savedScores[pids[0]] || {}
                const tot = calculateTotal(Object.fromEntries(Object.entries(pts).filter(([,v]) => v)))
                let diff = null
                if (tot > 0 && holes.length > 0) {
                  diff = Object.entries(pts)
                    .filter(([, v]) => v)
                    .reduce((sum, [holeNum, score]) => {
                      const holeData = holesByNumber.get(parseInt(holeNum))
                      return sum + (parseInt(score) - (holeData?.par || 4))
                    }, 0)
                }
                const diffTxt = diff === null ? '–' : diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
                const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-300)'
                const memberNames = (standaloneState?.players || [])
                  .filter(sp => (sp.team ?? 0) === teamIdx)
                  .map(sp => sp.name)
                return (
                  <div key={`teamsum-${teamIdx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: teamIdx < numTeams - 1 ? '1px solid var(--green-mid)' : 'none' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.875rem' }}>{teamHeadingFor(teamIdx)}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray-300)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memberNames.join(', ')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.95rem' }}>{tot || '–'}</span>
                      {tot > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: diffColor }}>{diffTxt}</span>}
                    </div>
                  </div>
                )
              })
            ) : (
              groupPlayers.map(m => {
                // Stroke-play group summary reflects COMMITTED state only
                // (Bug #36) — the saved snapshot, not the live edit buffer.
                const pts = savedScores[m.player_id] || {}
                const tot = calculateTotal(Object.fromEntries(Object.entries(pts).filter(([,v]) => v)))
                const isMe = player?.id === m.player_id
                // Calculate +/- as sum of (score - par) for each scored hole
                // so it's accurate mid-round, not vs total course par
                let diff = null
                if (tot > 0 && holes.length > 0) {
                  diff = Object.entries(pts)
                    .filter(([, v]) => v)
                    .reduce((sum, [holeNum, score]) => {
                      const holeData = holesByNumber.get(parseInt(holeNum))
                      return sum + (parseInt(score) - (holeData?.par || 4))
                    }, 0)
                }
                const diffTxt = diff === null ? '–' : diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
                const diffColor = diff < 0 ? 'var(--blue-birdie)' : diff > 0 ? 'var(--red)' : 'var(--gray-300)'
                return (
                  <div key={m.player_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--green-mid)' }}>
                    <span style={{ fontWeight: isMe ? 600 : 400, color: isMe ? 'var(--gold)' : 'var(--cream)', fontSize: '0.875rem' }}>{m.name}</span>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.95rem' }}>{tot || '–'}</span>
                      {tot > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: diffColor }}>{diffTxt}</span>}
                    </div>
                  </div>
                )
              })
            )}
          </div>
          )
        })()}
        {/* Side games — casual rounds only. Anyone on the round can
            manage games (see netlify/functions/standalone-game.js auth). */}
        {standaloneState?.id && groupPlayers.length >= 2 && (
          <StandaloneGamesPanel
            standaloneRoundId={standaloneState.id}
            roundPlayers={groupPlayers}
            viewerPlayerId={player?.id}
            viewerCurrentHole={currentHole}
            holeCount={holes.length || parseInt(standaloneState.holes, 10) || 18}
            onActiveGamesChange={setActiveSideGames}
            controlledOpenForm={addGameOpen}
            onOpenFormChange={setAddGameOpen}
            hideChrome
          />
        )}

        {/* Keep screen awake toggle — device UI preference, defaults OFF. */}
        <WakeLockToggle on={wakeLockOn} onToggle={toggleWakeLock} />

        {/* S1 · Edit round + Exit Round */}
        {standaloneState && (
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={openEditRound}
              style={{
                flex: 1, padding: '14px',
                background: 'none', border: '1px solid var(--green-mid)',
                borderRadius: 'var(--radius)', color: 'var(--cream)',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Edit round
            </button>
            <button
              onClick={() => setExitStep('root')}
              style={{
                flex: 1, padding: '14px',
                background: 'none', border: '1px solid var(--red, #c0392b)',
                borderRadius: 'var(--radius)', color: 'var(--red, #c0392b)',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Exit Round
            </button>
          </div>
        )}
      </div>
      {toast && <div className={`toast ${toast.type}`} style={{ whiteSpace: 'pre-wrap', fontSize: toast.type === 'error' ? '0.75rem' : undefined, maxWidth: '90vw' }}>{toast.msg}</div>}

      {/* Round submitted modal */}
      {showSubmitted && (
        <SubmittedModal onDone={async () => {
          try { localStorage.removeItem('active_round') } catch {}
          if (standaloneKey) { try { localStorage.removeItem(standaloneKey) } catch {} }
          clearMapFailedLatch()
          // Round complete — clear the resume cache. round_scores rows stay.
          if (standaloneState?.id) {
            try { await db('delete_active_round', { standalone_round_id: standaloneState.id }) } catch {}
          }
          setShowSubmitted(false)
          if (standaloneState) {
            // replace:true — back from the detail must not re-enter the cleared scoring page.
            navigate('/round-leaderboard', { replace: true, state: { standalone_round_id: standaloneState.id, course: standaloneState.course, holes: holes.length } })
          } else {
            navigate('/leaderboard')
          }
        }} />
      )}

      {/* S1 · Edit round (holes / tee) */}
      {showEditRound && standaloneState && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '24px', maxWidth: 320, width: '90%', margin: '0 16px' }}>
            <h3 style={{ marginBottom: 16, textAlign: 'center' }}>Edit round</h3>

            <p className="text-xs text-muted text-mono" style={{ textTransform: 'uppercase', marginBottom: 6 }}>Holes</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {/* Gate options by the course's REAL hole count (Bug #33): a
                  9-hole course offers only '9'; an 18-hole course offers both. */}
              {editHoleOptions(courseHoleCount(standaloneState.course, holes.length)).map(n => (
                <button key={n} onClick={() => setEditHoles(n)} style={{
                  flex: 1, padding: '10px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600,
                  background: editHoles === n ? 'var(--gold)' : 'var(--green-deep)',
                  color: editHoles === n ? 'var(--green-deep)' : 'var(--cream)',
                  border: `1px solid ${editHoles === n ? 'var(--gold)' : 'var(--green-mid)'}`,
                }}>{n} holes</button>
              ))}
            </div>

            {/* F-104.1-T2 · Front/Back radio — only relevant when the user
                has selected 9 holes on an 18-hole course. Mirrors the same
                condition + option shape used by PlayPage.jsx round-create
                (`courseHoleCount === 18 && holes === '9'`) so a user who
                started a round there sees the same control here. The chip
                style matches the holes row above for visual consistency
                within the modal (btn-primary/btn-ghost classes would
                visually fight the existing gold/green chips). */}
            {courseHoleCount(standaloneState.course, holes.length) === 18 && editHoles === '9' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[
                  { id: 'front', label: 'Front 9' },
                  { id: 'back',  label: 'Back 9' },
                ].map(opt => (
                  <button key={opt.id} onClick={() => setEditNineSet(opt.id)} style={{
                    flex: 1, padding: '10px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600,
                    background: editNineSet === opt.id ? 'var(--gold)' : 'var(--green-deep)',
                    color: editNineSet === opt.id ? 'var(--green-deep)' : 'var(--cream)',
                    border: `1px solid ${editNineSet === opt.id ? 'var(--gold)' : 'var(--green-mid)'}`,
                  }}>{opt.label}</button>
                ))}
              </div>
            )}

            {/* Rework v2 (fixes) · the round-level TEE grid was REMOVED from Edit
                Round. Tee is now a per-player choice via each seat's ⋮ → Change
                tee (openChangeTee/changeSeatTee), which applies to that seat's
                whole round. Round-setup tee selection still lives on PlayPage. */}

            {/* Rework v2 · Players — the relocated in-round player controls. The
                scorecard-row ⋮ was removed; this section is the single home for
                per-player actions and renders for EVERY standalone round so
                Add-player / Replace-Swap / Change-tee stay reachable in solo and
                team rounds too. PLAYING/FINISHED is now a READ-ONLY status LABEL;
                the Finish/Resume toggle moved into the per-player ⋮ (F-077 freeze,
                still a non-team multi-scorer action). Change tee applies to the
                whole round; Replace/Swap stays guest-only. */}
            <p className="text-xs text-muted text-mono" style={{ textTransform: 'uppercase', marginBottom: 6 }}>Players</p>
            <p className="text-xs text-muted" style={{ marginBottom: 8 }}>
              Use a player&rsquo;s ⋮ to finish their round (freezes their card at the last entered hole), change their tee (applies to the whole round), or replace/swap a guest.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {groupPlayers.map(m => {
                const pid = m.player_id
                const done = !!playersDone[pid]
                const isMe = pid === player?.id
                const isScoring = !!scoringFor[pid]
                const isGuestSeat = seatGuestMap[pid] === true
                const canReplace = isGuestSeat && !isMe
                // Finish/Resume: the F-077 freeze — offered for ANY non-team seat
                // this device is scoring in an active round, INCLUDING the normal
                // solo/host case. (It was gated to multi-scorer only, so it never
                // appeared for the owner testing a solo/host round — defect 4.)
                // togglePlayerDone freezes the seat at its last entered hole and
                // toggles to Resume; par-autosave already skips a finished seat.
                const canFinish = !isTeamRound && isScoring
                const actions = []
                if (canFinish) {
                  actions.push({ label: done ? 'Resume (un-finish)' : 'Finish Round', onSelect: () => togglePlayerDone(pid), disabled: seatBusy })
                }
                actions.push({ label: 'Change tee', onSelect: () => openChangeTee(pid, m.name), disabled: seatBusy })
                actions.push(canReplace
                  ? { label: 'Replace / Swap player', onSelect: () => openReplaceFlow(pid, m.name), disabled: seatBusy }
                  : { label: 'Replace / Swap player', onSelect: () => {}, disabled: true, hint: 'Only guest players can be replaced' })
                return (
                  <div key={pid} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 'var(--radius)',
                    background: 'var(--green-deep)', border: '1px solid var(--green-mid)',
                  }}>
                    <span style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: '0.9rem', color: isMe ? 'var(--gold)' : 'var(--cream)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || 'Player'}</span>
                    {!isTeamRound && isScoring && (
                      <span style={{
                        flex: '0 0 auto', padding: '3px 10px',
                        borderRadius: 'var(--radius)', fontSize: '0.72rem', fontWeight: 700,
                        background: done ? 'var(--gold)' : 'var(--green-dark)',
                        color: done ? 'var(--green-deep)' : 'var(--gray-500)',
                        border: `1px solid ${done ? 'var(--gold)' : 'var(--green-mid)'}`,
                      }}>{done ? 'FINISHED' : 'PLAYING'}</span>
                    )}
                    <PlayerRowMenu label={m.name} actions={actions} />
                  </div>
                )
              })}
            </div>
            <button className="btn btn-secondary" onClick={handleAddPlayer} disabled={seatBusy} style={{ width: '100%', marginBottom: 16 }}>
              + Add player
            </button>

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowEditRound(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                // F-104.1-T2: thread the user's front/back choice into the T1
                // applyEditRound opts. When 9 holes is selected, map the radio
                // to the server-authoritative starting_hole (front→1, back→10).
                // When 18 is selected the span starts at 1 by convention; the
                // T1 overrun-clamp (requested+count-1 > realCount → 1) already
                // handles 9→18 even without an explicit override, but we pass
                // 1 anyway so the intent is unambiguous in review.
                const nextStartingHole = editHoles === '9'
                  ? (editNineSet === 'back' ? 10 : 1)
                  : 1
                applyEditRound({ nextStartingHole })
              }}>Apply</button>
            </div>
            <button onClick={() => { setShowEditRound(false); navigate('/play') }} style={{ display: 'block', width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: '0.8rem', cursor: 'pointer' }}>
              Change course (restart on Play)
            </button>
          </div>
        </div>
      )}

      {/* X2 · missing-hole warning before a save */}
      {missingPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '28px 24px', textAlign: 'center', maxWidth: 320, margin: '0 16px' }}>
            <h3 style={{ marginBottom: 8 }}>Missing scores</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 6 }}>
              {missingPrompt.missing.length === 1 ? 'Hole ' : 'Holes '}
              <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{missingPrompt.missing.join(', ')}</span>
              {missingPrompt.missing.length === 1 ? ' has' : ' have'} no score.
            </p>
            <p className="text-muted text-xs" style={{ marginBottom: 20 }}>Save anyway, or go back and fill them in?</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setMissingPrompt(null)} style={{ flex: 1, lineHeight: 1.2 }}>
                Go Back /<br />Edit
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { const p = missingPrompt.proceed; setMissingPrompt(null); p() }}
                style={{ flex: 1 }}
              >
                Save anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* F-077 · partial-players warning before "Save All Players" on an
          individual stroke round. Lists every still-selected player who hasn't
          finished all holes; "Go back" saves nothing. Peer of missingPrompt. */}
      {partialPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '28px 24px', textAlign: 'center', maxWidth: 340, margin: '0 16px' }}>
            <h3 style={{ marginBottom: 8 }}>Some players aren&rsquo;t finished</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
              These players haven&rsquo;t completed all {holes.length || 18} holes. Their rounds save as incomplete (kept for stats, not index-eligible):
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', textAlign: 'left' }}>
              {partialPrompt.players.map(p => (
                <li key={p.player_id} style={{ padding: '4px 0', borderBottom: '1px solid var(--green-mid)' }}>
                  <span style={{ color: 'var(--cream)', fontWeight: 600 }}>{p.name}</span>
                  <span className="text-muted text-sm"> — {p.holeCount} of {holes.length || 18} holes</span>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setPartialPrompt(null)} style={{ flex: 1 }}>
                Go back
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { const pr = partialPrompt.proceed; setPartialPrompt(null); pr() }}
                style={{ flex: 1 }}
              >
                Save anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* D-346 · in-round Replace/Swap player flow (guest seats only). Three
          steps: chooser → starting-point → manual back-entry. useHistoryModal
          ('replace') wires Android back to dismiss. */}
      {replaceFlow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '26px 22px', textAlign: 'center', maxWidth: 360, width: '100%', margin: '0 16px', maxHeight: '85vh', overflowY: 'auto' }}>

            {replaceFlow.step === 'choose' && (
              <>
                <h3 style={{ marginBottom: 6 }}>Replace / Swap {replaceFlow.seatName}</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 14 }}>
                  Enter the incoming player&rsquo;s name, then choose how to handle {replaceFlow.seatName}&rsquo;s scores.
                </p>
                <input
                  type="text"
                  value={replaceFlow.incomingName}
                  onChange={(e) => setReplaceFlow(f => ({ ...f, incomingName: e.target.value }))}
                  placeholder="New player name"
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px', marginBottom: 16, borderRadius: 'var(--radius)', border: '1px solid var(--green-mid)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '0.95rem' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                  <button className="btn btn-primary" onClick={doTransferRename} disabled={seatBusy} style={{ width: '100%' }}>
                    Transfer scores to new player
                  </button>
                  <span className="text-muted" style={{ fontSize: '0.68rem', marginTop: -6 }}>Keeps this card&rsquo;s scores; just changes the name.</span>
                  <button className="btn btn-secondary" onClick={doKeepAndFinish} disabled={seatBusy} style={{ width: '100%' }}>
                    Keep scores with {replaceFlow.seatName} & finish their round
                  </button>
                  <span className="text-muted" style={{ fontSize: '0.68rem', marginTop: -6 }}>{replaceFlow.seatName} keeps their card; the new player joins fresh.</span>
                  {/* Option 3 (Delete round) calls the OWNER-GATED remove_guest_seat
                      RPC (server rejects a non-owner). Show it only on the round
                      owner's device so a co-scorer never gets a dead-end option;
                      options 1 & 2 above are not owner-gated and stay available.
                      Mirrors the "Delete All Players" gate (standaloneState.player_id
                      is the round-start owner stamp). */}
                  {isRoundOwner(standaloneState?.player_id ?? null, player?.id) && (
                    <>
                      <button className="btn btn-secondary" onClick={doDeleteAndReplace} disabled={seatBusy} style={{ width: '100%' }}>
                        Delete {replaceFlow.seatName}&rsquo;s round
                      </button>
                      <span className="text-muted" style={{ fontSize: '0.68rem', marginTop: -6 }}>Clears this seat&rsquo;s scores; the new player takes it fresh.</span>
                    </>
                  )}
                </div>
                <button className="btn btn-ghost" onClick={() => setReplaceFlow(null)} disabled={seatBusy} style={{ width: '100%', marginTop: 16 }}>
                  Cancel
                </button>
              </>
            )}

            {replaceFlow.step === 'startpoint' && (
              <>
                <h3 style={{ marginBottom: 6 }}>Where does {replaceFlow.seatName} start?</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 18 }}>
                  The round is on hole {newcomerStartHole(currentHole)}. Start them here (earlier holes stay blank and don&rsquo;t count), or type in the scores they already have.
                </p>
                <button className="btn btn-primary btn-full" onClick={doStartOnCurrentHole} disabled={seatBusy} style={{ marginBottom: 10 }}>
                  Start on hole {newcomerStartHole(currentHole)}
                </button>
                {backEntryHoleNumbers(currentHole, firstHoleNum).length > 0 && (
                  <button className="btn btn-secondary btn-full" onClick={doEnterPrevious} disabled={seatBusy}>
                    Enter previous hole scores
                  </button>
                )}
              </>
            )}

            {replaceFlow.step === 'backentry' && (
              <>
                <h3 style={{ marginBottom: 6 }}>{replaceFlow.seatName}&rsquo;s earlier holes</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 14 }}>
                  Enter strokes for the holes {replaceFlow.seatName} already played. Leave a hole blank if they didn&rsquo;t play it — blanks aren&rsquo;t counted.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                  {(replaceFlow.holeNums || []).map(hn => {
                    const val = replaceFlow.entries[String(hn)]
                    const par = holesByNumber.get(Number(hn))?.par || 4
                    return (
                      <div key={hn} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ flex: 1, textAlign: 'left', fontSize: '0.85rem' }}>Hole {hn} <span className="text-muted" style={{ fontSize: '0.7rem' }}>(par {par})</span></span>
                        <button onClick={() => bumpBackEntryHole(hn, -1)} disabled={val === '' || val == null}
                          style={{ width: 34, height: 34, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.1rem', cursor: (val === '' || val == null) ? 'default' : 'pointer', opacity: (val === '' || val == null) ? 0.4 : 1 }}>−</button>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.25rem', fontWeight: 700, minWidth: 26, textAlign: 'center' }}>{val === '' || val == null ? '–' : val}</span>
                        <button onClick={() => bumpBackEntryHole(hn, 1)}
                          style={{ width: 34, height: 34, border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)', background: 'var(--green-deep)', color: 'var(--cream)', fontSize: '1.1rem', cursor: 'pointer' }}>+</button>
                      </div>
                    )
                  })}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-ghost" onClick={() => setReplaceFlow(f => ({ ...f, step: 'startpoint' }))} disabled={seatBusy} style={{ flex: 1 }}>
                    Back
                  </button>
                  <button className="btn btn-primary" onClick={doSaveBackEntry} disabled={seatBusy} style={{ flex: 1 }}>
                    {seatBusy ? 'Saving...' : 'Save & continue'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Rework v2 · Change tee (this player). A tee picker mirroring the Edit
          Round TEE selector; picking a tee applies it to the seat for the WHOLE
          round (recomputes yardages + rating/handicap context via a resave) with
          entered strokes untouched. useHistoryModal('changetee') wires Android
          back. */}
      {changeTeeFlow && standaloneState && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '26px 22px', textAlign: 'center', maxWidth: 340, width: '100%', margin: '0 16px', maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 6 }}>Change tee — {changeTeeFlow.seatName}</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
              Applies to {changeTeeFlow.seatName}&rsquo;s whole round. Yardages and handicap context update; entered scores stay the same.
            </p>
            {(standaloneState.course?.tees || []).length > 0 ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 18 }}>
                {standaloneState.course.tees.map(t => {
                  const k = teeKey(t)
                  const g = teeGenderSuffix(t)
                  const selected = changeTeeFlow.current === k
                  return (
                    <button key={k} onClick={() => changeSeatTee(changeTeeFlow.seatPid, k)} disabled={seatBusy} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: 18, cursor: seatBusy ? 'default' : 'pointer', fontSize: '0.82rem',
                      background: selected ? 'var(--gold)' : 'var(--green-deep)',
                      color: selected ? 'var(--green-deep)' : 'var(--cream)',
                      border: `1px solid ${selected ? 'var(--gold)' : 'var(--green-mid)'}`,
                      opacity: seatBusy ? 0.6 : 1,
                    }}>
                      {t.tee_color && <span style={{ width: 11, height: 11, borderRadius: '50%', background: t.tee_color, border: '1px solid rgba(0,0,0,0.3)' }} />}
                      {g ? `${t.tee_name} (${g})` : t.tee_name}
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="text-muted text-sm" style={{ marginBottom: 18 }}>This course has no tee data to choose from.</p>
            )}
            <button className="btn btn-ghost" onClick={() => setChangeTeeFlow(null)} disabled={seatBusy} style={{ width: '100%' }}>
              {seatBusy ? 'Saving...' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Creator-delete handoff prompt (D-279). Shown when the by-id liveness
          poll detects the round CREATOR deleted this round while we were still
          scoring. "Keep playing" grabs ownership (first-write-wins server-side);
          "Leave" exits to Home. useHistoryModal('grab') wires Android back. */}
      {grabPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '28px 24px', textAlign: 'center', maxWidth: 320, margin: '0 16px' }}>
            <h3 style={{ marginBottom: 8 }}>Round ended by host</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
              The round creator has deleted this round. Would you like to keep playing? You&rsquo;ll take over as the scorekeeper and every score already entered stays.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={leaveDeletedRound} style={{ flex: 1 }}>
                Leave
              </button>
              <button className="btn btn-primary" onClick={keepPlayingGrab} style={{ flex: 1 }}>
                Keep playing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit round — three-step: root (Save/Delete), then Me/All choice.
          Solo rounds skip the Me/All step (no "others" to distinguish).
          "Save My Score" walks off with a partial record kept for stats
          (never index-eligible). "Delete My Score" walks off with no
          record. Both leave the round active for other players. */}
      {exitStep && (() => {
        const myHolesScored = Object.values(scores[player?.id] || {}).filter(Boolean).length
        const canSave = myHolesScored > 1
        const multiPlayer = groupPlayers.length > 1
        // Creator/organizer signal for the "Delete All Players" gate (F-014).
        // standaloneState.player_id is stamped to the round starter at
        // round-start (PlayPage) and rides in the resume payload, so on the
        // owner's device it equals player.id; on a joined (cross-device)
        // player's device it's the OTHER person's id. When it's absent (a
        // legacy payload with no owner stamp) we fall back to showing the
        // action and let the server's owner check decide — client gating is
        // UX only; the server is authoritative.
        const roundOwnerId = standaloneState?.player_id ?? null
        const isRoundCreator = !roundOwnerId || roundOwnerId === player?.id
        const redBtnStyle = {
          flex: 1, padding: '12px', border: 'none', borderRadius: 'var(--radius)',
          background: 'var(--red, #c0392b)', color: '#fff',
          fontWeight: 600, cursor: 'pointer',
        }
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--green-dark)', border: '1px solid var(--green-mid)', borderRadius: 'var(--radius-lg)', padding: '32px 24px', textAlign: 'center', maxWidth: 320, margin: '0 16px' }}>
              {exitStep === 'root' && (
                <>
                  <h3 style={{ marginBottom: 8 }}>Exit Round?</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                    {canSave
                      ? 'Save your progress, or delete the round and lose your scores. A saved partial round is kept for stats only — it is not index-eligible.'
                      : 'Do you want to delete this round? Your scores will be lost.'}
                  </p>
                  {canSave && (
                    <button
                      className="btn btn-primary btn-full"
                      onClick={() => setExitStep(multiPlayer ? 'save' : 'save-solo')}
                      disabled={saving}
                      style={{ marginBottom: 10 }}
                    >
                      Save Round
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-ghost" onClick={() => setExitStep(null)} style={{ flex: 1 }}>Cancel</button>
                    <button
                      onClick={() => setExitStep(multiPlayer ? 'delete' : 'delete-solo')}
                      style={redBtnStyle}
                    >
                      Delete Round
                    </button>
                  </div>
                </>
              )}

              {/* Solo save — no Me/All distinction, just save + exit. */}
              {exitStep === 'save-solo' && (
                <>
                  <h3 style={{ marginBottom: 8 }}>Save Round?</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                    Your partial round will be kept for stats only — not index-eligible.
                  </p>
                  <button
                    className="btn btn-primary btn-full"
                    onClick={() => guardedSave(saveAndExit, commitCurrentHolePar())}
                    disabled={saving}
                    style={{ marginBottom: 10 }}
                  >
                    {saving ? 'Saving…' : 'Save Round'}
                  </button>
                  <button className="btn btn-ghost btn-full" onClick={() => setExitStep('root')} disabled={saving}>Back</button>
                </>
              )}

              {/* Multi-player save — Me vs Everyone. */}
              {exitStep === 'save' && (
                <>
                  <h3 style={{ marginBottom: 8 }}>Save Round</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                    Save just your scores (the round stays live for the rest of the group), or save the whole group's scores and end the round.
                  </p>
                  <button
                    className="btn btn-primary btn-full"
                    onClick={() => guardedSave(saveMyScoreAndExit, commitCurrentHolePar())}
                    disabled={saving}
                    style={{ marginBottom: 10 }}
                  >
                    {saving ? 'Saving…' : 'Save My Score'}
                  </button>
                  <button
                    className="btn btn-primary btn-full"
                    onClick={saveAllPlayers}
                    disabled={saving}
                    style={{ marginBottom: 10 }}
                  >
                    {saving ? 'Saving…' : 'Save All Players'}
                  </button>
                  <button className="btn btn-ghost btn-full" onClick={() => setExitStep('root')} disabled={saving}>Back</button>
                </>
              )}

              {/* Solo delete — no Me/All distinction. */}
              {exitStep === 'delete-solo' && (
                <>
                  <h3 style={{ marginBottom: 8 }}>Delete Round?</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                    Your scores will be lost.
                  </p>
                  <button onClick={deleteAllAndExit} style={{ ...redBtnStyle, width: '100%', padding: '14px', marginBottom: 10 }}>
                    Delete Round
                  </button>
                  <button className="btn btn-ghost btn-full" onClick={() => setExitStep('root')}>Back</button>
                </>
              )}

              {/* Multi-player delete — Me vs Everyone. */}
              {exitStep === 'delete' && (
                <>
                  <h3 style={{ marginBottom: 8 }}>Delete Round</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                    {isRoundCreator
                      ? 'Delete just your scores (the round stays live for the rest of the group), or delete the whole round for everyone.'
                      : 'Delete just your scores. The round stays live for the rest of the group.'}
                  </p>
                  <button onClick={deleteMyScoreAndExit} style={{ ...redBtnStyle, width: '100%', padding: '14px', marginBottom: 10 }}>
                    Delete My Score
                  </button>
                  {/* "Delete All Players" is creator/organizer-only. The server
                      re-checks this against standalone_rounds.owner_player_id
                      (F-014); this render gate is UX only. */}
                  {isRoundCreator && (
                    <button onClick={deleteAllAndExit} style={{ ...redBtnStyle, width: '100%', padding: '14px', marginBottom: 10 }}>
                      Delete All Players
                    </button>
                  )}
                  <button className="btn btn-ghost btn-full" onClick={() => setExitStep('root')}>Back</button>
                </>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

function SubmittedModal({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000)
    return () => clearTimeout(t)
  }, [])
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--green-dark)', border: '1px solid var(--gold)', borderRadius: 'var(--radius-lg)', padding: '40px 32px', textAlign: 'center', maxWidth: 280, margin: '0 16px' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🏌️</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: 8, color: 'var(--gold)' }}>Round Submitted!</h2>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>Your scorecard has been saved.</p>
        <div style={{ width: '100%', height: 4, background: 'var(--green-mid)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 2, animation: 'drain 4s linear forwards' }} />
        </div>
        <p className="text-xs text-muted" style={{ marginTop: 8 }}>Taking you to your scorecard...</p>
        <style>{`@keyframes drain { from { width: 100% } to { width: 0% } }`}</style>
      </div>
    </div>
  )
}
